/**
 * Vietnamese translations — vi
 *
 * Flat key structure: feature.element or feature.element.detail
 * Keys use camelCase segments separated by dots.
 */

import type { TranslationDict } from './index';

export const vi: TranslationDict = {
  // ============================================
  // Common / Shared
  // ============================================
  'common.save': 'Lưu',
  'common.saving': 'Đang lưu...',
  'common.cancel': 'Hủy',
  'common.delete': 'Xóa',
  'common.deleting': 'Đang xóa...',
  'common.confirm': 'Xác nhận',
  'common.create': 'Tạo mới',
  'common.update': 'Cập nhật',
  'common.edit': 'Chỉnh sửa',
  'common.close': 'Đóng',
  'common.back': 'Quay lại',
  'common.next': 'Tiếp theo',
  'common.retry': 'Thử lại',
  'common.refresh': 'Làm mới',
  'common.loading': 'Đang tải...',
  'common.loadMore': 'Tải thêm',
  'common.processing': 'Đang xử lý...',
  'common.search': 'Tìm kiếm',
  'common.reset': 'Đặt lại',
  'common.resetFilters': 'Đặt lại bộ lọc',
  'common.clearFilters': 'Xóa bộ lọc',
  'common.clearSelection': 'Bỏ chọn',
  'common.viewAll': 'Xem tất cả →',
  'common.showing': 'Hiển thị',
  'common.of': 'trong',
  'common.page': 'Trang',
  'common.all': 'Tất cả',
  'common.active': 'Hoạt động',
  'common.pending': 'Chờ xử lý',
  'common.completed': 'Hoàn thành',
  'common.cancelled': 'Đã hủy',
  'common.unknown': 'Không rõ',
  'common.na': 'N/A',
  'common.justNow': 'Vừa xong',
  'common.minutesAgo': '{count} phút trước',
  'common.hoursAgo': '{count} giờ trước',
  'common.daysAgo': '{count} ngày trước',
  'common.onThisPage': '{loaded} trên trang này, {total} tổng cộng',
  'common.error': 'Lỗi',
  'common.success': 'Thành công',
  'common.unlimited': 'Không giới hạn',
  'common.free': 'Miễn phí',
  'common.items': 'sản phẩm',
  'common.item': 'sản phẩm',
  'common.date': 'Ngày',
  'common.status': 'Trạng thái',
  'common.actions': 'Thao tác',
  'common.total': 'Tổng',
  'common.name': 'Tên',
  'common.phone': 'Điện thoại',
  'common.email': 'Email',
  'common.address': 'Địa chỉ',
  'common.quantity': 'Số lượng',
  'common.price': 'Giá',
  'common.days': 'ngày',
  'common.unsavedChanges': 'Có thay đổi chưa lưu',
  'common.noResults': 'Không tìm thấy kết quả',
  'common.somethingWentWrong': 'Đã xảy ra lỗi',
  'common.tryAgain': 'Thử lại',
  'common.goBack': 'Quay lại',
  'common.yes': 'Có',
  'common.no': 'Không',
  'common.or': 'hoặc',
  'common.selected': 'đã chọn',
  'common.advancedFilters': 'Bộ lọc nâng cao',
  'common.dateFrom': 'Từ ngày',
  'common.dateTo': 'Đến ngày',
  'common.minPrice': 'Giá tối thiểu',
  'common.maxPrice': 'Giá tối đa',
  'common.sortBy': 'Sắp xếp theo',
  'common.order': 'Đơn hàng',

  // ============================================
  // Export Menu
  // ============================================
  'export.title': 'Xuất',
  'export.exporting': 'Đang xuất...',
  'export.csv': 'Xuất CSV',
  'export.csvDescription': 'Tải dữ liệu dạng bảng tính',
  'export.printReceipts': 'In biên lai',
  'export.printReceiptsDescription': 'In mục đã chọn dạng biên lai',
  'export.printReport': 'In báo cáo',
  'export.printReportDescription': 'In mục đã chọn dạng A4',
  'export.printLabels': 'In nhãn',
  'export.printLabelsDescription': 'In nhãn mã vạch / QR',
  'export.downloadPdf': 'Tải PDF',
  'export.downloadPdfDescription': 'Lưu dạng tài liệu PDF',
  'export.actions': 'Hành động',
  'export.download': 'Tải xuống',
  'export.print': 'In',
  'export.printGroup': 'In ({count} đã chọn)',
  'export.noSelection': 'Chọn mục trước',

  // ============================================
  // Auth — Login
  // ============================================
  'auth.signIn': 'Đăng nhập',
  'auth.signInDescription': 'Nhập thông tin đăng nhập để truy cập tài khoản',
  'auth.sessionReplaced':
    'Bạn đã bị đăng xuất vì tài khoản được đăng nhập từ nơi khác. Vui lòng đăng nhập lại.',
  'auth.emailLabel': 'Email',
  'auth.emailPlaceholder': 'ban@example.com',
  'auth.passwordLabel': 'Mật khẩu',
  'auth.passwordPlaceholder': '••••••••',
  'auth.signingIn': 'Đang đăng nhập...',
  'auth.forgotPassword': 'Quên mật khẩu?',
  'auth.noAccount': 'Chưa có tài khoản?',
  'auth.signUp': 'Đăng ký',
  'auth.loginSuccess': 'Chào mừng trở lại! Bạn đã đăng nhập thành công.',
  'auth.loginSuccessTitle': 'Đăng nhập thành công',
  'auth.loginFailed': 'Đăng nhập thất bại. Vui lòng thử lại.',
  'auth.loginFailedTitle': 'Đăng nhập thất bại',

  // Auth — Signup
  'auth.createAccount': 'Tạo tài khoản',
  'auth.signupDescription': 'Nhập email để bắt đầu sử dụng Store Manager',
  'auth.checkEmail': 'Kiểm tra email của bạn',
  'auth.checkEmailSent': 'Chúng tôi đã gửi liên kết xác minh đến {email}.',
  'auth.checkEmailClick': 'Nhấp vào liên kết để hoàn tất đăng ký.',
  'auth.checkEmailExpiry': 'Liên kết có hiệu lực trong 72 giờ.',
  'auth.sending': 'Đang gửi...',
  'auth.continueWithEmail': 'Tiếp tục với Email',
  'auth.resendCooldown': 'Gửi lại sau {seconds} giây',
  'auth.resendEmail': 'Không nhận được email? Gửi lại',
  'auth.haveAccount': 'Đã có tài khoản?',
  'auth.signInLink': 'Đăng nhập',
  'auth.validationEnterEmail': 'Vui lòng nhập địa chỉ email',
  'auth.validationErrorTitle': 'Lỗi xác thực',
  'auth.emailSent': 'Kiểm tra hộp thư để tìm liên kết xác minh!',
  'auth.emailSentTitle': 'Đã gửi email',
  'auth.signupFailed': 'Đăng ký thất bại. Vui lòng thử lại.',
  'auth.signupFailedTitle': 'Đăng ký thất bại',
  'auth.unexpectedError': 'Đã xảy ra lỗi không mong muốn',
  'auth.emailResent': 'Đã gửi lại email xác minh!',
  'auth.emailResentTitle': 'Đã gửi lại email',
  'auth.resendFailed': 'Gửi lại email thất bại. Vui lòng thử lại.',
  'auth.resendFailedTitle': 'Gửi lại thất bại',
  'auth.termsAcknowledgment': 'Bằng việc đăng ký, bạn xác nhận đồng ý với',
  'auth.termsOfService': 'Điều khoản dịch vụ',
  'auth.privacyPolicy': 'Chính sách bảo mật',
  'auth.and': 'và',
  'auth.visual.tagline': 'Quản lý thông minh hơn, phát triển nhanh hơn.',
  'auth.visual.description':
    'Theo dõi kho hàng, phân tích và cộng tác nhóm theo thời gian thực — tất cả trong một nơi.',

  // Auth — Onboarding
  'auth.onboarding.stepPersonal': 'Thông tin cá nhân',
  'auth.onboarding.stepBusiness': 'Thông tin doanh nghiệp',
  'auth.onboarding.stepStorehouse': 'Kho hàng',
  'auth.onboarding.joinTeam': 'Tham gia {businessName}',
  'auth.onboarding.joinTeamGeneric': 'Tham gia nhóm của bạn',
  'auth.onboarding.completeRegistration': 'Hoàn tất đăng ký',
  'auth.onboarding.invalidLink': 'Liên kết xác minh không hợp lệ',
  'auth.onboarding.completeProfile': 'Hoàn thành hồ sơ cho {email}',
  'auth.onboarding.settingUpAccount': 'Thiết lập tài khoản cho {email}',
  'auth.onboarding.stepIndicator': 'Bước {step}: {label}',
  'auth.onboarding.verifying': 'Đang xác minh liên kết...',
  'auth.onboarding.fullName': 'Họ và tên',
  'auth.onboarding.birthDate': 'Ngày sinh',
  'auth.onboarding.phoneOptional': 'Số điện thoại (Tùy chọn)',
  'auth.onboarding.password': 'Mật khẩu',
  'auth.onboarding.confirmPassword': 'Xác nhận mật khẩu',
  'auth.onboarding.businessName': 'Tên doanh nghiệp',
  'auth.onboarding.businessAddress': 'Địa chỉ doanh nghiệp',
  'auth.onboarding.businessPhone': 'Số điện thoại doanh nghiệp',
  'auth.onboarding.businessEmailOptional': 'Email doanh nghiệp (Tùy chọn)',
  'auth.onboarding.storehouseNameOptional': 'Tên kho hàng (Tùy chọn)',
  'auth.onboarding.storehouseAddress': 'Địa chỉ kho hàng',
  'auth.onboarding.storehousePhoneOptional':
    'Số điện thoại kho hàng (Tùy chọn)',
  'auth.onboarding.storehouseEmailOptional': 'Email kho hàng (Tùy chọn)',
  'auth.onboarding.ageRequirement': 'Bạn phải từ 18 tuổi trở lên',
  'auth.onboarding.passwordRequirement':
    'Tối thiểu 8 ký tự gồm chữ hoa, chữ thường và số',
  'auth.onboarding.namePlaceholder': 'Nguyễn Văn A',
  'auth.onboarding.phonePlaceholder': '+84 912 345 678',
  'auth.onboarding.businessNamePlaceholder': 'Cửa hàng của tôi',
  'auth.onboarding.businessAddressPlaceholder': '123 Nguyễn Huệ, TP.HCM',
  'auth.onboarding.businessPhonePlaceholder': '+84 912 345 678',
  'auth.onboarding.businessEmailPlaceholder': 'lienhe@doanhnghiep.com',
  'auth.onboarding.storehouseNamePlaceholder': 'Kho chính',
  'auth.onboarding.storehouseAddressPlaceholder': '456 Lê Lợi, TP.HCM',
  'auth.onboarding.storehousePhonePlaceholder': '+84 912 345 679',
  'auth.onboarding.storehouseEmailPlaceholder': 'kho@doanhnghiep.com',
  'auth.onboarding.joining': 'Đang tham gia...',
  'auth.onboarding.joinTeamBtn': 'Tham gia nhóm',
  'auth.onboarding.creating': 'Đang tạo...',
  'auth.onboarding.completeSetup': 'Hoàn tất thiết lập',
  'auth.onboarding.signUpAgain': 'Đăng ký lại',
  'auth.onboarding.validationName': 'Vui lòng nhập tên của bạn',
  'auth.onboarding.validationBirthDate': 'Vui lòng nhập ngày sinh',
  'auth.onboarding.validationAge': 'Bạn phải từ 18 tuổi trở lên',
  'auth.onboarding.validationPasswordLength':
    'Mật khẩu phải có ít nhất 8 ký tự',
  'auth.onboarding.validationPasswordStrength':
    'Mật khẩu phải chứa ít nhất một chữ hoa, một chữ thường và một số',
  'auth.onboarding.validationPasswordMatch': 'Mật khẩu không khớp',
  'auth.onboarding.validationBusinessName': 'Vui lòng nhập tên doanh nghiệp',
  'auth.onboarding.validationBusinessAddress':
    'Vui lòng nhập địa chỉ doanh nghiệp',
  'auth.onboarding.validationBusinessPhone':
    'Vui lòng nhập số điện thoại doanh nghiệp',
  'auth.onboarding.validationStorehouseAddress':
    'Vui lòng nhập địa chỉ kho hàng',
  'auth.onboarding.welcomeMessage':
    'Tài khoản đã được tạo! Đang chuyển đến trang chủ...',
  'auth.onboarding.welcomeTitle': 'Chào mừng!',
  'auth.onboarding.registrationFailed': 'Đăng ký thất bại. Vui lòng thử lại.',
  'auth.onboarding.registrationFailedTitle': 'Đăng ký thất bại',
  'auth.onboarding.joinWelcome':
    'Chào mừng đến {businessName}! Đang chuyển đến trang chủ...',
  'auth.onboarding.noToken':
    'Không có mã xác minh. Vui lòng kiểm tra liên kết trong email.',
  'auth.onboarding.invalidToken':
    'Liên kết xác minh không hợp lệ hoặc đã hết hạn.',
  'auth.onboarding.validationTitle': 'Xác thực',

  // Auth — Forgot Password
  'auth.forgotPasswordTitle': 'Quên mật khẩu',
  'auth.forgotPasswordDescription':
    'Nhập email và chúng tôi sẽ gửi liên kết đặt lại mật khẩu',
  'auth.sendResetLink': 'Gửi liên kết đặt lại',
  'auth.backToSignIn': 'Quay lại đăng nhập',
  'auth.rememberPassword': 'Nhớ mật khẩu?',
  'auth.forgotPasswordSuccess':
    'Nếu tài khoản với email đó tồn tại, liên kết đặt lại mật khẩu đã được gửi. Vui lòng kiểm tra hộp thư.',
  'auth.forgotPasswordError': 'Đã xảy ra lỗi. Vui lòng thử lại sau.',
  'auth.forgotPasswordErrorTitle': 'Lỗi',

  // Auth — Reset Password
  'auth.resetPasswordTitle': 'Đặt lại mật khẩu',
  'auth.resetPasswordDescription': 'Nhập mật khẩu mới bên dưới',
  'auth.invalidLink': 'Liên kết không hợp lệ',
  'auth.invalidLinkDescription':
    'Liên kết đặt lại mật khẩu không hợp lệ hoặc thiếu mã xác minh.',
  'auth.newPassword': 'Mật khẩu mới',
  'auth.confirmPasswordLabel': 'Xác nhận mật khẩu',
  'auth.resetting': 'Đang đặt lại...',
  'auth.resetPassword': 'Đặt lại mật khẩu',
  'auth.requestNewLink': 'Yêu cầu liên kết mới',
  'auth.resetPasswordsMismatch': 'Mật khẩu không khớp.',
  'auth.resetPasswordLength': 'Mật khẩu phải có ít nhất 8 ký tự.',
  'auth.resetPasswordStrength':
    'Mật khẩu phải chứa ít nhất một chữ hoa, một chữ thường và một số.',
  'auth.resetTokenMissing': 'Thiếu mã đặt lại.',
  'auth.resetTokenExpired':
    'Liên kết đặt lại không hợp lệ hoặc đã hết hạn. Vui lòng yêu cầu liên kết mới.',
  'auth.resetGenericError': 'Đã xảy ra lỗi. Vui lòng thử lại.',
  'auth.resetSuccess':
    'Mật khẩu đã được đặt lại thành công. Vui lòng đăng nhập.',
  'auth.resetSuccessTitle': 'Đặt lại mật khẩu',

  // ProtectedRoute
  'auth.loadingSession': 'Đang tải...',

  // NoBusiness page
  'auth.noBusiness.title': 'Không có doanh nghiệp',
  'auth.noBusiness.description':
    'Tài khoản của bạn không được liên kết với doanh nghiệp nào. Điều này có thể xảy ra nếu chủ doanh nghiệp đã xóa doanh nghiệp hoặc tài khoản của bạn đã bị xóa khỏi nhóm.',
  'auth.noBusiness.createTitle': 'Tạo doanh nghiệp',
  'auth.noBusiness.createDescription':
    'Bắt đầu mới bằng cách tạo doanh nghiệp riêng.',
  'auth.noBusiness.createButton': 'Tạo doanh nghiệp',
  'auth.noBusiness.joinTitle': 'Tham gia doanh nghiệp',
  'auth.noBusiness.joinDescription':
    'Nhờ quản trị viên mời bạn qua email. Nhấp vào liên kết trong email mời để tham gia tự động, hoặc dán mã thông báo bên dưới.',
  'auth.noBusiness.tokenPlaceholder': 'Dán mã mời…',
  'auth.noBusiness.joinButton': 'Tham gia doanh nghiệp',
  'auth.noBusiness.joinSuccess': 'Tham gia doanh nghiệp thành công!',
  'auth.noBusiness.joinFailed': 'Không thể tham gia doanh nghiệp',
  'auth.noBusiness.or': 'hoặc',
  'auth.noBusiness.loggedInAs': 'Đăng nhập với {email}',
  'auth.noBusiness.logout': 'Đăng xuất',

  // SetupBusiness page
  'auth.setup.title': 'Tạo doanh nghiệp',
  'auth.setup.description': 'Thiết lập doanh nghiệp của bạn để bắt đầu.',
  'auth.setup.nameLabel': 'Tên doanh nghiệp',
  'auth.setup.namePlaceholder': 'Ví dụ: Cửa hàng của tôi',
  'auth.setup.addressLabel': 'Địa chỉ',
  'auth.setup.addressPlaceholder': 'Ví dụ: 123 Đường chính',
  'auth.setup.phoneLabel': 'Số điện thoại',
  'auth.setup.phonePlaceholder': 'Ví dụ: 0901234567',
  'auth.setup.emailLabel': 'Email doanh nghiệp (tùy chọn)',
  'auth.setup.emailPlaceholder': 'Ví dụ: lienhe@cuahang.com',
  'auth.setup.storehouseLabel': 'Tên kho hàng đầu tiên',
  'auth.setup.storehousePlaceholder': 'Ví dụ: Kho chính',
  'auth.setup.submit': 'Tạo doanh nghiệp',
  'auth.setup.creating': 'Đang tạo…',
  'auth.setup.success': 'Tạo doanh nghiệp thành công!',
  'auth.setup.failed': 'Không thể tạo doanh nghiệp',
  'auth.setup.back': 'Quay lại',

  // ============================================
  // Navigation / Layout
  // ============================================
  'nav.inventory': 'Kho hàng',
  'nav.dashboard': 'Bảng điều khiển',
  'nav.orders': 'Đơn hàng',
  'nav.imports': 'Nhập hàng',
  'nav.dataImport': 'Nhập dữ liệu',
  'nav.transfers': 'Chuyển kho',
  'nav.clients': 'Khách hàng',
  'nav.suppliers': 'Nhà cung cấp',
  'nav.analytics': 'Phân tích',
  'nav.activity': 'Hoạt động',
  'nav.auditLog': 'Nhật ký',
  'nav.storeManager': 'Store Manager',
  'nav.toggleMenu': 'Mở/đóng menu',
  'nav.switchToLight': 'Chuyển sang chế độ sáng',
  'nav.switchToDark': 'Chuyển sang chế độ tối',
  'nav.profile': 'Hồ sơ',
  'nav.businessSettings': 'Cài đặt doanh nghiệp',
  'nav.teamManagement': 'Quản lý nhóm',
  'nav.billing': 'Thanh toán',
  'nav.devPortal': 'Cổng nhà phát triển',
  'nav.requestsInbox': 'Hộp thư yêu cầu',
  'nav.translations': 'Bản dịch',
  'nav.riskAnalytics': 'Phân tích rủi ro',
  'nav.stockIntelligence': 'Dự báo tồn kho',
  'nav.landing': 'Trang chủ',
  'seo.landing.title': 'khoai.io — Phần mềm quản lý kho hàng và cửa hàng',
  'nav.logout': 'Đăng xuất',
  'nav.requiresPlan': 'Yêu cầu gói {plan}',

  // ============================================
  // Home / Dashboard
  // ============================================
  'home.welcomeBack': 'Chào mừng trở lại, {name}!',
  'home.welcomeUser': 'Bạn',
  'home.subtitle': 'Tổng quan nhanh về hoạt động cửa hàng',
  'home.totalItems': 'Tổng sản phẩm',
  'home.lowStockItems': 'Sắp hết hàng',
  'home.pendingOrders': 'Đơn chờ xử lý',
  'home.activeImports': 'Nhập hàng đang xử lý',
  'home.lowStockAlert': 'Cảnh báo sắp hết hàng',
  'home.recentImports': 'Nhập hàng gần đây',
  'home.recentOrders': 'Đơn hàng gần đây',
  'home.quickRestock': 'Nhập hàng nhanh',
  'home.allWellStocked': 'Tất cả sản phẩm đều đủ hàng!',
  'home.noRecentImports': 'Chưa có nhập hàng gần đây',
  'home.noRecentOrders': 'Chưa có đơn hàng gần đây',
  'home.stock': 'Tồn kho: {quantity} {unit}',
  'home.lowStock': 'Sắp hết',
  'home.urgencyCritical': 'Nghiêm trọng',
  'home.urgencyUrgent': 'Cấp bách',
  'home.urgencyWatch': 'Theo dõi',
  'home.orderBefore': 'Còn {days} ngày',
  'home.unknownSupplier': 'NCC không rõ',
  'home.unknownClient': 'Khách không rõ',
  'home.viewLowStock': 'Xem tất cả {count} sản phẩm sắp hết →',
  'home.nItems': '{count} sản phẩm',
  'home.nItemsPlural': '{count} sản phẩm',

  // ============================================
  // Not Found (404)
  // ============================================
  'notFound.title': '404',
  'notFound.subtitle': 'Không tìm thấy trang',
  'notFound.description': 'Trang bạn tìm không tồn tại hoặc đã được di chuyển.',
  'notFound.backHome': 'Quay lại trang chủ',

  // ============================================
  // Error Boundary
  // ============================================
  'errorBoundary.unexpectedError': 'Đã xảy ra lỗi không mong muốn',
  'errorBoundary.title': 'Đã xảy ra lỗi',
  'errorBoundary.tryAgain': 'Thử lại',

  // ============================================
  // Data Import (Bulk Onboarding)
  // ============================================
  'dataImport.title': 'Nhập dữ liệu hàng loạt',
  'dataImport.subtitle':
    'Tải lên file CSV để tạo nhanh nhiều mặt hàng hoặc đối tác. Tải mẫu, điền thông tin, xem trước rồi xác nhận.',
  'dataImport.supportNote':
    'Nếu cấu trúc dữ liệu của bạn khác và cần hỗ trợ nhập dữ liệu, hãy email cho chúng tôi và mô tả vấn đề. Chúng tôi sẽ ưu tiên hỗ trợ ngay tại',
  'dataImport.entity': 'Loại dữ liệu',
  'dataImport.entity.items': 'Mặt hàng',
  'dataImport.entity.partners': 'Đối tác (khách hàng & nhà cung cấp)',
  'dataImport.downloadTemplate': 'Lấy mẫu',
  'dataImport.chooseFile': 'Chọn file',
  'dataImport.preview': 'Xem trước',
  'dataImport.previewing': 'Đang xem trước…',
  'dataImport.commit': 'Xác nhận nhập',
  'dataImport.committing': 'Đang nhập…',
  'dataImport.cancel': 'Hủy',
  'dataImport.cancelling': 'Đang hủy…',
  'dataImport.startOver': 'Bắt đầu lại',
  'dataImport.totalRows': 'Tổng số dòng',
  'dataImport.validRows': 'Dòng hợp lệ',
  'dataImport.errorRows': 'Dòng có lỗi',
  'dataImport.skippedRows': 'Bỏ qua (trùng lặp)',
  'dataImport.skippedNote':
    'Một số dòng đã bị bỏ qua vì bản ghi tương ứng đã tồn tại và chế độ xử lý trùng lặp đang là Bỏ qua.',
  'dataImport.errorsTitle': 'Lỗi phát hiện',
  'dataImport.errorsHelp':
    'Sửa các dòng này trong file CSV và tải lại, hoặc tiếp tục để nhập các dòng hợp lệ.',
  'dataImport.errorRow': 'Dòng',
  'dataImport.errorField': 'Trường',
  'dataImport.errorMessage': 'Thông báo',
  'dataImport.noFile': 'Vui lòng chọn file CSV trước.',
  'dataImport.previewSuccess': 'Đã xem trước — hãy kiểm tra và xác nhận.',
  'dataImport.commitSuccess': 'Đã nhập thành công {count} dòng.',
  'dataImport.commitNothing':
    'Không có gì để nhập — tất cả các dòng đều có lỗi. Hãy sửa và tải lại.',
  'dataImport.cancelled': 'Đã hủy nhập dữ liệu.',
  'dataImport.fileLabel': 'File',
  'dataImport.statusLabel': 'Trạng thái',
  'dataImport.fileSize': '{size} KB',
  'dataImport.dropHere': 'Thả file CSV vào đây',
  'dataImport.dropOrChoose': 'Kéo thả file CSV vào đây, hoặc bấm để chọn',
  'dataImport.fileHint': 'Chỉ chấp nhận .csv. Tối đa 10,000 dòng mỗi lần.',
  'dataImport.parsing': 'Đang đọc file…',
  'dataImport.parseFailed':
    'Không thể đọc file này. Hãy đảm bảo đó là file CSV hợp lệ.',
  // Ánh xạ cột
  'dataImport.mappingTitle': 'Ánh xạ cột',
  'dataImport.mappingHint':
    'Khớp từng cột trong file của bạn với trường đích. Cột để "Bỏ qua" sẽ không được nhập.',
  'dataImport.mappingSource': 'Cột của bạn',
  'dataImport.mappingTarget': 'Ánh xạ tới',
  'dataImport.mappingIgnore': '— Bỏ qua —',
  'dataImport.mappingEmpty': 'Hãy ánh xạ ít nhất một cột trước khi xem trước.',
  // Chọn dòng
  'dataImport.selectAll': 'Chọn tất cả các dòng trong nhóm',
  'dataImport.selectedRows': 'Đã chọn',
  'dataImport.commitSelected': 'Nhập {count} dòng đã chọn',
  // Tải lại các dòng lỗi
  'dataImport.downloadErrors': 'Tải dòng lỗi (CSV)',
  'dataImport.downloadingErrors': 'Đang chuẩn bị tải xuống…',
  // Tự động tạo kho (chỉ admin)
  'dataImport.autoCreateStorehouses': 'Tự động tạo kho còn thiếu (admin)',
  'dataImport.autoCreateStorehousesHint':
    'Khi bật, mọi storeHouseName trong file của bạn chưa tồn tại sẽ được tạo tự động với địa chỉ tạm thời mà bạn có thể sửa sau.',
  'dataImport.onDuplicateItem': 'Khi mặt hàng bị trùng',
  'dataImport.onDuplicateItem.ignore': 'Bỏ qua dòng mới',
  'dataImport.onDuplicateItem.add':
    'Cộng thêm số lượng nhập vào số lượng hiện có',
  'dataImport.onDuplicateItem.replace': 'Thay thế số lượng hiện có',
  'dataImport.onDuplicateItemHint':
    'Mặt hàng trùng được xác định theo SKU trước, sau đó theo tên mặt hàng trong cùng kho.',
  'dataImport.pendingStorehousesNote': 'Sẽ tạo {count} kho khi nhập: {names}',
  'dataImport.removeFile': 'Xóa file',
  'dataImport.replaceFile': 'Thay file',
  // Status badges
  'dataImport.status.previewed': 'Đã xem trước',
  'dataImport.status.committed': 'Đã nhập',
  'dataImport.status.failed': 'Thất bại',
  'dataImport.status.cancelled': 'Đã hủy',
  // Preview tabs
  'dataImport.previewTabs.errors': 'Lỗi ({count})',
  'dataImport.previewTabs.byStoreHouse': 'Theo kho',
  'dataImport.previewTabs.byPartnerType': 'Theo loại đối tác',
  'dataImport.noValidRows': 'Không có dòng hợp lệ để xem trước.',
  'dataImport.unassignedStoreHouse': 'Chưa chỉ định kho',
  'dataImport.unassignedWarning':
    'Mặt hàng chưa được gán kho. Mỗi mặt hàng phải thuộc một kho tồn tại — kiểm tra cột storeHouseName trong file CSV của bạn.',
  'dataImport.partnerType.supplier': 'Nhà cung cấp',
  'dataImport.partnerType.client': 'Khách hàng',
  'dataImport.partnerType.unknown': 'Khác',
  'dataImport.itemsCount': '{count} mặt hàng',
  'dataImport.partnersCount': '{count} đối tác',
  // Item columns
  'dataImport.col.name': 'Tên',
  'dataImport.col.sku': 'Mã SKU',
  'dataImport.col.unitPrice': 'Đơn giá',
  'dataImport.col.quantity': 'SL',
  'dataImport.col.unit': 'Đơn vị',
  'dataImport.col.lowStockAt': 'Cảnh báo tồn',
  // Partner columns
  'dataImport.col.partnerName': 'Tên',
  'dataImport.col.partnerType': 'Loại',
  'dataImport.col.phoneNumber': 'Điện thoại',
  'dataImport.col.email': 'Email',
  'dataImport.col.address': 'Địa chỉ',

  // ============================================
  // Inventory
  // ============================================
  'inventory.title': 'Kho hàng',
  'inventory.subtitle': 'Theo dõi và quản lý tồn kho tại tất cả các địa điểm',
  'inventory.addNewItem': 'Thêm sản phẩm',
  'inventory.exportCSV': 'Xuất CSV',
  'inventory.exporting': 'Đang xuất...',
  'inventory.totalItems': 'Tổng sản phẩm',
  'inventory.inventoryValue': 'Giá trị tồn kho',
  'inventory.outOfStock': 'Hết hàng',
  'inventory.lowStock': 'Sắp hết',
  'inventory.inStock': 'Còn hàng',
  'inventory.loadingInventory': 'Đang tải kho hàng...',
  'inventory.showingItems': 'Hiển thị {from} trong {total} sản phẩm',
  'inventory.pageOf': 'Trang {page} / {pages}',

  // Inventory — Add/Edit Item
  'inventory.editItem': 'Sửa sản phẩm',
  'inventory.itemDetails': 'Chi tiết sản phẩm',
  'inventory.viewItemInfo': 'Xem thông tin sản phẩm',
  'inventory.updateItemInfo': 'Cập nhật thông tin sản phẩm',
  'inventory.itemName': 'Tên sản phẩm',
  'inventory.itemNamePlaceholder': 'Nhập tên sản phẩm...',
  'inventory.similarItemFound': 'Tìm thấy sản phẩm tương tự',
  'inventory.similarItemsFound': 'Tìm thấy các sản phẩm tương tự',
  'inventory.autofillSuggestion':
    'Tìm thấy sản phẩm tương tự. Nhấn để tự động điền:',
  'inventory.conflictWarning':
    'Cân nhắc sử dụng sản phẩm hiện có hoặc chọn tên khác để tránh nhầm lẫn.',
  'inventory.description': 'Mô tả',
  'inventory.descriptionPlaceholder': 'Mô tả sản phẩm...',
  'inventory.sku': 'Mã SKU',
  'inventory.skuPlaceholder': 'VD: WH-BOLT-M8, 8901234567890',
  'inventory.skuHint': 'Mã sản phẩm tùy chọn dùng cho quét mã vạch',
  'inventory.generateSku': 'Tạo mã',
  'inventory.unitPrice': 'Đơn giá',
  'inventory.initialQuantity': 'Số lượng ban đầu',
  'inventory.lowStockAlert': 'Cảnh báo sắp hết',
  'inventory.alertThresholdHint': 'Cảnh báo khi tồn kho dưới mức này',
  'inventory.unit': 'Đơn vị',
  'inventory.unitPlaceholder': 'VD: cái, hộp, kg...',
  'inventory.existingUnits': 'Đơn vị đã có:',
  'inventory.storehouse': 'Kho',
  'inventory.selectStorehouse': 'Chọn kho...',
  'inventory.selectAStorehouse': 'Chọn một kho',
  'inventory.origin': 'Xuất xứ',
  'inventory.originPlaceholder': 'VD: Trung Quốc, Đài Loan, Mỹ',
  'inventory.tags': 'Nhãn',
  'inventory.tagsPlaceholder': 'Tìm kiếm hoặc nhập nhãn...',
  'inventory.addMoreTags': 'Thêm nhãn...',
  'inventory.tagsHint': 'Nhấn Enter hoặc dấu phẩy để thêm nhãn tùy chỉnh',
  'inventory.separateTagsCommas': 'Phân cách nhãn bằng dấu phẩy',
  'inventory.createItem': 'Tạo sản phẩm',
  'inventory.creating': 'Đang tạo...',
  'inventory.itemCreatedSuccess': 'Tạo sản phẩm thành công!',
  'inventory.itemUpdatedSuccess': '{name} đã được cập nhật',
  'inventory.itemUpdated': 'Đã cập nhật sản phẩm',

  // Inventory — Details View
  'inventory.name': 'Tên',
  'inventory.quantity': 'Số lượng',
  'inventory.location': 'Vị trí',
  'inventory.itemCode': 'Mã sản phẩm',
  'inventory.qr': 'QR',
  'inventory.barcode': 'Mã vạch',
  'inventory.barcodePrintComingSoon': 'In nhãn mã vạch & QR — sắp ra mắt',
  'inventory.reorderLevel': 'Mức đặt lại',
  'inventory.tagsCategories': 'Nhãn/Danh mục',
  'inventory.addTag': 'Thêm nhãn...',
  'inventory.add': 'Thêm',
  'inventory.saving': 'Đang lưu...',
  'inventory.saveChanges': 'Lưu thay đổi',
  'inventory.transfer': 'Chuyển kho',
  'inventory.printLabel': 'In nhãn',

  // Inventory — Transfer
  'inventory.transferStock': 'Chuyển kho',
  'inventory.moveToStorehouse': 'Chuyển {itemName} sang kho khác',
  'inventory.currentLocation': 'Vị trí hiện tại',
  'inventory.unknown': 'Không xác định',
  'inventory.availableStock': 'Tồn kho có sẵn',
  'inventory.destinationStorehouse': 'Kho đích',
  'inventory.quantityToTransfer': 'Số lượng chuyển',
  'inventory.fullTransferWarning':
    'Chuyển toàn bộ — sản phẩm sẽ được chuyển hoàn toàn',
  'inventory.partialTransferInfo':
    'Chuyển một phần — {qty} {unit} sẽ được chuyển, còn lại {remaining}',
  'inventory.note': 'Ghi chú',
  'inventory.noteOptional': '(tùy chọn)',
  'inventory.reasonPlaceholder': 'Lý do chuyển kho...',
  'inventory.transferNow': 'Chuyển ngay',
  'inventory.moveImmediately': 'Chuyển kho ngay lập tức',
  'inventory.pendingTransfer': 'Chờ duyệt',
  'inventory.createRequestLater': 'Tạo yêu cầu, hoàn thành sau',
  'inventory.transferring': 'Đang chuyển...',
  'inventory.createTransferRequest': 'Tạo yêu cầu chuyển kho',
  'inventory.stockTransferredSuccess': 'Chuyển kho thành công',
  'inventory.transferRequestCreated': 'Đã tạo yêu cầu chuyển kho (chờ duyệt)',

  // Inventory — Filters
  'inventory.searchPlaceholder': 'Tìm theo tên, mô tả hoặc nhãn...',
  'inventory.stock': 'Tồn kho:',
  'inventory.allStock': 'Tất cả',
  'inventory.locationFilter': 'Vị trí:',
  'inventory.allLocations': 'Tất cả vị trí',
  'inventory.tagsFilter': 'Nhãn:',
  'inventory.loadingTags': 'Đang tải nhãn...',
  'inventory.noTagsYet': 'Chưa có nhãn nào',

  // Inventory — Table
  'inventory.item': 'Sản phẩm',
  'inventory.category': 'Danh mục',
  'inventory.stockStatus': 'Tình trạng',
  'inventory.cost': 'Giá',
  'inventory.lastUpdated': 'Cập nhật lần cuối',
  'inventory.unitLabel': 'Đơn vị: {unit}',
  'inventory.noItemsFound': 'Không tìm thấy sản phẩm',
  'inventory.adjustFiltersHint': 'Thử điều chỉnh bộ lọc hoặc từ khóa tìm kiếm',
  'inventory.nItemsSelected': 'Đã chọn {count} sản phẩm',
  'inventory.inStockCount': 'Còn {count} trong kho',
  'inventory.importStock': 'Nhập hàng',
  'inventory.createImportOrder': 'Tạo đơn nhập hàng',
  'inventory.viewDetails': 'Xem chi tiết',
  'inventory.seeFullInfo': 'Xem thông tin đầy đủ',
  'inventory.generateBarcodeQr': 'Tạo mã vạch / QR',
  'inventory.printAll': 'In tất cả ({count})',
  'inventory.printAllDescription': 'In nhãn cho tất cả mục hiển thị',
  'inventory.printSelected': 'In đã chọn',

  // Inventory — Print Labels
  'inventory.printLabels': 'In nhãn',
  'inventory.nItems': '{count} sản phẩm',
  'inventory.nLabels': '{count} / {max} nhãn',
  'inventory.maxLabelsReached': 'Đã đạt giới hạn nhãn tối đa',
  'inventory.codeType': 'Loại mã',
  'inventory.qrCode': 'Mã QR',
  'inventory.labelSize': 'Kích thước nhãn',
  'inventory.itemsCopies': 'Sản phẩm & Số lượng bản',
  'inventory.searchItems': 'Tìm sản phẩm…',
  'inventory.noMatchSearch': 'Không tìm thấy sản phẩm khớp "{term}"',
  'inventory.qty': 'SL: {qty}',
  'inventory.preview': 'Xem trước',
  'inventory.andMoreItems': '… và {count} sản phẩm khác',
  'inventory.downloadPdf': 'Tải PDF',
  'inventory.generatingPdf': 'Đang tạo…',
  'inventory.codeGenerationError': 'Không thể tạo mã',

  // Inventory — Stock Adjustment
  'inventory.adjustStock': 'Điều chỉnh tồn kho',
  'inventory.stockAdjustedSuccess': 'Điều chỉnh tồn kho thành công!',
  'inventory.currentStock': 'Tồn kho hiện tại',
  'inventory.addStock': '+ Thêm hàng',
  'inventory.removeStock': '− Xuất hàng',
  'inventory.newStockHint': 'Tồn kho mới: {qty} {unit}',
  'inventory.reason': 'Lý do',
  'inventory.supplierDelivery': 'Nhà cung cấp giao hàng',
  'inventory.customerReturn': 'Khách hàng trả lại',
  'inventory.manualCount': 'Kiểm kê thủ công',
  'inventory.sale': 'Bán hàng',
  'inventory.damagedDefective': 'Hư hỏng/Lỗi',
  'inventory.theftLoss': 'Mất cắp/Thất thoát',
  'inventory.notesOptional': 'Ghi chú (tùy chọn)',
  'inventory.notesPlaceholder': 'Thêm ghi chú...',
  'inventory.adjusting': 'Đang điều chỉnh...',
  'inventory.confirmAdjustment': 'Xác nhận điều chỉnh',
  'inventory.validQuantityRequired': 'Vui lòng nhập số lượng hợp lệ lớn hơn 0',
  'inventory.cannotRemoveMore':
    'Không thể xuất {qty} đơn vị. Chỉ còn {available}.',

  // Inventory — Create Import
  'inventory.createImportFor': 'Tạo đơn nhập hàng cho {itemName}',
  'inventory.noStorehouse': 'Không có kho',
  'inventory.supplierOptional': 'Nhà cung cấp (tùy chọn)',
  'inventory.noSupplierOther': 'Không có NCC / Khác',
  'inventory.leaveEmptyInternal':
    'Để trống cho chuyển nội bộ hoặc nhập không qua NCC',
  'inventory.pricePerUnit': 'Giá mỗi {unit} từ nhà cung cấp',
  'inventory.newStockWillBe': 'Tồn kho mới sẽ là: {qty} {unit}',
  'inventory.totalAmount': 'Tổng tiền',
  'inventory.importCreatedSuccess': 'Đã tạo đơn nhập cho {itemName}',
  'inventory.importCreated': 'Đã tạo đơn nhập',

  // ============================================
  // Orders
  // ============================================
  'orders.title': 'Đơn hàng',
  'orders.subtitle': 'Theo dõi và quản lý giao dịch bán hàng với khách hàng',
  'orders.newOrder': 'Đơn mới',
  'orders.exportCSV': 'Xuất CSV',
  'orders.exporting': 'Đang xuất...',
  'orders.allOrders': 'Tất cả đơn hàng',
  'orders.validationMinItem': 'Cần ít nhất một sản phẩm hợp lệ',

  // Orders — Filters
  'orders.searchPlaceholder': 'Tìm theo mã, khách hàng, email, SĐT...',
  'orders.allClients': 'Tất cả khách hàng',
  'orders.sortDate': 'Ngày',
  'orders.sortTotalPrice': 'Tổng tiền',
  'orders.sortClientName': 'Tên khách hàng',
  'orders.sortNewestFirst': 'Mới nhất',
  'orders.sortOldestFirst': 'Cũ nhất',
  'orders.sortHighToLow': 'Cao đến thấp',
  'orders.sortLowToHigh': 'Thấp đến cao',
  'orders.client': 'Khách hàng',

  // Orders — Table
  'orders.loadingOrders': 'Đang tải đơn hàng...',
  'orders.failedToLoad': 'Không thể tải đơn hàng',
  'orders.noMatch': 'Không tìm thấy đơn hàng phù hợp',
  'orders.noOrders': 'Chưa có đơn hàng',
  'orders.nSelected': '{count} đơn hàng đã chọn',
  'orders.orderId': 'Mã đơn',
  'orders.nItems': '{count} sản phẩm',
  'orders.viewDetails': 'Xem chi tiết',
  'orders.editOrder': 'Sửa đơn hàng',
  'orders.cannotEdit':
    'Không thể sửa - hàng đã giao. Hãy bỏ đánh dấu giao hàng trước.',
  'orders.deleteOrder': 'Xóa đơn hàng',
  'orders.showingOrders': 'Hiển thị {from} trong {total} đơn hàng',
  'orders.pageOfOrders': 'Trang {page} / {pages}',
  'orders.matchSearch': '{count} đơn hàng phù hợp',
  'orders.onThisPage': '({rawCount} trên trang này, tổng {total})',
  'orders.clearSearch': 'Xóa tìm kiếm để xem tất cả {count} đơn trên trang này',

  // Orders — Form Modal
  'orders.createNewOrder': 'Tạo đơn hàng mới',
  'orders.editOrderTitle': 'Sửa đơn hàng',
  'orders.formClient': 'Khách hàng',
  'orders.formItems': 'Sản phẩm *',
  'orders.formTotalAmount': 'Tổng tiền',
  'orders.addItem': 'Thêm sản phẩm',
  'orders.createOrder': 'Tạo đơn hàng',
  'orders.updateOrder': 'Cập nhật đơn hàng',
  'orders.walkInCustomer': 'Khách vãng lai',
  'orders.selectItem': 'Chọn sản phẩm...',
  'orders.qty': 'SL',
  'orders.formPrice': 'Giá',

  // Orders — Delete Modal
  'orders.deleteOrderTitle': 'Xóa đơn hàng',
  'orders.deleteConfirm':
    'Bạn có chắc muốn xóa đơn hàng này? Thao tác này không thể hoàn tác.',

  // Orders — Confirm Action Modal
  'orders.confirmAction': 'Xác nhận thao tác',

  // Orders — Detail Modal
  'orders.orderDetails': 'Chi tiết đơn hàng',
  'orders.orderItems': 'Sản phẩm trong đơn',
  'orders.actionsTitle': 'Thao tác',
  'orders.reactivateOrder': 'Kích hoạt lại đơn',
  'orders.reopenOrder': 'Mở lại đơn',
  'orders.statusLabel': 'Trạng thái',
  'orders.clientLabel': 'Khách hàng',
  'orders.totalAmount': 'Tổng tiền',
  'orders.createdAt': 'Ngày tạo',
  'orders.itemsDeliveredOn': 'Ngày giao hàng',
  'orders.paymentCompletedOn': 'Ngày thanh toán',
  'orders.listedPrice': 'Giá niêm yết: {price}',
  'orders.sellPrice': 'Giá bán: {price}',
  'orders.discountPercent': '(giảm {pct}%)',
  'orders.markupPercent': '(+{pct}% phụ thu)',
  'orders.savedAmount': 'Tiết kiệm {amount}',
  'orders.itemFallback': 'Sản phẩm {id}',
  'orders.cannotEditWarning': 'Không thể sửa sản phẩm',
  'orders.cannotEditDescription':
    'Hàng đã giao và đã trừ khỏi kho. Để sửa, hãy bỏ đánh dấu giao hàng trước.',
  'orders.markDelivered': 'Đánh dấu đã giao',
  'orders.removeFromInventory': 'Trừ sản phẩm khỏi kho',
  'orders.completePayment': 'Hoàn tất thanh toán',
  'orders.markPaymentComplete': 'Đánh dấu thanh toán từ khách hàng hoàn tất',
  'orders.markDeliveredBtn': 'Đánh dấu đã giao',
  'orders.unmark': 'Bỏ đánh dấu',
  'orders.markPaid': 'Đánh dấu đã trả',
  'orders.completeOrder': 'Hoàn tất đơn hàng',
  'orders.cancelOrder': 'Hủy đơn hàng',
  'orders.markAsPending': 'Đánh dấu chờ xử lý',
  'orders.confirmDelivery': 'Thao tác này sẽ trừ sản phẩm khỏi kho. Tiếp tục?',
  'orders.confirmUndelivery':
    'Thao tác này sẽ thêm sản phẩm trở lại kho. Tiếp tục?',
  'orders.confirmPayment': 'Đánh dấu thanh toán hoàn tất?',
  'orders.confirmUnpayment': 'Bỏ đánh dấu thanh toán hoàn tất?',
  'orders.confirmComplete': 'Đánh dấu đơn hàng hoàn thành?',
  'orders.confirmCancel': 'Hủy đơn hàng này? Thao tác không thể hoàn tác.',
  'orders.reactivateDescription':
    'Đơn hàng sẽ chuyển lại trạng thái chờ xử lý, cho phép bạn hoàn tất hoặc chỉnh sửa.',
  'orders.reopenDescription':
    'Đơn hàng sẽ chuyển lại trạng thái chờ xử lý. Các ngày (giao hàng, thanh toán) sẽ được giữ nguyên.',

  // Orders — Selection Bar
  'orders.nOrdersSelected': '{count} đơn đã chọn',
  'orders.report': 'Báo cáo',
  'orders.receipt': 'Hóa đơn',
  'orders.printReceipts': 'In hóa đơn',

  // Orders — Print
  'orders.print.storeManager': 'Store Manager',
  'orders.print.total': 'Tổng:',
  'orders.print.grandTotal': 'TỔNG CỘNG:',
  'orders.print.thankYou': 'Cảm ơn quý khách!',
  'orders.print.walkIn': 'Khách vãng lai',
  'orders.print.itemFallback': 'Sản phẩm',
  'orders.print.businessReport': 'Báo cáo kinh doanh',
  'orders.print.generatedOn': 'Tạo ngày {date}',
  'orders.print.totalOrders': 'Tổng đơn hàng',
  'orders.print.totalRevenue': 'Tổng doanh thu',
  'orders.print.avgOrder': 'Đơn TB',
  'orders.print.walkInCustomer': 'Khách vãng lai',
  'orders.print.endOfReport': 'Hết báo cáo',
  'orders.print.ordersAcross': '{orders} đơn trong {days} ngày',
  'orders.print.thankYouMessage': 'Cảm ơn bạn đã sử dụng Store Manager!',
  'orders.print.receiptTitle': 'Hóa đơn - {date}',
  'orders.print.reportTitle': 'Báo cáo kinh doanh - {date}',
  'orders.print.item': 'Sản phẩm',
  'orders.print.qty': 'SL',
  'orders.print.price': 'Giá',
  'orders.print.unitPrice': 'Đơn giá',
  'orders.print.subtotal': 'Tạm tính',
  'orders.print.orderTotal': 'Tổng đơn:',
  'orders.print.days': 'Ngày',
  'orders.print.nOrders': '{count} đơn',

  // ============================================
  // Quick Restock Modal
  // ============================================
  'restock.title': 'Nhập hàng nhanh',
  'restock.summary': '{items} · {suppliers} · Ước tính {cost}',
  'restock.nItems': '{count} sản phẩm',
  'restock.nItemsPlural': '{count} sản phẩm',
  'restock.nSuppliers': '{count} nhà cung cấp',
  'restock.nSuppliersPlural': '{count} nhà cung cấp',
  'restock.noItems': 'Không có sản phẩm cần nhập',
  'restock.unassigned': 'Chưa gán',
  'restock.current': 'Hiện tại: {quantity} {unit}',
  'restock.alertAt': 'Cảnh báo: {threshold} (do bạn đặt)',
  'restock.alertAtTooltip':
    'Ngưỡng cảnh báo tồn kho do bạn cài đặt trong sản phẩm',
  'restock.removeItem': 'Xóa sản phẩm',
  'restock.supplier': 'Nhà cung cấp',
  'restock.orderQuantity': 'Số lượng đặt ({unit})',
  'restock.unitPrice': 'Đơn giá',
  'restock.subtotal': 'Tạm tính: {amount}',
  'restock.totalCost': 'Tổng chi phí:',
  'restock.createImports': 'Tạo {count} đơn nhập',
  'restock.createImportsPlural': 'Tạo {count} đơn nhập',
  'restock.creating': 'Đang tạo...',
  'restock.assignSuppliers': 'Vui lòng gán nhà cung cấp cho sản phẩm',
  'restock.noSuppliersTitle': 'Chưa có nhà cung cấp',
  'restock.noSuppliersDescription':
    'Bạn vẫn có thể tạo đơn nhập hàng không gán nhà cung cấp, hoặc tạo nhà cung cấp trước để quản lý mua hàng rõ ràng hơn.',
  'restock.noSuppliersOption': 'Không có nhà cung cấp',
  'restock.createSupplier': 'Tạo nhà cung cấp',
  'restock.successSingle': 'Đã tạo thành công {count} đơn nhập hàng',
  'restock.successPlural': 'Đã tạo thành công {count} đơn nhập hàng',
  'restock.aiTooltip':
    'Gợi ý AI dựa trên tốc độ tiêu thụ & thời gian giao hàng',
  'restock.aiDetected': 'AI phát hiện',
  'restock.aiDetectedTooltip':
    'Sản phẩm này được AI phát hiện dựa trên tốc độ bán và thời gian giao hàng, không chỉ ngưỡng tồn kho',

  // ============================================
  // Imports
  // ============================================
  'imports.title': 'Nhập hàng',
  'imports.subtitle': 'Quản lý đơn mua hàng và bổ sung tồn kho từ nhà cung cấp',
  'imports.newImport': 'Nhập mới',
  'imports.restockLowStock': 'Nhập hàng sắp hết',
  'imports.allImports': 'Tất cả nhập hàng',

  // Imports — Table
  'imports.loadingImports': 'Đang tải nhập hàng...',
  'imports.noMatch': 'Không tìm thấy nhập hàng phù hợp',
  'imports.noImports': 'Chưa có nhập hàng',
  'imports.importId': 'Mã nhập',
  'imports.supplier': 'Nhà cung cấp',
  'imports.nItems': '{count} sản phẩm',

  // Imports — Filters
  'imports.searchPlaceholder': 'Tìm theo mã, nhà cung cấp, email, SĐT...',
  'imports.allSuppliers': 'Tất cả NCC',
  'imports.sortDate': 'Ngày',
  'imports.sortTotalPrice': 'Tổng tiền',
  'imports.sortSupplierName': 'Tên NCC',
  'imports.sortNewestFirst': 'Mới nhất',
  'imports.sortOldestFirst': 'Cũ nhất',
  'imports.noSupplier': 'Không có NCC',
  'imports.printMode': 'Chế độ in:',
  'imports.a4Report': 'Báo cáo A4',
  'imports.thermalReceipt': 'Hóa đơn nhiệt (80mm)',

  // Imports — Form Modal
  'imports.createImport': 'Tạo nhập hàng',
  'imports.editImport': 'Sửa nhập hàng',
  'imports.supplierOptional': 'Nhà cung cấp (Tùy chọn)',
  'imports.formItems': 'Sản phẩm *',
  'imports.unitPrice': 'Đơn giá',
  'imports.subtotal': 'Thành tiền:',
  'imports.formTotal': 'Tổng:',
  'imports.addItem': '+ Thêm sản phẩm',
  'imports.selectItem': 'Chọn sản phẩm...',
  'imports.validationMinItem':
    'Vui lòng thêm ít nhất một sản phẩm với đầy đủ thông tin',

  // Imports — Delete Modal
  'imports.deleteImport': 'Xóa nhập hàng',
  'imports.deleteConfirm':
    'Bạn có chắc muốn xóa nhập hàng {id}? Thao tác này không thể hoàn tác.',

  // Imports — Detail
  'imports.confirmAction': 'Xác nhận thao tác',
  'imports.supplierLabel': 'Nhà cung cấp:',
  'imports.statusLabel': 'Trạng thái:',
  'imports.totalLabel': 'Tổng:',
  'imports.itemsReceived': 'Đã nhận hàng:',
  'imports.paymentCompleted': 'Đã thanh toán:',
  'imports.createdLabel': 'Ngày tạo:',
  'imports.actionsTitle': 'Thao tác',
  'imports.markReceived': 'Đánh dấu đã nhận',
  'imports.addToInventory': 'Thêm sản phẩm vào kho',
  'imports.completePayment': 'Hoàn tất thanh toán',
  'imports.markPaymentToSupplier': 'Đánh dấu đã thanh toán cho nhà cung cấp',
  'imports.reactivateImport': 'Kích hoạt lại nhập hàng',
  'imports.reactivateDescription':
    'Chuyển trạng thái về chờ xử lý, cho phép hoàn tất hoặc chỉnh sửa.',
  'imports.reopenImport': 'Mở lại nhập hàng',
  'imports.reopenDescription':
    'Chuyển trạng thái về chờ xử lý. Các ngày (nhận hàng, thanh toán) sẽ được giữ nguyên.',
  'imports.markReceivedBtn': 'Đánh dấu đã nhận',
  'imports.unmark': 'Bỏ đánh dấu',
  'imports.markPaid': 'Đánh dấu đã trả',
  'imports.completeImport': 'Hoàn tất nhập hàng',
  'imports.cancelImport': 'Hủy nhập hàng',
  'imports.markAsPending': 'Đánh dấu chờ xử lý',
  'imports.confirmReceive': 'Thao tác này sẽ thêm sản phẩm vào kho. Tiếp tục?',
  'imports.confirmUnreceive':
    'Thao tác này sẽ xóa sản phẩm khỏi kho. Tiếp tục?',
  'imports.confirmPayment': 'Đánh dấu thanh toán hoàn tất?',
  'imports.confirmUnpayment': 'Bỏ đánh dấu thanh toán hoàn tất?',
  'imports.confirmComplete': 'Đánh dấu nhập hàng hoàn thành?',
  'imports.confirmCancel': 'Hủy nhập hàng này? Thao tác không thể hoàn tác.',
  'imports.cannotEditWarning': 'Không thể sửa sản phẩm',
  'imports.cannotEditDescription':
    'Hàng đã nhận và đã vào kho. Để sửa, hãy bỏ đánh dấu nhận hàng trước.',
  'imports.cannotEditTooltip':
    'Không thể sửa - hàng đã trong kho. Bỏ đánh dấu nhận hàng trước.',

  // Imports — Pagination
  'imports.showingImports': 'Hiển thị {from} trong {total} nhập hàng',
  'imports.pageOfImports': 'Trang {page} / {pages}',
  'imports.matchSearch': '{count} nhập hàng phù hợp',
  'imports.clearSearch':
    'Xóa tìm kiếm để xem tất cả {count} nhập hàng trên trang này',

  // Imports — Selection & Print
  'imports.nSelected': '{count} nhập hàng đã chọn',
  'imports.printSelected': 'In đã chọn',
  'imports.print.purchaseImports': 'NHẬP HÀNG',
  'imports.print.purchaseImportReport': 'BÁO CÁO NHẬP HÀNG',
  'imports.print.purchaseReport': 'Báo cáo nhập hàng',
  'imports.print.totalImports': 'Tổng nhập hàng',
  'imports.print.totalValue': 'Tổng giá trị',
  'imports.print.avgImport': 'TB mỗi lần nhập',
  'imports.print.generatedOn': 'Tạo ngày {date}',
  'imports.print.noSupplier': 'Không có NCC',
  'imports.print.grandTotal': 'TỔNG CỘNG:',
  'imports.print.storeManager': 'Store Manager',
  'imports.print.receiptTitle': 'Biên lai nhập hàng - {date}',
  'imports.print.reportTitle': 'Báo cáo nhập hàng - {date}',
  'imports.print.nImports': '{count} lần nhập',
  'imports.print.total': 'Tổng:',
  'imports.print.days': 'Ngày',
  'imports.print.item': 'Sản phẩm',
  'imports.print.id': 'Mã',
  'imports.print.supplier': 'Nhà cung cấp',
  'imports.print.items': 'Sản phẩm',
  'imports.print.status': 'Trạng thái',
  'imports.print.importsAndTotal': '{count} nhập hàng - {total}',
  'imports.print.endOfReport': 'Kết thúc báo cáo',
  'imports.print.thankYouMessage': 'Cảm ơn bạn đã sử dụng Store Manager!',

  // Imports — Tooltips
  'imports.viewDetails': 'Xem chi tiết',
  'imports.editImportTooltip': 'Sửa nhập hàng',
  'imports.deleteImportTooltip': 'Xóa nhập hàng',

  // ============================================
  // Transfers
  // ============================================
  'transfers.title': 'Chuyển kho',
  'transfers.subtitle': 'Theo dõi chuyển hàng giữa các kho',
  'transfers.featureGate':
    'Chuyển kho yêu cầu gói Pro hoặc Enterprise. Nâng cấp để di chuyển hàng giữa các kho.',
  'transfers.noTransfers': 'Chưa có chuyển kho',
  'transfers.searchPlaceholder': 'Tìm theo ID...',
  'transfers.emptyDescription':
    'Các lần chuyển kho sẽ hiển thị ở đây khi bạn di chuyển hàng giữa các kho.',
  'transfers.emptyHint':
    'Vào Kho hàng → Xem sản phẩm → Chuyển kho để di chuyển hàng.',
  'transfers.exceptionsTitle': 'Ngoại lệ chuyển kho',
  'transfers.exceptionsSubtitle':
    'Ưu tiên xử lý các phiếu chuyển bị kẹt quá lâu trong trạng thái vận chuyển hoặc nhập một phần.',
  'transfers.overdueInTransit': 'Quá hạn đang vận chuyển',
  'transfers.stalledPartialReceives': 'Nhập một phần bị chậm',
  'transfers.noPendingExceptions':
    'Hiện không có ngoại lệ chuyển kho nào cần xử lý.',
  'transfers.noOverdueInTransit':
    'Không có phiếu chuyển quá hạn đang vận chuyển.',
  'transfers.noStalledPartialReceives':
    'Không có phiếu chuyển nhập một phần bị chậm.',
  'transfers.exceptionReferenceDate': 'Cần xem xét từ {date}',
  'transfers.viewDetails': 'Xem chi tiết',
  'transfers.activityTimeline': 'Dòng thời gian hoạt động',
  'transfers.timelineCreated': 'Tạo phiếu chuyển',
  'transfers.timelineShipped': 'Đã xuất kho',
  'transfers.timelineInTransit': 'Đánh dấu đang vận chuyển',
  'transfers.timelineReceived': 'Đã nhập {quantity} {unit}',
  'transfers.timelineCompleted': 'Hoàn tất chuyển kho',
  'transfers.timelineDiscrepancy': 'Chênh lệch: {reason}',
  'transfers.timelineNoteValue': 'Ghi chú: {note}',

  // Transfers — Detail Modal
  'transfers.detailTitle': 'Chi tiết chuyển kho',
  'transfers.itemLabel': 'Sản phẩm',
  'transfers.quantityLabel': 'Số lượng:',
  'transfers.from': 'Từ',
  'transfers.to': 'Đến',
  'transfers.note': 'Ghi chú',
  'transfers.transferred': 'Đã chuyển',
  'transfers.created': 'Ngày tạo',
  'transfers.complete': 'Hoàn tất',
  'transfers.ship': 'Xuất kho',
  'transfers.markInTransit': 'Đánh dấu đang vận chuyển',
  'transfers.receive': 'Nhập kho',
  'transfers.statusShipped': 'Đã xuất',
  'transfers.statusInTransit': 'Đang vận chuyển',
  'transfers.statusPartiallyReceived': 'Nhập một phần',
  'transfers.cancelTransfer': 'Hủy chuyển kho',
  'transfers.completeTransfer': 'Hoàn tất chuyển kho',
  'transfers.deleteTransfer': 'Xóa chuyển kho',
  'transfers.none': 'Không có',
  'transfers.unknownItem': 'Sản phẩm không rõ',
  'transfers.units': 'đơn vị',

  // Transfers — Confirm
  'transfers.confirmComplete': 'Hoàn tất chuyển kho?',
  'transfers.confirmShip': 'Xuất kho?',
  'transfers.confirmMarkInTransit': 'Đánh dấu đang vận chuyển?',
  'transfers.confirmReceive': 'Nhập kho?',
  'transfers.confirmCancel': 'Hủy chuyển kho?',
  'transfers.confirmDelete': 'Xóa chuyển kho?',
  'transfers.confirmCompleteDesc':
    'Thao tác này sẽ chuyển {quantity} {unit} "{itemName}" từ {from} đến {to}.',
  'transfers.confirmShipDesc':
    'Thao tác này đánh dấu phiếu chuyển là đã xuất kho và sẵn sàng vận chuyển.',
  'transfers.confirmMarkInTransitDesc':
    'Thao tác này đánh dấu phiếu chuyển đang vận chuyển.',
  'transfers.confirmReceiveDesc':
    'Nhập kho theo số lượng hiện tại. Có thể nhập một phần và hoàn tất sau.',
  'transfers.confirmCancelDesc':
    'Chuyển kho sẽ bị hủy. Không có hàng nào được di chuyển.',
  'transfers.confirmDeleteDesc': 'Bản ghi chuyển kho này sẽ bị xóa vĩnh viễn.',
  'transfers.receiveQuantity': 'Số lượng nhập',
  'transfers.receiveRemaining': 'Còn lại cần nhập: {qty}',
  'transfers.availableNow': 'Khả dụng hiện tại ở kho nguồn: {qty}',
  'transfers.availableLessThanRemaining':
    'Tồn kho nguồn hiện thấp hơn số lượng còn lại của phiếu chuyển. Đã giới hạn về mức có thể nhập ngay.',
  'transfers.noAvailableToReceive':
    'Hiện không còn hàng khả dụng để nhập cho phiếu chuyển này.',
  'transfers.discrepancyReasonOptional': 'Lý do chênh lệch (Tùy chọn)',
  'transfers.receiveNoteOptional': 'Ghi chú nhập kho (Tùy chọn)',
  'transfers.reasonDamaged': 'Hư hỏng',
  'transfers.reasonMissing': 'Thiếu hàng',
  'transfers.reasonWrongItem': 'Sai sản phẩm',
  'transfers.reasonQtyMismatch': 'Lệch số lượng',
  'transfers.reasonOther': 'Khác',
  'transfers.deleted': 'Đã xóa chuyển kho',

  // ============================================
  // Clients
  // ============================================
  'clients.title': 'Khách hàng',
  'clients.subtitle': 'Quản lý khách hàng của bạn',
  'clients.newClient': 'Thêm khách hàng',
  'clients.searchPlaceholder': 'Tìm khách hàng...',
  'clients.loadingClients': 'Đang tải khách hàng...',
  'clients.noClients': 'Chưa có khách hàng',
  'clients.createPartner': 'Tạo khách hàng',
  'clients.editPartner': 'Sửa khách hàng',
  'clients.deletePartner': 'Xóa khách hàng',
  'clients.clientName': 'Tên khách hàng *',
  'clients.phoneNumber': 'Số điện thoại',
  'clients.deleteConfirm':
    'Bạn có chắc muốn xóa {name}? Thao tác này không thể hoàn tác.',
  'clients.showingClients': 'Hiển thị {from} trong {total} khách hàng',
  'clients.pageOfClients': 'Trang {page} / {pages}',
  'clients.transactions': '{name} - Giao dịch',
  'clients.noTransactions': 'Chưa có giao dịch',
  'clients.noTransactionsMatch': 'Không có giao dịch phù hợp',
  'clients.showingTransactions': 'Hiển thị {from} trong {total} giao dịch',
  'clients.viewTransactions': 'Xem giao dịch',
  'clients.editClient': 'Sửa khách hàng',
  'clients.deleteClient': 'Xóa khách hàng',
  'clients.toggleFilters': 'Bộ lọc',
  'clients.printReport': 'In báo cáo',
  'clients.filters': 'Bộ lọc',

  // Clients — Print
  'clients.print.transactionsTitle': 'Giao dịch khách hàng - {name}',
  'clients.print.totalTransactions': 'Tổng giao dịch',
  'clients.print.totalAmount': 'Tổng tiền',
  'clients.print.avgTransaction': 'TB mỗi giao dịch',
  'clients.print.generatedOn': 'Tạo ngày {date}',
  'clients.print.transactionTotal': 'Tổng giao dịch:',
  'clients.print.storeManager': 'Store Manager',
  'clients.print.clientFallback': 'Khách hàng',
  'clients.card.transaction': 'Giao dịch',
  'clients.card.status': 'Trạng thái:',
  'clients.card.total': 'Tổng:',

  // ============================================
  // Suppliers
  // ============================================
  'suppliers.title': 'Nhà cung cấp',
  'suppliers.subtitle': 'Quản lý nhà cung cấp của bạn',
  'suppliers.newSupplier': 'Thêm NCC',
  'suppliers.searchPlaceholder': 'Tìm nhà cung cấp...',
  'suppliers.loadingSuppliers': 'Đang tải nhà cung cấp...',
  'suppliers.noSuppliers': 'Chưa có nhà cung cấp',
  'suppliers.createSupplier': 'Tạo nhà cung cấp',
  'suppliers.editSupplier': 'Sửa nhà cung cấp',
  'suppliers.deleteSupplier': 'Xóa nhà cung cấp',
  'suppliers.supplierName': 'Tên NCC *',
  'suppliers.phoneNumber': 'Số điện thoại',
  'suppliers.deleteConfirm':
    'Bạn có chắc muốn xóa {name}? Thao tác này không thể hoàn tác.',
  'suppliers.showingSuppliers': 'Hiển thị {from} trong {total} NCC',
  'suppliers.pageOfSuppliers': 'Trang {page} / {pages}',
  'suppliers.imports': '{name} - Nhập hàng',
  'suppliers.noImports': 'Chưa có nhập hàng',
  'suppliers.noImportsMatch': 'Không có nhập hàng phù hợp',
  'suppliers.showingImports': 'Hiển thị {from} trong {total} nhập hàng',
  'suppliers.viewImports': 'Xem nhập hàng',
  'suppliers.editSupplierAction': 'Sửa NCC',
  'suppliers.deleteSupplierAction': 'Xóa NCC',
  'suppliers.toggleFilters': 'Bộ lọc',
  'suppliers.printImports': 'In nhập hàng',

  // Suppliers — Print
  'suppliers.print.importsTitle': 'Nhập hàng từ NCC - {name}',
  'suppliers.print.totalImports': 'Tổng nhập hàng',
  'suppliers.print.totalAmount': 'Tổng tiền',
  'suppliers.print.avgImport': 'TB mỗi lần nhập',
  'suppliers.print.generatedOn': 'Tạo ngày {date}',
  'suppliers.print.importTotal': 'Tổng nhập:',
  'suppliers.print.storeManager': 'Store Manager',
  'suppliers.print.supplierFallback': 'Nhà cung cấp',
  'suppliers.card.import': 'Nhập hàng',
  'suppliers.card.status': 'Trạng thái:',
  'suppliers.card.total': 'Tổng:',

  // ============================================
  // Floating Action Button
  // ============================================
  'fab.createOrder': 'Tạo đơn hàng',
  'fab.addItem': 'Thêm sản phẩm',
  'fab.addClient': 'Thêm khách hàng',
  'fab.addSupplier': 'Thêm nhà cung cấp',

  // ============================================
  // Settings
  // ============================================
  'settings.title': 'Cài đặt doanh nghiệp',
  'settings.subtitle':
    'Quản lý thông tin doanh nghiệp, cài đặt khu vực và kho hàng',
  'settings.businessInfo': 'Thông tin doanh nghiệp',
  'settings.businessInfoDesc': 'Thông tin chung về doanh nghiệp',
  'settings.regionalSettings': 'Cài đặt khu vực',
  'settings.regionalSettingsDesc': 'Tiền tệ và múi giờ cho doanh nghiệp',
  'settings.storehouses': 'Kho hàng',
  'settings.storehousesDesc': 'Quản lý địa điểm kho hàng',
  'settings.newStorehouse': 'Kho mới',
  'settings.businessName': 'Tên doanh nghiệp',
  'settings.currency': 'Tiền tệ',
  'settings.timezone': 'Múi giờ',
  'settings.storehouseName': 'Tên *',
  'settings.storehouseAddress': 'Địa chỉ *',
  'settings.storehousePhone': 'Điện thoại',
  'settings.storehouseEmail': 'Email',
  'settings.currencyHelp': 'Tất cả giá sẽ hiển thị theo loại tiền này.',
  'settings.timezoneHelp': 'Tất cả thời gian sẽ hiển thị theo múi giờ này.',
  'settings.saveChanges': 'Lưu thay đổi',
  'settings.addStorehouse': '+ Thêm kho hàng',
  'settings.creating': 'Đang tạo...',
  'settings.locked': 'Đã khóa',
  'settings.noBusinessFound':
    'Không tìm thấy doanh nghiệp. Vui lòng liên hệ quản trị viên.',
  'settings.noStorehouses': 'Chưa có kho hàng. Thêm kho mới để bắt đầu.',
  'settings.deleteStorehouse': 'Xóa kho hàng?',
  'settings.deleteStorehouseConfirm':
    'Bạn có chắc muốn xóa "{name}"? Sản phẩm trong kho này có thể bị ảnh hưởng.',
  'settings.businessSaved': 'Đã lưu cài đặt doanh nghiệp',
  'settings.storehouseCreated': 'Đã tạo kho hàng',
  'settings.storehouseUpdated': 'Đã cập nhật kho hàng',
  'settings.storehouseDeleted': 'Đã xóa "{name}"',

  // Settings — Placeholders
  'settings.businessNamePlaceholder': 'Tên doanh nghiệp',
  'settings.businessEmailPlaceholder': 'doanhnghiep@example.com',
  'settings.businessPhonePlaceholder': '+84 912 345 678',
  'settings.businessAddressPlaceholder': '123 Nguyễn Huệ, TP.HCM',
  'settings.storehouseNamePlaceholder': 'Kho chính',
  'settings.storehouseAddressPlaceholder': '123 Đường Kho',
  'settings.storehousePhonePlaceholder': '+84 912 345 678',
  'settings.storehouseEmailPlaceholder': 'kho@example.com',

  // Settings — Danger Zone
  'settings.dangerZone': 'Vùng nguy hiểm',
  'settings.dangerZoneDesc':
    'Các thao tác vĩnh viễn và không thể hoàn tác. Hãy thực hiện hết sức cẩn trọng.',
  'settings.deleteBusiness': 'Xóa doanh nghiệp',
  'settings.deleteBusinessDesc':
    'Xóa vĩnh viễn doanh nghiệp này và toàn bộ dữ liệu — kho hàng, giao dịch, nhân viên và tất cả các bản ghi liên quan. Không thể hoàn tác.',
  'settings.deleteBusinessButton': 'Xóa doanh nghiệp',
  'settings.deleteBusinessConfirmTitle': 'Xóa doanh nghiệp?',
  'settings.deleteBusinessConfirmText':
    'Thao tác này sẽ xóa vĩnh viễn doanh nghiệp và toàn bộ dữ liệu liên quan bao gồm nhân viên, kho hàng, giao dịch và báo cáo. Tất cả thành viên sẽ mất quyền truy cập ngay lập tức. Không thể hoàn tác.',
  'settings.deleteBusinessDeleting': 'Đang xóa...',
  'settings.deleteBusinessDeleted': 'Đã xóa doanh nghiệp thành công',

  // ============================================
  // Team Management
  // ============================================
  'team.title': 'Quản lý nhóm',
  'team.subtitle': 'Quản lý thành viên, tạo vai trò tùy chỉnh và phân quyền',
  'team.members': 'Thành viên',
  'team.roles': 'Vai trò',
  'team.access': 'Phân quyền',
  'team.customRolesGate':
    'Vai trò tùy chỉnh yêu cầu gói Pro hoặc Enterprise. Nâng cấp để tạo bộ quyền riêng cho nhóm.',
  'team.accessGate':
    'Phân quyền vai trò & kho hàng yêu cầu gói Pro hoặc Enterprise.',

  // Team — Members
  'team.membersTitle': 'Thành viên',
  'team.membersDesc': 'Quản lý thành viên và trạng thái tài khoản',
  'team.inviteMember': 'Mời thành viên',
  'team.inviteTeamMember': 'Mời thành viên',
  'team.email': 'Email',
  'team.emailPlaceholder': 'dongnghiep@example.com',
  'team.check': 'Kiểm tra',
  'team.checking': 'Đang kiểm tra...',
  'team.emailAvailable': 'Email khả dụng',
  'team.alreadyRegistered': 'Đã đăng ký là người dùng',
  'team.alreadyInvited': 'Đã có lời mời chờ xác nhận',
  'team.couldNotVerify': 'Không thể xác minh email',
  'team.makeAdmin': 'Cấp quyền quản trị',
  'team.adminCanManage':
    'Quản trị viên có thể quản lý thành viên, vai trò và cài đặt',
  'team.storehouseAccessLabel': 'Quyền truy cập kho',
  'team.loadingStorehouses': 'Đang tải kho hàng...',
  'team.noStorehousesFound': 'Không tìm thấy kho hàng',
  'team.assignRoles': 'Gán vai trò',
  'team.loadingRoles': 'Đang tải vai trò...',
  'team.noCustomRolesYet': 'Chưa có vai trò tùy chỉnh. Bạn có thể gán sau.',
  'team.sendInvitation': 'Gửi lời mời',
  'team.sending': 'Đang gửi...',
  'team.invitationSentTo': 'Đã gửi lời mời đến {email}.',
  'team.invitationSent': 'Đã gửi lời mời',
  'team.invitationFailed': 'Gửi lời mời thất bại',
  'team.invitationFailedMsg': 'Không thể gửi lời mời. Vui lòng thử lại.',
  'team.searchMembers': 'Tìm theo tên hoặc email...',
  'team.allStatus': 'Tất cả trạng thái',
  'team.deactivated': 'Đã vô hiệu hóa',
  'team.invited': 'Đã mời',
  'team.allRoles': 'Tất cả vai trò',
  'team.noMembers': 'Không có thành viên.',
  'team.noMembersMatch': 'Không có thành viên phù hợp.',
  'team.admin': 'Quản trị',
  'team.owner': 'Chủ sở hữu',
  'team.you': '(bạn)',
  'team.reactivate': 'Kích hoạt lại',
  'team.deactivate': 'Vô hiệu hóa',
  'team.expires': 'Hết hạn {date}',
  'team.resend': 'Gửi lại',
  'team.resending': 'Đang gửi...',
  'team.deactivateMember': 'Vô hiệu hóa thành viên',
  'team.reactivateMember': 'Kích hoạt lại thành viên',
  'team.deactivateConfirm':
    'Bạn có chắc muốn vô hiệu hóa {name}? Họ sẽ bị đăng xuất ngay và không thể truy cập cho đến khi được kích hoạt lại.',
  'team.reactivateConfirm': 'Kích hoạt lại {name}? Họ sẽ có thể đăng nhập lại.',
  'team.userDeactivated': '{name} đã bị vô hiệu hóa.',
  'team.userDeactivatedTitle': 'Đã vô hiệu hóa',
  'team.userReactivated': '{name} đã được kích hoạt lại.',
  'team.userReactivatedTitle': 'Đã kích hoạt lại',
  'team.invitationResent': 'Đã gửi lại lời mời đến {email}',
  'team.invitationResentTitle': 'Đã gửi lại lời mời',
  'team.accessUpdated': 'Đã cập nhật phân quyền',
  'team.promoted': '{name} đã được thăng lên quản trị',
  'team.demoted': '{name} đã bị hạ xuống thành viên',

  // Team — Access
  'team.accessControl': 'Phân quyền',
  'team.accessDesc': 'Gán vai trò, kho hàng và thăng/hạ chức thành viên',
  'team.searchMembersAccess': 'Tìm thành viên...',
  'team.noUsers': 'Không có người dùng.',
  'team.noUsersMatch': 'Không có thành viên phù hợp.',
  'team.fullAccess': 'Toàn quyền truy cập (quyền quản trị)',
  'team.rolesLabel': 'Vai trò: ',
  'team.none': 'Không có',
  'team.storehousesLabel': 'Kho hàng: ',
  'team.noneAssigned': 'Chưa gán',
  'team.demoteToUser': 'Hạ xuống thành viên',
  'team.promoteToAdmin': 'Thăng lên quản trị',
  'team.demoteBtn': 'Hạ chức',
  'team.promoteBtn': 'Thăng chức',
  'team.editAccess': 'Sửa phân quyền',
  'team.assignedRoles': 'Vai trò đã gán',
  'team.noRolesDefined': 'Chưa có vai trò nào',
  'team.storehouseAccess': 'Quyền truy cập kho',
  'team.leaveEmptyDeny': 'Để trống để từ chối mọi quyền truy cập kho.',
  'team.noStorehouses': 'Không tìm thấy kho hàng',
  'team.saveChanges': 'Lưu thay đổi',

  // ============================================
  // Roles (RoleManager)
  // ============================================
  'role.customRoles': 'Vai trò tùy chỉnh',
  'role.customRolesDesc': 'Tạo bộ quyền để gán cho người dùng',
  'role.newRole': '+ Vai trò mới',
  'role.roleName': 'Tên vai trò *',
  'role.roleNamePlaceholder': 'VD: Nhân viên kho',
  'role.roleDescription': 'Mô tả',
  'role.roleDescriptionPlaceholder': 'Vai trò này dùng để làm gì',
  'role.permissionsLabel': 'Phân quyền',
  'role.readAccessNote':
    'Tất cả người dùng đều có quyền đọc. Bật/tắt quyền ghi bên dưới.',
  'role.toggleAll': 'Bật/tắt tất cả',
  'role.saving': 'Đang lưu...',
  'role.createRole': 'Tạo vai trò',
  'role.saveChanges': 'Lưu thay đổi',
  'role.noRolesYet':
    'Chưa có vai trò. Hãy tạo một vai trò để gán quyền cho người dùng.',
  'role.permissionCount': '{count} quyền',
  'role.deleteRoleTitle': 'Xóa vai trò?',
  'role.deleteRoleConfirm':
    'Bạn có chắc muốn xóa "{name}"? Người dùng được gán vai trò này sẽ mất các quyền đó.',
  'role.roleCreated': 'Đã tạo vai trò',
  'role.roleUpdated': 'Đã cập nhật vai trò',
  'role.roleDeleted': 'Đã xóa "{name}"',
  'role.resource_items': 'Hàng hóa',
  'role.resource_imports': 'Nhập hàng',
  'role.resource_transactions': 'Đơn hàng',
  'role.resource_transfers': 'Chuyển kho',
  'role.resource_partners': 'Đối tác',
  'role.resource_storehouses': 'Kho hàng',
  'role.resource_businessSettings': 'Cài đặt doanh nghiệp',
  'role.resource_users': 'Người dùng',
  'role.action_create': 'Tạo',
  'role.action_update': 'Sửa',
  'role.action_delete': 'Xóa',
  'role.action_execute': 'Thực thi',
  'role.actionDesc_create': 'Tạo bản ghi mới',
  'role.actionDesc_update': 'Sửa bản ghi hiện có',
  'role.actionDesc_delete': 'Xóa bản ghi',
  'role.actionDesc_execute':
    'Thực hiện thay đổi trạng thái (duyệt, hoàn thành, hủy, gán)',

  // ============================================
  // Billing
  // ============================================
  'billing.title': 'Thanh toán & Gói dịch vụ',
  'billing.subtitle':
    'Quản lý gói dịch vụ, xem mức sử dụng và mở khóa thêm tính năng.',

  // Billing — Dimension & Feature Labels
  'billing.storehouses': 'Kho hàng',
  'billing.teamMembers': 'Thành viên',
  'billing.itemsLabel': 'Sản phẩm',
  'billing.monthlyTransactions': 'Giao dịch hàng tháng',
  'billing.featureTransfers': 'Chuyển hàng giữa các kho',
  'billing.featureCustomRoles': 'Vai trò & Quyền tùy chỉnh',
  'billing.featureBasicAnalytics': 'Phân tích doanh thu cơ bản',
  'billing.featureAnalyticsReports': 'Phân tích & Báo cáo',
  'billing.featureAdvancedReports': 'Trí tuệ & Phân tích nâng cao',
  'billing.featureBarcodePrint': 'In nhãn mã vạch & QR',

  // Billing — Trial
  'billing.tryProFree': 'Dùng thử Pro miễn phí!',
  'billing.trialDescription':
    ' Bắt đầu dùng thử Pro 30 ngày — không cần thẻ tín dụng.',
  'billing.starting': 'Đang bắt đầu…',
  'billing.startFreeTrial': 'Dùng thử miễn phí',
  'billing.confirmTrialTitle': 'Bắt đầu dùng thử Pro?',
  'billing.confirmTrialBody':
    'Bạn sẽ được dùng thử Pro 30 ngày cho doanh nghiệp. Không cần thẻ tín dụng. Bạn chỉ có thể dùng thử miễn phí một lần.',
  'billing.proTrialActive': 'Đang dùng thử Pro',
  'billing.trialCountdown':
    ' — còn {days} ngày (kết thúc {date}). Nâng cấp để giữ tính năng Pro.',
  'billing.upgradeNow': 'Nâng cấp ngay',
  'billing.trialStarted': 'Đã bắt đầu dùng thử Pro 30 ngày!',

  // Billing — Past Due
  'billing.paymentFailed': 'Thanh toán thất bại',
  'billing.paymentFailedDesc':
    ' — Thanh toán gần nhất không thành công. Vui lòng cập nhật phương thức thanh toán.',
  'billing.opening': 'Đang mở…',
  'billing.fixPayment': 'Sửa thanh toán',

  // Billing — Pending Downgrade
  'billing.switchingTo': 'Chuyển sang {plan} khi gia hạn',
  'billing.switchingToDesc':
    ' — có hiệu lực vào {date}. Gói hiện tại vẫn hoạt động đến lúc đó.',
  'billing.cancelling': 'Đang hủy…',
  'billing.keepCurrentPlan': 'Giữ gói hiện tại',

  // Billing — Manual Payment Request
  'billing.requestPending': 'Yêu cầu thanh toán đang chờ',
  'billing.requestPendingDesc':
    'Yêu cầu nâng cấp lên {plan} ({cycle}) đang chờ xác nhận. Bạn sẽ được thông báo khi được duyệt.',
  'billing.requestSubmitted': 'Đã gửi yêu cầu thanh toán. Đang chờ xác nhận.',
  'billing.cancelRequest': 'Huỷ yêu cầu',
  'billing.requestCancelled': 'Đã huỷ yêu cầu thanh toán.',
  'billing.requestAlreadyPending':
    'Đang có yêu cầu thanh toán chờ duyệt. Hãy huỷ yêu cầu đó trước khi chọn gói khác.',
  'billing.manualDowngradeBlocked':
    'Không thể hạ cấp trong kỳ thanh toán hiện tại. Gói của bạn hoạt động đến {date}. Sau khi hết hạn, bạn có thể đăng ký gói khác.',

  // Billing — Add-On Request
  'billing.requestAddOn': 'Yêu cầu bổ sung',
  'billing.requestingAddOn': 'Đang gửi…',
  'billing.addonRequestPending': 'Yêu cầu bổ sung đang chờ',
  'billing.addonRequestPendingDesc':
    'Yêu cầu bổ sung đang được xem xét. Chúng tôi sẽ liên hệ sớm.',
  'billing.addonSubmitted': 'Đã gửi yêu cầu bổ sung. Đội ngũ sẽ xem xét sớm.',
  'billing.customLimitsAvailable': 'Giới hạn tùy chỉnh',
  'billing.addonPlaceholder':
    'Cho chúng tôi biết nhu cầu của bạn — quy mô sử dụng, số lượng nhân viên, v.v.',
  'billing.requestedLimits': 'Giới hạn yêu cầu',
  'billing.addonMessage': 'Tin nhắn (tùy chọn)',
  'billing.addonBasePrice': 'Gói cơ bản',
  'billing.addonAddOn': 'Bổ sung',
  'billing.addonBaseIncluded': '{count} bao gồm',
  'billing.addonExtraUnits': '+{extra} thêm',
  'billing.addonMin': 'Tối thiểu {value}',
  'billing.addonPerUnit': '{price} mỗi {step}',
  'billing.addonEstimatedMonthly': 'Ước tính hàng tháng',
  'billing.addonEstimatedAnnual': 'Ước tính hàng năm (giảm 20%)',
  'billing.addonYourLimits': 'Tùy chỉnh giới hạn',
  'billing.addonPriceSummary': 'Ước tính giá',
  'billing.addonNoAddOns': 'Giới hạn gói cơ bản — không bổ sung',

  // Billing — Add-Ons Section
  'billing.addOnsTitle': 'Tiện ích bổ sung',
  'billing.addOnsDesc':
    'Mở rộng giới hạn vượt gói cơ bản với dung lượng bổ sung.',
  'billing.requestAddOns': 'Yêu cầu bổ sung',

  // Billing — Current Plan
  'billing.currentPlan': 'Gói hiện tại',
  'billing.onPlan': 'Bạn đang dùng gói {plan}',
  'billing.status_active': 'Hoạt động',
  'billing.status_trialing': 'Dùng thử',
  'billing.status_past_due': 'Quá hạn',
  'billing.status_canceled': 'Đã hủy',
  'billing.status_expired': 'Hết hạn',
  'billing.annual': 'Năm',
  'billing.monthly': 'Tháng',
  'billing.unableToLoad':
    'Không thể tải dữ liệu gói dịch vụ. Vui lòng thử lại.',
  'billing.nextRenewal': 'Gia hạn tiếp: ',
  'billing.cycle': 'Chu kỳ: ',
  'billing.cancelsOn': 'Hủy vào: ',
  'billing.reactivating': 'Đang kích hoạt lại…',
  'billing.undoCancel': 'Hoàn tác hủy',
  'billing.card': 'Thẻ: ',
  'billing.nextInvoice': 'Hóa đơn tiếp: ',
  'billing.featureAccess': 'Tính năng',

  // Billing — Plan Comparison
  'billing.availablePlans': 'Các gói dịch vụ',
  'billing.save20': 'Tiết kiệm 20%',
  'billing.comingSoon': 'Sắp ra mắt',
  'billing.limits': 'Giới hạn',
  'billing.features': 'Tính năng',
  'billing.currentPlanBtn': 'Gói hiện tại',
  'billing.changeScheduled': 'Đã lên lịch',
  'billing.pendingChangeLock': 'Đang có thay đổi chờ xử lý',
  'billing.changing': 'Đang thay đổi...',
  'billing.switchTo': 'Chuyển sang {plan}',
  'billing.adminRequired': 'Cần quyền quản trị',
  'billing.cancelToDowngrade': 'Dùng "Hủy gói" để hạ cấp',
  'billing.cannotSwitchAnnualMonthly':
    'Không thể chuyển từ gói năm sang gói tháng',
  'billing.cannotUpgradeAnnualToMonthly':
    'Chuyển sang gói tháng trước khi nâng cấp',
  'billing.cannotCrossTypeTransfer': 'Không hỗ trợ chuyển đổi giữa hai gói này',

  // Billing — Management
  'billing.billingManagement': 'Quản lý thanh toán',
  'billing.billingManagementDesc':
    'Quản lý phương thức thanh toán, tải hóa đơn, hoặc hủy gói.',
  'billing.manageBilling': 'Quản lý thanh toán',
  'billing.cancelPlan': 'Hủy gói',

  // Billing — Invoice History
  'billing.invoiceHistory': 'Lịch sử hóa đơn',
  'billing.amount': 'Số tiền',
  'billing.invoice': 'Hóa đơn',
  'billing.view': 'Xem',

  // Billing — Subscription History
  'billing.subscriptionHistory': 'Lịch sử đăng ký',
  'billing.event.plan_activated': 'Kích hoạt gói',
  'billing.event.addon_activated': 'Kích hoạt tiện ích',
  'billing.event.plan_subscribed': 'Đăng ký gói',
  'billing.event.plan_upgraded': 'Nâng cấp gói',
  'billing.event.plan_renewed': 'Gia hạn gói',
  'billing.event.trial_started': 'Bắt đầu dùng thử',
  'billing.event.trial_expired': 'Hết hạn dùng thử',
  'billing.event.plan_canceled': 'Hủy gói',
  'billing.event.plan_reactivated': 'Kích hoạt lại gói',
  'billing.event.downgrade_scheduled': 'Lên lịch hạ cấp',
  'billing.event.downgrade_executed': 'Hạ cấp thực hiện',
  'billing.event.downgrade_canceled': 'Hủy hạ cấp',
  'billing.eventPlan': 'Gói',
  'billing.eventPreviousPlan': 'Trước',
  'billing.eventCycle': 'Chu kỳ',
  'billing.noSubscriptionHistory': 'Chưa có lịch sử đăng ký.',

  // Billing — Locked Resources
  'billing.manageLockedResources': 'Quản lý tài nguyên bị khóa',
  'billing.manageLockedResourcesDesc':
    'Mở khóa tài nguyên sau khi nâng cấp, hoặc hoán đổi tài nguyên bị khóa.',
  'billing.unlockResources': 'Mở khóa',
  'billing.swap': 'Hoán đổi',
  'billing.unlockDesc':
    'Mở khóa: Sau khi nâng cấp, mở khóa kho hàng hoặc kích hoạt lại người dùng (trong giới hạn gói mới).',
  'billing.swapDesc':
    'Hoán đổi: Đổi tài nguyên bị khóa — 1 đổi 1. Giới hạn 2 lần hoán đổi mỗi ngày.',

  // Billing — Confirm Dialogs
  'billing.upgradeToTitle': 'Nâng cấp lên {target}',
  'billing.confirmUpgrade': 'Xác nhận nâng cấp',
  'billing.upgradeDescription':
    'Gói của bạn sẽ được nâng cấp ngay lên {target} với giá {price} ({cycle}).',
  'billing.upgradeProration':
    'Phương thức thanh toán sẽ bị tính phí chênh lệch cho thời gian còn lại của chu kỳ.',
  'billing.upgradeNextCycle':
    'Từ chu kỳ tiếp theo, bạn sẽ bị tính phí đầy đủ theo gói {target}.',
  'billing.switchToTitle': 'Chuyển sang {tier}',
  'billing.scheduleChange': 'Lên lịch thay đổi',
  'billing.switchDescription':
    'Gói hiện tại vẫn hoạt động đến hết chu kỳ. Thay đổi có hiệu lực khi gia hạn.',
  'billing.switchNoCharge':
    'Không tính phí ngay — bạn vẫn được sử dụng tính năng gói hiện tại cho đến lúc đó.',
  'billing.cancelScheduledTitle': 'Hủy lịch thay đổi gói',
  'billing.cancelScheduledConfirm':
    'Hủy thay đổi gói đã lên lịch? Gói hiện tại sẽ tiếp tục khi gia hạn.',
  'billing.cancelSubscriptionTitle': 'Hủy gói dịch vụ',
  'billing.cancelSubscriptionConfirm': 'Bạn có chắc muốn hủy gói dịch vụ?',
  'billing.cancelSubscriptionDesc':
    'Gói vẫn hoạt động đến hết chu kỳ. Sau đó, tài khoản sẽ chuyển về gói Miễn phí.',

  // Billing — Notifications
  'billing.subscriptionUpdated': 'Đã cập nhật gói! Gói mới sẽ hoạt động ngay.',
  'billing.alreadyProcessed':
    'Thanh toán đã được xử lý. Gói sẽ tự động kích hoạt — vui lòng đợi và tải lại trang.',
  'billing.planChangeScheduled':
    'Đã lên lịch chuyển sang gói {tier} ({cycle}). Có hiệu lực khi gia hạn.',
  'billing.scheduleCancelled':
    'Đã hủy lịch thay đổi gói. Gói hiện tại sẽ tiếp tục.',
  'billing.subscriptionCancelled': 'Gói sẽ bị hủy vào cuối chu kỳ thanh toán.',
  'billing.planChanged': 'Đã chuyển sang gói {target}',
  'billing.resourcesUnlocked': 'Đã mở khóa tài nguyên thành công',
  'billing.resourcesSwapped': 'Đã hoán đổi tài nguyên thành công',
  'billing.reactivated': 'Đã kích hoạt lại gói! Gói sẽ tiếp tục khi gia hạn.',
  'billing.changeAlreadyScheduled':
    'Đã có lịch thay đổi gói khi gia hạn. Hãy hủy lịch đó trước.',
  'billing.cannotSwitchMsg':
    'Không thể chuyển từ gói năm sang gói tháng. Gói năm sẽ tự động gia hạn cuối chu kỳ.',

  // ============================================
  // Payment Modal
  // ============================================
  'payment.subscribeTo': 'Đăng ký {plan}',
  'payment.billingCycle': 'Thanh toán {cycle} — {price}',
  'payment.orderSummary': 'Tóm tắt đơn hàng',
  'payment.plan': 'Gói',
  'payment.billingPeriod': 'Chu kỳ thanh toán',
  'payment.totalDue': 'Tổng cần thanh toán',
  'payment.confirmAndPay': 'Xác nhận & Thanh toán',
  'payment.proceedNote':
    'Bạn sẽ được chuyển hướng để hoàn tất thanh toán một cách bảo mật.',
  'payment.proceedNoteManual':
    'Gửi yêu cầu và quản trị viên sẽ liên hệ để sắp xếp thanh toán.',
  'payment.submitRequest': 'Gửi yêu cầu',
  'payment.manualNote':
    'Yêu cầu của bạn sẽ được đội ngũ xét duyệt. Đại diện tài khoản sẽ liên hệ để sắp xếp thanh toán.',
  'payment.submitting': 'Đang gửi yêu cầu…',
  'payment.initFailed':
    'Nhà cung cấp thanh toán không thể khởi tạo. Vui lòng kiểm tra cấu hình.',
  'payment.formLoadFailed':
    'Không thể tải biểu mẫu thanh toán. Vui lòng thử lại.',
  'payment.alreadyProcessed':
    'Thanh toán đã được xử lý! Gói sẽ tự động kích hoạt. Vui lòng đợi và tải lại trang.',
  'payment.formNotReady': 'Biểu mẫu thanh toán chưa sẵn sàng. Vui lòng đợi.',
  'payment.paymentFailed': 'Thanh toán thất bại. Vui lòng thử lại.',
  'payment.unexpectedError': 'Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.',
  'payment.payAmount': 'Thanh toán {price}',
  'payment.securePayments': 'Thanh toán được bảo mật bởi ',
  'payment.provider': 'Xendit',
  'payment.noTouchServers':
    '. Thông tin thẻ không bao giờ đi qua máy chủ của chúng tôi.',
  'payment.redirecting': 'Đang chuyển hướng đến trang thanh toán…',

  // ============================================
  // Resolve Modal
  // ============================================
  'resolve.title': 'Mở khóa tài nguyên',
  'resolve.description':
    'Chọn kho hàng bị khóa để mở và người dùng bị vô hiệu hóa để kích hoạt lại. Giới hạn gói sẽ được áp dụng.',
  'resolve.loadFailed': 'Không thể tải tài nguyên. Vui lòng thử lại.',
  'resolve.emptyState': 'Không có kho bị khóa hoặc người dùng bị vô hiệu hóa.',
  'resolve.lockedStorehouses': 'Kho hàng bị khóa',
  'resolve.nSelected': '{n} đã chọn',
  'resolve.unlockDesc':
    'Kho được mở khóa sẽ hoạt động trở lại. Bạn chỉ có thể mở khóa trong giới hạn gói.',
  'resolve.locked': 'Đã khóa',
  'resolve.deactivatedUsers': 'Người dùng bị vô hiệu hóa',
  'resolve.reactivateDesc':
    'Người dùng được kích hoạt lại sẽ có thể đăng nhập. Bạn chỉ có thể kích hoạt trong giới hạn gói.',
  'resolve.deactivatedBadge': 'Đã vô hiệu hóa',
  'resolve.unlocking': 'Đang mở khóa...',
  'resolve.unlockSelected': 'Mở khóa đã chọn',

  // ============================================
  // Compliance Gate
  // ============================================
  'compliance.loading': 'Đang tải…',
  'compliance.planLimitsExceeded': 'Vượt quá giới hạn gói',
  'compliance.limitsExceededDesc': 'Gói {tier} của bạn đã vượt quá giới hạn',
  'compliance.over': '(vượt {excess})',
  'compliance.itemsTransactionsHint':
    'Sản phẩm và giao dịch không thể bị khóa. Vui lòng nâng cấp gói hoặc xóa bớt thủ công.',
  'compliance.upgradePlan': 'Nâng cấp gói',
  'compliance.loadFailed': 'Không thể tải yêu cầu. Vui lòng tải lại trang.',
  'compliance.selectToComply':
    'Chọn tài nguyên để khóa hoặc vô hiệu hóa để tuân thủ gói, hoặc nâng cấp.',
  'compliance.selectStorehousesToLock': 'Chọn kho hàng để khóa',
  'compliance.nOfTotalSelected': '{n} / {total} đã chọn',
  'compliance.lockedReadOnly':
    'Kho bị khóa sẽ chỉ đọc. Không thể tạo hoặc sửa sản phẩm, giao dịch, chuyển kho trong kho đó.',
  'compliance.selectUsersToDeactivate': 'Chọn người dùng để vô hiệu hóa',
  'compliance.deactivateDesc':
    'Người dùng bị vô hiệu hóa sẽ bị đăng xuất và không thể đăng nhập lại.',
  'compliance.enforceFailed': 'Không thể áp dụng giới hạn. Vui lòng thử lại.',
  'compliance.backToStorehouses': '← Quay lại kho hàng',
  'compliance.applying': 'Đang áp dụng...',
  'compliance.nextStep': 'Tiếp theo →',
  'compliance.confirmContinue': 'Xác nhận & Tiếp tục',
  'compliance.nonAdminMessage': 'Doanh nghiệp đã vượt quá giới hạn gói.',
  'compliance.contactAdmin': 'Vui lòng liên hệ quản trị viên để giải quyết.',
  'compliance.created': 'Tạo lúc ',
  'compliance.dimStorehouses': 'Kho hàng',
  'compliance.dimUsers': 'Người dùng',
  'compliance.dimItems': 'Sản phẩm',
  'compliance.dimMonthlyTransactions': 'Giao dịch hàng tháng',

  // Compliance — Tier Labels
  'tier.free': 'Miễn phí',
  'tier.pro': 'Pro',
  'tier.enterprise': 'Enterprise',
  'tier.freeDesc': 'Dành cho doanh nghiệp nhỏ mới bắt đầu',
  'tier.proDesc': 'Dành cho doanh nghiệp đang phát triển với nhiều chi nhánh',
  'tier.enterpriseDesc':
    'Dành cho tổ chức lớn — giới hạn tùy chỉnh theo yêu cầu',
  'tier.pricePerMonth': '{price}/tháng',
  'tier.pricePerYear': '{price}/năm',

  // ============================================
  // Analytics
  // ============================================
  'analytics.title': 'Phân tích',
  'analytics.subtitle': 'Thông tin tài chính và chỉ số hiệu suất doanh nghiệp.',
  'analytics.featureGate':
    'Phân tích & Báo cáo có trong gói Pro trở lên. Nâng cấp để xem doanh thu, báo cáo tồn kho và nhiều hơn.',
  'analytics.proGate':
    'Nâng cấp lên Pro để xem xu hướng, sản phẩm bán chạy, phân tích đối tác, báo cáo tồn kho, v.v.',
  'analytics.intelligenceGate':
    'Trí tuệ nâng cao có trong gói Enterprise. Nâng cấp để xem điểm rủi ro, phân tích biên lợi và gợi ý hành động.',
  'analytics.allStorehouses': 'Tất cả kho hàng',

  // Analytics — Period options
  'analytics.period7d': '7 ngày qua',
  'analytics.period30d': '30 ngày qua',
  'analytics.period90d': '90 ngày qua',
  'analytics.periodYtd': 'Từ đầu năm',
  'analytics.period12m': '12 tháng qua',
  'analytics.periodAll': 'Tất cả',

  // Analytics — Type options
  'analytics.mostSold': 'Bán nhiều nhất',
  'analytics.mostPurchased': 'Mua nhiều nhất',
  'analytics.mostProfitable': 'Lợi nhuận cao nhất',
  'analytics.topClients': 'Khách hàng hàng đầu',
  'analytics.topSuppliers': 'Nhà cung cấp hàng đầu',

  // Analytics — Column / label i18n
  'analytics.itemCol': 'Sản phẩm',
  'analytics.itemsCol': 'Sản phẩm',
  'analytics.from': 'Từ',
  'analytics.to': 'Đến',
  'analytics.ordersLabel': 'Đơn hàng:',
  'analytics.importsLabel': 'Nhập hàng:',
  'analytics.txCount': 'Đơn hàng',
  'analytics.impCount': 'Nhập hàng',
  'analytics.liveDataNotice': 'Dữ liệu trực tiếp — không lọc theo kỳ',
  'analytics.gainTrend': 'Xu hướng biên LN',
  'analytics.topTransferredItems': 'SP chuyển kho nhiều nhất',
  'analytics.transferCount': 'Lần chuyển',

  // Analytics — Profit & Loss
  'analytics.profitLoss': 'Lãi & Lỗ',
  'analytics.revenue': 'Doanh thu',
  'analytics.cost': 'Chi phí',
  'analytics.grossProfit': 'Lợi nhuận gộp',
  'analytics.activity': 'Hoạt động',
  'analytics.nOrders': '{n} đơn hàng',
  'analytics.nImports': '{n} nhập hàng',
  'analytics.vsPrevPeriod': 'so với kỳ trước',
  'analytics.margin': '% biên lợi nhuận',

  // Analytics — Trends
  'analytics.revenueCostTrend': 'Xu hướng Doanh thu & Chi phí',
  'analytics.profit': 'Lợi nhuận',
  'analytics.visual': 'Biểu đồ',
  'analytics.noDataPeriod': 'Không có dữ liệu cho khoảng thời gian này.',

  // Analytics — Top Items
  'analytics.topItems': 'Sản phẩm hàng đầu',
  'analytics.marginCol': 'Biên LN',
  'analytics.qty': 'SL',
  'analytics.value': 'Giá trị',
  'analytics.avgPrice': 'Giá TB',
  'analytics.share': 'Tỷ trọng',
  'analytics.noItemsPeriod': 'Không có sản phẩm trong khoảng thời gian này.',

  // Analytics — Partners
  'analytics.partnerAnalytics': 'Phân tích đối tác',
  'analytics.totalValue': 'Tổng giá trị',
  'analytics.ordersCol': 'Đơn hàng',
  'analytics.avgOrder': 'Đơn TB',
  'analytics.lastActivity': 'Hoạt động gần nhất',
  'analytics.walkIn': '(Vãng lai)',
  'analytics.noPartnerData': 'Không có dữ liệu đối tác.',

  // Analytics — Inventory Snapshot
  'analytics.inventorySnapshot': 'Tổng quan tồn kho',
  'analytics.totalValueStat': 'Tổng giá trị',
  'analytics.totalItemsStat': 'Tổng sản phẩm',
  'analytics.lowStockStat': 'Sắp hết hàng',
  'analytics.deadStock': 'Hàng tồn đọng',
  'analytics.turnoverRate': 'Tốc độ quay vòng',
  'analytics.itemsBelowThreshold': 'Sản phẩm dưới ngưỡng',
  'analytics.noSalesInPeriod': 'Không có bán hàng trong kỳ',
  'analytics.turnoverDesc': 'Đã bán / tồn kho hiện tại',
  'analytics.byStorehouse': 'Theo kho hàng',
  'analytics.inventory': 'Tồn kho',
  'analytics.lockedBadge': '(Đã khóa)',

  // Analytics — Payment & Delivery Insights
  'analytics.paymentDeliveryInsights': 'Thông tin thanh toán & giao hàng',
  'analytics.outstandingPayments': 'Thanh toán chưa hoàn tất',
  'analytics.customerReceivables': 'Công nợ khách hàng',
  'analytics.supplierPayables': 'Phải trả NCC',
  'analytics.averageLagDays': 'Độ trễ TB (ngày)',
  'analytics.paymentLag': 'Độ trễ thanh toán',
  'analytics.deliveryLag': 'Độ trễ giao hàng',

  // Analytics — Margin Analysis
  'analytics.marginAnalysis': 'Phân tích biên lợi nhuận',
  'analytics.totalGain': 'Tổng lãi (Bán − Mua)',
  'analytics.sumAcrossItems': 'tổng qua tất cả sản phẩm đã bán',
  'analytics.avgMargin': 'Biên LN TB',
  'analytics.avgMarginDesc': '(bán − giá mua TB) / bán',
  'analytics.itemsTracked': 'SP được theo dõi',
  'analytics.topItemsByMargin': 'SP hàng đầu theo biên LN',
  'analytics.avgBuy': 'Giá mua TB',
  'analytics.avgSell': 'Giá bán TB',
  'analytics.marginPercent': 'Biên LN %',
  'analytics.totalGainCol': 'Tổng lãi',

  // Analytics — Storehouse Comparison
  'analytics.storehouseComparison': 'So sánh kho hàng',
  'analytics.storehouse': 'Kho hàng',
  'analytics.transfersIn': 'Chuyển vào',
  'analytics.transfersOut': 'Chuyển ra',
  'analytics.noStorehouseData': 'Không có dữ liệu kho hàng.',

  // Analytics — Transfer Flow
  'analytics.transferFlow': 'Luồng chuyển kho',
  'analytics.flowBetweenStorehouses': 'Luồng giữa các kho',
  'analytics.count': 'Số lần',
  'analytics.netFlowByStorehouse': 'Luồng ròng theo kho',
  'analytics.in': 'Vào',
  'analytics.out': 'Ra',
  'analytics.net': 'Ròng',
  'analytics.noTransferData': 'Không có dữ liệu chuyển kho trong kỳ này.',

  // Analytics — Inventory Risk / Dead Stock
  'analytics.inventoryRisk': 'Rủi ro tồn kho',
  'analytics.capitalAtRisk': 'Vốn có rủi ro',
  'analytics.capitalAtRiskCol': 'Vốn có rủi ro',
  'analytics.estCarryingCost': 'Chi phí lưu kho ước tính',
  'analytics.forSelectedPeriod': 'Trong kỳ đã chọn',
  'analytics.criticalItems': 'Hàng nghiêm trọng',
  'analytics.highRisk': 'rủi ro cao',
  'analytics.avgRiskScore': 'Điểm rủi ro TB',
  'analytics.needsAttention': 'Cần chú ý',
  'analytics.healthy': 'Tốt',
  'analytics.agingBuckets': 'Phân nhóm theo tuổi',
  'analytics.itemsLabel': 'mặt hàng',
  'analytics.atRisk': 'có rủi ro',
  'analytics.topRiskItems': 'Hàng rủi ro cao nhất',
  'analytics.itemName': 'Mặt hàng',
  'analytics.riskScore': 'Rủi ro',
  'analytics.daysSinceLastSale': 'Ngày từ lần bán cuối',
  'analytics.sellThrough': 'Tỷ lệ bán',
  'analytics.trend': 'Xu hướng',
  'analytics.actions': 'Hành động',
  'analytics.expired': 'HẾT HẠN',
  'analytics.actionTransfer': 'Chuyển kho',
  'analytics.transferDesc': 'Chuyển hàng tồn sang kho khác',
  'analytics.actionDiscount': 'Giảm giá',
  'analytics.actionStopReorder': 'Ngừng đặt hàng',
  'analytics.agingBucketRange': '{min}–{max} ngày',
  'analytics.agingBucket90Plus': '90+ ngày',
  'analytics.reason_noTransfers':
    'Sản phẩm chưa được chuyển đến kho khác — hãy cân nhắc phân phối lại.',
  'analytics.reason_expiringSoon':
    'Sắp hết hạn — hãy cân nhắc giảm giá thanh lý.',
  'analytics.reason_slowMoving':
    'Hàng tồn chậm — hãy cân nhắc khuyến mãi để đẩy nhanh luân chuyển.',
  'analytics.reason_noSales':
    'Không có bán hàng trong kỳ — tạm ngừng đặt hàng cho đến khi nhu cầu phục hồi.',

  // Cài đặt hiển thị
  'analytics.configureViews': 'Tùy chỉnh hiển thị',
  'analytics.viewSettings': 'Cài đặt hiển thị',
  'analytics.section.profitLoss': 'Lãi & Lỗ',
  'analytics.section.trends': 'Xu hướng Doanh thu & Chi phí',
  'analytics.section.topItems': 'Sản phẩm nổi bật',
  'analytics.section.partners': 'Phân tích đối tác',
  'analytics.section.inventory': 'Tổng quan tồn kho',
  'analytics.section.payments': 'Thanh toán & Giao hàng',
  'analytics.section.storehouses': 'So sánh kho',
  'analytics.section.transfers': 'Luồng chuyển kho',
  'analytics.section.deadStock': 'Rủi ro tồn kho / Hàng chết',
  'analytics.section.stockIntelligence': 'Dự báo tồn kho',
  'analytics.section.margins': 'Phân tích biên lợi nhuận',

  // Trang phân tích rủi ro
  'analytics.backToAnalytics': '← Quay lại Phân tích',
  'analytics.riskAnalytics': 'Phân tích rủi ro tồn kho',
  'analytics.riskAnalyticsSubtitle':
    'Đánh giá toàn diện rủi ro tồn kho với bộ lọc và sắp xếp',
  'analytics.allRiskLevels': 'Tất cả mức rủi ro',
  'analytics.riskLevelCritical': 'Nghiêm trọng',
  'analytics.riskLevelHigh': 'Cao',
  'analytics.riskLevelMedium': 'Trung bình',
  'analytics.riskLevelLow': 'Thấp',
  'analytics.searchItems': 'Tìm sản phẩm...',

  // Phần bị khóa
  'analytics.advancedIntelligence': 'Phân tích nâng cao',
  'analytics.unlockIntelligence':
    'Mở khóa phân tích nâng cao với gói Enterprise',

  // Dự báo tồn kho
  'analytics.stockIntelligence': 'Dự báo tồn kho',
  'analytics.si.pageTitle': 'Dự báo tồn kho',
  'analytics.si.pageSubtitle':
    'Dự báo hết hàng với phân tích tác động tài chính',
  'analytics.si.quickRestock': 'Nhập hàng nhanh',
  'analytics.si.quickRestockDesc': 'Tạo đơn nhập hàng cho sản phẩm này',
  'analytics.si.createImport': 'Tạo đơn nhập ({count})',
  'analytics.si.importModalTitle': 'Tạo đơn nhập từ phân tích',
  'analytics.si.importFillQuantities':
    'Vui lòng nhập số lượng cho tất cả sản phẩm',
  'analytics.si.importQtyPlaceholder': 'Nhập SL',
  'analytics.si.importSuccess': 'Tạo thành công đơn nhập với {count} sản phẩm',
  'analytics.si.revenueAtRisk': 'Doanh thu có rủi ro',
  'analytics.si.revenueAtRiskCol': 'Doanh thu rủi ro',
  'analytics.si.criticalItems': 'Nghiêm trọng',
  'analytics.si.urgentItems': 'Hàng cấp bách',
  'analytics.si.urgent': 'cấp bách',
  'analytics.si.avgDaysLeft': 'TB ngày còn lại',
  'analytics.si.avgDaysLeftDesc': 'Thời gian tồn kho trung bình',
  'analytics.si.needReorder': 'Cần đặt hàng',
  'analytics.si.pastReorderPoint': 'Quá điểm đặt hàng',
  'analytics.si.topItems': 'Ưu tiên đặt hàng',
  'analytics.si.burnRate': 'Tốc độ bán',
  'analytics.si.daysLeft': 'Ngày còn lại',
  'analytics.si.stockOutDate': 'Ngày hết hàng',
  'analytics.si.leadTime': 'Thời gian giao',
  'analytics.si.supplier': 'Nhà cung cấp',
  'analytics.si.reorderPoint': 'Điểm đặt hàng',
  'analytics.si.urgencyCol': 'Mức độ',
  'analytics.si.day': 'ngày',
  'analytics.si.reliabilityTooltip': 'Điểm tin cậy giao hàng nhà cung cấp',
  'analytics.si.allUrgencies': 'Tất cả mức độ',
  'analytics.si.urgencyCritical': 'Nghiêm trọng',
  'analytics.si.urgencyUrgent': 'Cấp bách',
  'analytics.si.urgencyWatch': 'Theo dõi',
  'analytics.si.urgencyHealthy': 'Tốt',
  'analytics.si.searchPlaceholder': 'Tìm sản phẩm, SKU, kho...',
  'analytics.si.action_reorder-now': 'Đặt hàng ngay',
  'analytics.si.action_reorder-soon': 'Đặt hàng sớm',
  'analytics.si.action_adjust-threshold': 'Điều chỉnh ngưỡng',
  'analytics.si.action_monitor': 'Theo dõi',
  'analytics.si.reason_pastReorderWindow':
    'Hàng sẽ hết trước khi nhà cung cấp kịp giao. Đặt hàng ngay để giảm thiểu mất doanh thu.',
  'analytics.si.reason_approachingStockOut':
    'Tồn kho đang giảm và sắp đến điểm đặt hàng. Hãy đặt sớm để duy trì hàng hóa.',
  'analytics.si.reason_thresholdMismatch':
    'Ngưỡng cảnh báo tồn kho thấp hiện tại khác biệt đáng kể so với điểm đặt hàng tối ưu được tính toán.',
  'analytics.si.reason_stockHealthy':
    'Mức tồn kho ổn định. Không cần hành động lúc này.',

  // ============================================
  // Profile
  // ============================================
  'profile.title': 'Hồ sơ',
  'profile.subtitle': 'Xem và quản lý thông tin tài khoản',
  'profile.loadingTitle': 'Đang tải...',
  'profile.loadingBody': 'Đang tải hồ sơ...',
  'profile.errorLoading': 'Lỗi khi tải hồ sơ',
  'profile.personalInfo': 'Thông tin cá nhân',
  'profile.editProfile': 'Sửa hồ sơ',
  'profile.security': 'Bảo mật',
  'profile.securityDesc': 'Cập nhật mật khẩu để bảo vệ tài khoản.',
  'profile.changePassword': 'Đổi mật khẩu',
  'profile.dangerZone': 'Vùng nguy hiểm',
  'profile.deleteAccount': 'Xóa tài khoản',
  'profile.deleteAccountDesc':
    'Xóa vĩnh viễn tài khoản và toàn bộ dữ liệu liên quan. Hành động này không thể hoàn tác.',
  'profile.deleteAccountConfirmTitle': 'Xóa tài khoản của bạn',
  'profile.deleteAccountConfirmText':
    'Hành động này sẽ xóa vĩnh viễn tài khoản, xóa bạn khỏi mọi doanh nghiệp và xóa toàn bộ dữ liệu. Không thể hoàn tác.',
  'profile.deleteAccountCreatorWarning':
    'Bạn là người tạo doanh nghiệp này. Xóa tài khoản sẽ xóa vĩnh viễn toàn bộ doanh nghiệp bao gồm tất cả hàng hóa, giao dịch, nhập hàng và dữ liệu nhóm. Tất cả thành viên sẽ bị xóa khỏi nhóm.',
  'profile.deleteAccountPasswordLabel': 'Nhập mật khẩu để xác nhận',
  'profile.deleteAccountButton': 'Xóa vĩnh viễn tài khoản',
  'profile.deleteAccountDeleting': 'Đang xóa...',
  'profile.deleteAccountSuccess': 'Đã xóa tài khoản thành công.',

  // ============================================
  // Error Messages
  // ============================================
  'error.2000': 'Thao tác thành công',
  'error.2001': 'Tạo thành công',
  'error.2002': 'Hoàn thành',
  'error.4000': 'Dữ liệu không hợp lệ. Vui lòng kiểm tra lại.',
  'error.4001': 'Tham số truy vấn không hợp lệ.',
  'error.4002': 'Email hoặc mật khẩu không đúng. Vui lòng thử lại.',
  'error.4003': 'Cần đăng nhập. Vui lòng đăng nhập.',
  'error.4009': 'Định dạng mật khẩu không hợp lệ.',
  'error.4004':
    'Email này đã được đăng ký. Vui lòng dùng email khác hoặc đăng nhập.',
  'error.4005': 'Mã đặt lại mật khẩu không hợp lệ.',
  'error.4006': 'Thiếu mã đặt lại mật khẩu.',
  'error.4007': 'Mã đặt lại mật khẩu đã hết hạn.',
  'error.4101':
    'Token truy cập không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.',
  'error.4104': 'Phiên đã hết hạn. Vui lòng đăng nhập lại.',
  'error.4105': 'Bạn đã bị đăng xuất vì tài khoản được đăng nhập từ nơi khác.',
  'error.4303': 'Mã bảo mật không khớp. Vui lòng tải lại trang.',
  'error.4304': 'Bạn không có quyền thực hiện thao tác này.',
  'error.4305':
    'Không có doanh nghiệp liên kết với tài khoản. Vui lòng tạo hoặc tham gia.',
  'error.4306':
    'Liên kết xác minh không hợp lệ hoặc đã hết hạn. Vui lòng đăng ký lại.',
  'error.4307': 'Liên kết xác minh đã hết hạn. Vui lòng đăng ký lại.',
  'error.4308': 'Tài khoản đã bị vô hiệu hóa. Vui lòng liên hệ quản trị viên.',
  'error.4309': 'Bạn không có quyền cần thiết cho thao tác này.',
  'error.4310': 'Bạn không được gán vào kho hàng này.',
  'error.4311':
    'Chưa có kho hàng nào được gán cho tài khoản. Liên hệ quản trị viên.',
  'error.4312': 'Thao tác này yêu cầu quyền quản trị.',
  'error.4313': 'Bạn không thể thực hiện thao tác này trên tài khoản của mình.',
  'error.4314': 'Tài khoản này không thể chỉnh sửa.',
  'error.4315': 'Chủ doanh nghiệp không thể bị chỉnh sửa.',
  'error.4290': 'Quá nhiều yêu cầu. Vui lòng thử lại sau.',
  'error.4020': 'Cần đăng ký gói dịch vụ. Vui lòng thiết lập gói.',
  'error.4021': 'Gói dịch vụ đã hết hạn. Vui lòng gia hạn.',
  'error.4022': 'Đã đạt giới hạn gói hiện tại. Nâng cấp để có thêm dung lượng.',
  'error.4023': 'Tính năng này không có trong gói hiện tại. Vui lòng nâng cấp.',
  'error.4024':
    'Không thể hạ cấp — mức sử dụng hiện tại vượt quá giới hạn gói mục tiêu.',
  'error.4025': 'Kho hàng này đã bị khóa do hạ cấp gói. Nâng cấp để mở khóa.',
  'error.4400': 'Không tìm thấy người dùng.',
  'error.4401': 'Không tìm thấy doanh nghiệp.',
  'error.4402': 'Không tìm thấy kho hàng.',
  'error.4403': 'Không tìm thấy sản phẩm.',
  'error.4404': 'Không tìm thấy đối tác.',
  'error.4405': 'Không tìm thấy giao dịch.',
  'error.4406': 'Không tìm thấy phiếu nhập hàng.',
  'error.4410': 'Không tìm thấy thành viên.',
  'error.4411': 'Không tìm thấy phiên.',
  'error.4412': 'Không tìm thấy vai trò.',
  'error.4413':
    'Không thể xóa kho — vẫn còn sản phẩm. Hãy di chuyển hoặc xóa sản phẩm trước.',
  'error.5000': 'Lỗi máy chủ. Vui lòng thử lại sau.',
  'error.6000': 'Dịch vụ email không khả dụng. Vui lòng thử lại sau.',
  'error.7000': 'Lỗi cơ sở dữ liệu. Vui lòng thử lại.',
  'error.7001': 'Yêu cầu cơ sở dữ liệu không hợp lệ.',
  'error.7002': 'Cơ sở dữ liệu tạm thời không khả dụng.',
  'error.7003': 'Xác thực dữ liệu thất bại. Vui lòng kiểm tra lại.',
  'error.7004': 'Bản ghi này đã tồn tại.',
  'error.unknown': 'Đã xảy ra lỗi không mong muốn. Vui lòng thử lại.',

  // Billing — Banner
  'billing.daysRemaining': '— Còn {days} ngày.',
  'billing.contactAdminResolve': 'Liên hệ quản trị viên để giải quyết.',
  'billing.reviewOrUpgrade': 'Xem lại gói hoặc nâng cấp để hủy.',
  'billing.storehousesLocked': '{n} kho hàng đã bị khóa',
  'billing.dueToPlanDowngrade': 'do hạ cấp gói.',
  'billing.contactAdminUnlock': 'Liên hệ quản trị viên để mở khóa.',
  'billing.upgradePlanUnlock': 'Nâng cấp gói để mở khóa.',
  'billing.goToBilling': 'Đến trang Thanh toán →',

  // Swap Modal
  'swap.title': 'Hoán đổi tài nguyên',
  'swap.description':
    'Hoán đổi kho hàng bị khóa hoặc người dùng bị vô hiệu hóa. Mỗi lần hoán đổi là 1-1 (không thay đổi tổng số). Giới hạn 2 lần mỗi ngày.',
  'swap.storehouses': 'Kho hàng',
  'swap.users': 'Người dùng',
  'swap.loadFailed': 'Không tải được danh sách hoán đổi. Vui lòng thử lại.',
  'swap.emptyState':
    'Không có kho hàng bị khóa hoặc người dùng bị vô hiệu hóa để hoán đổi.',
  'swap.unlockCurrentlyLocked': 'Mở khóa (hiện đang bị khóa)',
  'swap.lockCurrentlyActive': 'Khóa (hiện đang hoạt động)',
  'swap.reactivateCurrentlyDeactivated':
    'Kích hoạt lại (hiện đang bị vô hiệu hóa)',
  'swap.deactivateCurrentlyActive': 'Vô hiệu hóa (hiện đang hoạt động)',
  'swap.dailyLimit': 'Giới hạn 2 lần hoán đổi mỗi ngày',
  'swap.summary': 'Tóm tắt:',
  'swap.storehouseSummary': 'Khóa "{lockName}" → Mở khóa "{unlockName}"',
  'swap.userSummary':
    'Vô hiệu hóa "{deactivateName}" → Kích hoạt lại "{reactivateName}"',
  'swap.created': 'Tạo ngày',
  'swap.active': 'Hoạt động',
  'swap.swapping': 'Đang hoán đổi...',
  'swap.confirmSwap': 'Xác nhận hoán đổi',

  // Downgrade Modal
  'downgrade.title': 'Hạ cấp xuống {tier}',
  'downgrade.loading': 'Đang tải yêu cầu...',
  'downgrade.loadFailed': 'Không tải được yêu cầu hạ cấp. Vui lòng thử lại.',
  'downgrade.noChangesNeeded':
    'Không cần thay đổi — mức sử dụng hiện tại phù hợp với gói {tier}.',
  'downgrade.downgrading': 'Đang hạ cấp...',
  'downgrade.confirm': 'Xác nhận hạ cấp',

  // Upgrade Prompt
  'upgrade.featureNotAvailable': 'Tính năng này không có trong gói hiện tại.',
  'upgrade.barcodePrintRequired':
    'In nhãn mã vạch & QR chỉ có trên gói Pro và Enterprise.',
  'upgrade.limitReached':
    'Bạn đã đạt giới hạn ({usage}). Nâng cấp để tăng dung lượng.',
  'upgrade.default': 'Nâng cấp gói để mở khóa tính năng này.',
  'upgrade.goToBilling': 'Đến trang Thanh toán →',

  // Error Titles
  'errorTitle.planLimit': 'Đã đạt giới hạn gói',
  'errorTitle.auth': 'Lỗi xác thực',
  'errorTitle.cannotDelete': 'Không thể xóa',
  'errorTitle.notFound': 'Không tìm thấy',
  'errorTitle.server': 'Lỗi máy chủ',
  'errorTitle.email': 'Lỗi email',
  'errorTitle.database': 'Lỗi cơ sở dữ liệu',
  'errorTitle.rateLimit': 'Vượt giới hạn yêu cầu',
  'errorTitle.validation': 'Lỗi xác thực',

  // Activity Log
  'activity.title': 'Nhật ký hoạt động',
  'activity.subtitle':
    'Xem tổng hợp tất cả giao dịch, nhập hàng, chuyển kho và điều chỉnh tồn kho',
  'activity.tabs.all': 'Tất cả',
  'activity.tabs.orders': 'Đơn hàng',
  'activity.tabs.imports': 'Nhập hàng',
  'activity.tabs.transfers': 'Chuyển kho',
  'activity.tabs.adjustments': 'Điều chỉnh',
  'activity.filters.allTypes': 'Tất cả loại',
  'activity.filters.search': 'Tìm theo ID, đối tác, tên hàng...',
  'activity.filters.dateFrom': 'Từ ngày',
  'activity.filters.dateTo': 'Đến ngày',
  'activity.filters.createdBy': 'Người tạo',
  'activity.filters.allUsers': 'Tất cả',
  'activity.filters.reset': 'Xóa bộ lọc',
  'activity.filters.advanced': 'Bộ lọc',
  'activity.type.order': 'Đơn hàng',
  'activity.type.import': 'Nhập hàng',
  'activity.type.transfer': 'Chuyển kho',
  'activity.type.adjustment': 'Điều chỉnh',
  'activity.timeline.empty': 'Không có hoạt động nào',
  'activity.timeline.emptyHint': 'Thử thay đổi bộ lọc hoặc khoảng thời gian',
  'activity.timeline.loadMore': 'Tải thêm',
  'activity.timeline.items': '{count} mặt hàng',
  'activity.timeline.units': '{count} đơn vị',
  'activity.timeline.from': 'Từ',
  'activity.timeline.to': 'Đến',
  'activity.timeline.by': 'bởi',
  'activity.timeline.completedBy': 'Hoàn thành bởi',
  'activity.timeline.createdBy': 'Tạo bởi',
  'activity.detail.id': 'Mã hồ sơ',
  'activity.detail.partner': 'Đối tác',
  'activity.detail.createdAt': 'Ngày tạo',
  'activity.detail.updatedAt': 'Cập nhật lúc',

  // Adjustments
  'adjustments.title': 'Điều chỉnh tồn kho',
  'adjustments.newAdjustment': 'Điều chỉnh mới',
  'adjustments.item': 'Mặt hàng',
  'adjustments.quantity': 'Số lượng',
  'adjustments.reason': 'Lý do',
  'adjustments.notes': 'Ghi chú',
  'adjustments.previous': 'Trước',
  'adjustments.new': 'Sau',
  'adjustments.change': 'Thay đổi',
  'adjustments.performedBy': 'Người thực hiện',
  'adjustments.date': 'Ngày',
  'adjustments.allReasons': 'Tất cả lý do',
  'adjustments.searchPlaceholder': 'Tìm theo ID...',
  'adjustments.creating': 'Đang tạo...',
  'adjustments.createSuccess': 'Điều chỉnh tồn kho thành công',
  'adjustments.createError': 'Không thể điều chỉnh tồn kho',
  'adjustments.reason.supplier-delivery': 'Giao hàng từ NCC',
  'adjustments.reason.return': 'Trả hàng',
  'adjustments.reason.manual-count': 'Kiểm đếm',
  'adjustments.reason.sale': 'Bán hàng',
  'adjustments.reason.damage': 'Hư hỏng',
  'adjustments.reason.theft': 'Mất cắp',
  'adjustments.empty': 'Chưa có điều chỉnh nào',
  'adjustments.emptyHint':
    'Điều chỉnh tồn kho sẽ xuất hiện ở đây khi hàng hóa được điều chỉnh thủ công',
  'adjustments.selectItem': 'Chọn mặt hàng',
  'adjustments.addStock': 'Thêm hàng',
  'adjustments.removeStock': 'Bớt hàng',
  'adjustments.notesPlaceholder':
    'Ghi chú về điều chỉnh này (không bắt buộc)...',
  'adjustments.quantityPlaceholder': 'Nhập số lượng',

  // ── Nhật ký kiểm toán ──────────────────────────────────────
  'auditLog.title': 'Nhật ký kiểm toán',
  'auditLog.subtitle':
    'Theo dõi thay đổi danh mục và cấu hình trong doanh nghiệp',
  'auditLog.showFilters': 'Hiện bộ lọc',
  'auditLog.hideFilters': 'Ẩn bộ lọc',
  'auditLog.clearFilters': 'Xóa bộ lọc',
  'auditLog.searchPlaceholder': 'Tìm theo tên, người dùng...',
  'auditLog.allActions': 'Tất cả hành động',
  'auditLog.allEntities': 'Tất cả đối tượng',
  'auditLog.empty': 'Không có bản ghi kiểm toán nào',
  'auditLog.error': 'Không thể tải nhật ký kiểm toán',
  'auditLog.retry': 'Thử lại',
  'auditLog.showing': 'Hiển thị',
  'auditLog.of': 'trên',
  'auditLog.prev': 'Trước',
  'auditLog.next': 'Sau',

  // Nhật ký kiểm toán — Tiêu đề cột
  'auditLog.col.when': 'Thời gian',
  'auditLog.col.user': 'Người dùng',
  'auditLog.col.action': 'Hành động',
  'auditLog.col.description': 'Mô tả',
  'auditLog.col.details': 'Chi tiết',

  // Nhật ký kiểm toán — Hành động
  'auditLog.action.create': 'Tạo mới',
  'auditLog.action.update': 'Cập nhật',
  'auditLog.action.delete': 'Xóa',
  'auditLog.action.bulkCreate': 'Tạo hàng loạt',
  'auditLog.action.bulkUpdate': 'Cập nhật hàng loạt',
  'auditLog.action.bulkDelete': 'Xóa hàng loạt',
  'auditLog.action.assignRoles': 'Gán vai trò',
  'auditLog.action.assignStorehouses': 'Gán kho hàng',
  'auditLog.action.changeAppRole': 'Đổi vai trò',
  'auditLog.action.deactivate': 'Vô hiệu hóa',
  'auditLog.action.reactivate': 'Kích hoạt lại',
  'auditLog.action.invite': 'Mời',

  // Nhật ký kiểm toán — Đối tượng
  'auditLog.entity.Item': 'Sản phẩm',
  'auditLog.entity.StoreHouse': 'Kho hàng',
  'auditLog.entity.User': 'Người dùng',
  'auditLog.entity.Business': 'Doanh nghiệp',
  'auditLog.entity.CustomRole': 'Vai trò',
  'auditLog.entity.Partner': 'Đối tác',
  'auditLog.entity.PendingUser': 'Người dùng được mời',

  // Nhật ký kiểm toán — Modal chi tiết
  'auditLog.detail.summary': 'Tóm tắt',
  'auditLog.detail.user': 'Thực hiện bởi',
  'auditLog.detail.target': 'Đối tượng',
  'auditLog.detail.targetId': 'Mã đối tượng',
  'auditLog.detail.count': 'Số lượng',
  'auditLog.detail.details': 'Chi tiết',
  'auditLog.detail.timestamp': 'Thời gian',

  // ============================================
  // Bố cục công khai
  // ============================================
  'public.nav.pricing': 'Bảng giá',
  'public.nav.login': 'Đăng nhập',
  'public.nav.signup': 'Bắt đầu miễn phí',
  'public.nav.brand': 'khoai.io',
  'public.footer.copyright': '© 2026 khoai.io. Mọi quyền được bảo lưu.',
  'public.footer.privacy': 'Chính sách bảo mật',
  'public.footer.terms': 'Điều khoản dịch vụ',
  'public.footer.billingTerms': 'Điều khoản thanh toán',
  'public.footer.contact': 'Liên hệ',

  // ============================================
  // Trang chủ (Landing)
  // ============================================

  // Hero — Chuyên nghiệp & Trực tiếp (B2B)
  'landing.hero.title': 'Đừng để hết hàng âm thầm bào mòn doanh thu của bạn.',
  'landing.hero.subtitle':
    'Hết hàng tiêu tốn trung bình 4% doanh thu mỗi năm. khoai.io theo dõi mọi SKU trên tất cả kho và cửa hàng theo thời gian thực — cảnh báo sớm trước khi xảy ra mất hàng, để bạn kịp bổ sung và không để vuột đơn.',
  'landing.hero.getStarted': 'Dùng thử miễn phí, không cần thẻ tín dụng',
  'landing.hero.viewPricing': 'Xem bảng giá',
  'landing.hero.trust1': 'Gói miễn phí vĩnh viễn',
  'landing.hero.trust2': 'Dùng thử Pro 30 ngày',
  'landing.hero.trust3': 'Không cần thẻ tín dụng',
  'landing.hero.img.inventory':
    'Màn hình quản lý tồn kho khoai.io hiển thị mức tồn kho theo kho hàng',
  'landing.hero.img.mobile':
    'Ứng dụng di động khoai.io để quản lý kho và quét mã vạch mọi lúc mọi nơi',

  // Hero flow diagram
  'landing.hero.flow.label': 'thử khoAI',
  'landing.hero.flow.done': '✓ xong',
  'landing.hero.flow.importBtn': 'Nhập hàng',
  'landing.hero.flow.tableProduct': 'Sản phẩm',
  'landing.hero.flow.tableQty': 'Số lượng',
  'landing.hero.flow.tablePrice': 'Giá',
  'landing.hero.flow.row1Sku': 'Cáp USB',
  'landing.hero.flow.row1Price': '$4,50',
  'landing.hero.flow.row2Sku': 'Cảm biến v2',
  'landing.hero.flow.row2Price': '$32,00',
  'landing.hero.flow.row3Sku': 'Cuộn nhãn',
  'landing.hero.flow.row3Price': '$1,20',
  'landing.hero.flow.sellBtn': 'Bán hàng',
  'landing.hero.flow.revenueLabel': 'Doanh thu hôm nay',
  'landing.hero.flow.revenueAmount': '+$1.964,00',
  'landing.hero.flow.revenueEmpty': '$ —',
  'landing.hero.flow.revenueDetail': '↑ 3 đơn hoàn thành · 780 sản phẩm đã bán',

  // Cách hoạt động — Nhập → Ngưỡng → Cảnh báo
  'landing.howItWorks.title': 'Sẵn sàng vận hành trong chưa đầy 15 phút',
  'landing.howItWorks.subtitle':
    'Không cần tư vấn. Không cần thiết lập phức tạp. Kết nối danh mục, đặt ngưỡng và khoai.io lo phần còn lại — 24/7.',
  'landing.howItWorks.step1.title': '1. Nhập danh mục hàng hóa',
  'landing.howItWorks.step1.desc':
    'Tải lên CSV hoặc thêm sản phẩm thủ công. Thiết lập kho, cửa hàng và mời đội ngũ — hầu hết doanh nghiệp hoạt động trong dưới 15 phút.',
  'landing.howItWorks.step2.title': '2. Đặt ngưỡng một lần, thôi lo',
  'landing.howItWorks.step2.desc':
    'Xác định mức tồn kho thấp và mức nhập bổ sung cho từng SKU. Hệ thống theo dõi mọi đơn vị trên mọi địa điểm — 24/7, không cần bảng tính.',
  'landing.howItWorks.step3.title': '3. Bổ sung hàng trước khi khách nhận ra',
  'landing.howItWorks.step3.desc':
    'Ngay khi tồn kho giảm dưới ngưỡng, bạn nhận thông báo qua email và trong ứng dụng. Hành động dựa trên dữ liệu thực — không bao giờ để mất đơn hàng vì hết hàng có thể ngăn chặn.',

  // Tính năng
  'landing.features.title':
    'Đầy đủ công cụ quản lý tồn kho cho kho hàng và cửa hàng',
  'landing.features.subtitle':
    'Từ kho, quầy kệ cửa hàng đến mã vạch, khoai.io cung cấp công cụ giúp đội ngũ vận hành gọn gàng, hiệu quả và phù hợp với nhiều nhóm sản phẩm cần kiểm kê thường xuyên.',
  'landing.feature.warehouse.title': 'Theo dõi đa điểm kho và cửa hàng',
  'landing.feature.warehouse.desc':
    'Quản lý tồn kho trên nhiều kho và cửa hàng từ một bảng điều khiển duy nhất. Luôn biết bạn có gì và ở đâu.',
  'landing.feature.orders.title': 'Đơn hàng và giao dịch',
  'landing.feature.orders.desc':
    'Theo dõi mua, bán và điều chỉnh với lịch sử giao dịch đầy đủ cùng nhật ký kiểm toán cho các sản phẩm cần kiểm soát chặt.',
  'landing.feature.team.title': 'Vai trò và quyền đội ngũ',
  'landing.feature.team.desc':
    'Mời đội ngũ của bạn và phân quyền chi tiết với vai trò tùy chỉnh phù hợp với quy trình làm việc.',
  'landing.feature.transfers.title': 'Chuyển kho',
  'landing.feature.transfers.desc':
    'Di chuyển hàng tồn kho giữa các kho một cách liền mạch với bản ghi chuyển kho được theo dõi.',
  'landing.feature.analytics.title': 'Phân tích và trí tuệ',
  'landing.feature.analytics.desc':
    'Phát hiện hàng ế, dự đoán sắp hết hàng và ưu tiên nhập hàng với báo cáo trí tuệ tích hợp.',
  'landing.feature.barcode.title': 'Nhãn mã vạch và QR',
  'landing.feature.barcode.desc':
    'Tạo và in nhãn mã vạch hoặc mã QR cho sản phẩm trực tiếp từ ứng dụng.',
  'landing.feature.mobile.title': 'Ứng dụng di động',
  'landing.feature.mobile.desc':
    'Quét mã vạch, kiểm tra tồn kho và quản lý kho hàng khi di chuyển từ điện thoại.',
  'landing.feature.import.title': 'Nhập và xuất CSV',
  'landing.feature.import.desc':
    'Nhập hàng loạt danh mục hiện có hoặc xuất dữ liệu bất kỳ lúc nào. Dữ liệu luôn là của bạn.',

  // Vấn đề → Giải pháp
  'landing.problem.title': 'Chi phí ẩn của quản lý kho bằng bảng tính',
  'landing.problem.subtitle':
    'Doanh nghiệp trung bình mất 4% doanh thu hàng năm do hết hàng và tồn kho dư thừa. Nếu bạn vẫn quản lý kho bằng bảng tính, đây là những gì bạn đang thiếu:',
  // Story split section
  'landing.story.eyebrow': 'Một ngày làm việc',
  'landing.story.imgAlt':
    'Nhân viên quản lý kho đang làm việc tại văn phòng hiện đại với nhiều màn hình',
  'landing.story.title': 'Từ phỏng đoán đến kiểm soát — trong một ca làm',
  'landing.story.lead':
    'Hãy gặp Minh. Cô quản lý kho cho ba địa điểm và trước đây phải mất hàng giờ mỗi tuần để săn lỗi bảng tính. Với khoai.io, buổi sáng của cô đã hoàn toàn thay đổi.',
  'landing.story.p1heading': 'Nhìn thấy ngay, không cần tìm kiếm',
  'landing.story.p1body':
    'Minh mở khoai.io và thấy tất cả địa điểm trong một giao diện. Hết hàng tại Chi nhánh 2? Cảnh báo đã được gửi. Không cần gọi điện, không phải đào bời.',
  'landing.story.p2heading': 'Phát hiện lỗi trước khi thiệt hại xảy ra',
  'landing.story.p2body':
    'Sai lệch được đánh dấu tự động. Mọi chỉnh sửa đều có dấu thời gian và tên người thực hiện — Minh biết chính xác điều gì đã thay đổi, khi nào và ai làm.',
  'landing.story.p3heading': 'Cả đội cùng bước tiến',
  'landing.story.p3body':
    'Nhóm của cô làm việc trong phạm vi phân quyền riêng. Không ghi đè nhầm, không ai bị khóa quyền. Đơn nhập hàng được duyệt trong vài phút, không phải vài ngày.',
  'landing.story.cta': 'Xem cách hoạt động →',

  // Pain Feed marquee
  'landing.painfeed.a1': 'Tôi không biết thực sự còn bao nhiêu hàng',
  'landing.painfeed.a2': 'Ai đó lại sửa sai file nữa rồi',
  'landing.painfeed.a3': 'Không biết ai đã thay đổi số liệu này',
  'landing.painfeed.a4': 'Phát hiện ra lỗi sau ba tuần',
  'landing.painfeed.a5': 'Hai người đang chỉnh hai phiên bản khác nhau',
  'landing.painfeed.b1': 'Không biết kho nào đang có hàng',
  'landing.painfeed.b2': 'Phải kiểm kê thủ công mỗi tháng',
  'landing.painfeed.b3': 'Công thức sai suốt sáu tháng',
  'landing.painfeed.b4': 'Mất đơn vì tồn kho hiển thị sai',
  'landing.painfeed.b5': 'Không có nhật ký khi có sự cố',

  'landing.problem.eyebrow': 'So sánh',
  'landing.problem.item1': 'Mức tồn kho thời gian thực trên tất cả địa điểm',
  'landing.problem.item2': 'Cảnh báo tự động khi sắp hết hàng và hàng ế',
  'landing.problem.item3': 'Nhật ký kiểm toán cho mọi giao dịch',
  'landing.problem.item4':
    'Phân quyền theo vai trò để đội ngũ cộng tác an toàn',
  'landing.problem.cta': 'Xem cách khoai.io giải quyết vấn đề này →',
  'landing.showcase.smartAlerts': 'Cảnh báo thông minh khi sắp hết hàng',
  'landing.showcase.forecast': 'Dự báo tồn kho và gợi ý nhập bổ sung',

  // Xây dựng trên — tin tưởng hạ tầng
  'landing.builtWith.title': 'Xây dựng trên hạ tầng đáng tin cậy',
  'landing.builtWith.subtitle':
    'Chúng tôi sử dụng cùng công cụ được tin dùng bởi các công ty Fortune 500 để dữ liệu của bạn an toàn, nhanh và luôn sẵn sàng.',
  'landing.builtWith.item1.name': 'MongoDB Atlas',
  'landing.builtWith.item1.desc': 'Cơ sở dữ liệu được mã hóa',
  'landing.builtWith.item2.name': 'Xendit',
  'landing.builtWith.item2.desc': 'Xử lý thanh toán an toàn',
  'landing.builtWith.item3.name': 'Vercel',
  'landing.builtWith.item3.desc': 'Hosting toàn cầu',
  'landing.builtWith.item4.name': 'TLS / HTTPS',
  'landing.builtWith.item4.desc': 'Mã hóa khi truyền tải',
  'landing.builtWith.item5.name': 'Railway',
  'landing.builtWith.item5.desc': 'Hạ tầng backend',

  // Người sáng lập / minh bạch
  'landing.founder.title':
    'Được xây dựng bởi một người thật, không phải tập đoàn vô danh',
  'landing.founder.subtitle':
    'khoai.io là sản phẩm độc lập được xây dựng với tâm huyết. Là người dùng sớm, bạn được truy cập trực tiếp đến người xây dựng nó.',
  'landing.founder.chat': 'Chat với người sáng lập',
  'landing.founder.roadmap': 'Xem lộ trình công khai',
  'landing.founder.chatDesc':
    'Email trực tiếp đến người sáng lập tại human.khoai@gmail.com — không bot, không chờ đợi, câu trả lời từ người thật.',
  'landing.founder.roadmapDesc':
    'Xem chính xác chúng tôi đang xây dựng gì tiếp theo. Đề xuất tính năng. Bình chọn ưu tiên.',
  'landing.founder.social': 'Theo dõi cập nhật trên mạng xã hội',
  'landing.founder.socialDesc':
    'Các kênh mạng xã hội công khai đang được thiết lập. Chúng tôi sẽ đăng cập nhật sản phẩm, ghi chú phát hành và điểm nhấn lộ trình tại đó.',
  'landing.founder.socialCta': 'Liên kết mạng xã hội sắp ra mắt',

  // CTA
  'landing.cta.title': 'Ngừng mất doanh thu vì kệ hàng trống.',
  'landing.cta.subtitle':
    'Gói miễn phí bao gồm 1 địa điểm và tối đa 500 SKU — mãi mãi. Muốn nhiều hơn? Dùng thử Pro miễn phí 30 ngày. Không cần thẻ tín dụng. Hủy bất kỳ lúc nào.',
  'landing.cta.button': 'Bắt đầu miễn phí — không cần thẻ',

  // Explore / Danh mục tính năng
  'landing.explore.title': 'Khám phá sản phẩm chi tiết',
  'landing.explore.subtitle':
    'Duyệt các trang tính năng chuyên dụng cho từng khả năng chính trên nền tảng khoai.io.',
  'landing.explore.category01': 'Vận hành cốt lõi',
  'landing.explore.category02': 'AI & trí tuệ',
  'landing.explore.category03': 'Di động & mã vạch',
  'landing.explore.category04': 'Đội ngũ & phân quyền',
  'landing.explore.inventory': 'Quản lý kho hàng',
  'landing.explore.inventoryDesc':
    'Theo dõi tồn kho trên nhiều kho và cửa hàng, quản lý chuyển hàng, lịch sử giao dịch và quy trình kiểm kê định kỳ cho nhóm sản phẩm cần kiểm soát chặt.',
  'landing.explore.analytics': 'AI & Phân tích',
  'landing.explore.analyticsDesc':
    'Dự báo, cảnh báo sắp hết hàng, và gợi ý nhập bổ sung để hỗ trợ quyết định nhanh hơn.',
  'landing.explore.barcode': 'Mã vạch & Di động',
  'landing.explore.barcodeDesc':
    'Tạo và in mã vạch ngay trong ứng dụng. Quản lý kho trực tiếp trên điện thoại — vận hành nhanh hơn tại kho và quầy bán hàng.',
  'landing.explore.team': 'Đội ngũ & Quyền hạn',
  'landing.explore.teamDesc':
    'Phân quyền theo vai trò và trách nhiệm kỹ thuật để cộng tác an toàn.',

  // Dashboard Mockup
  'landing.dashboard.title': 'Bảng điều khiển khoai.io',
  'landing.dashboard.totalSku': 'Tổng SKU',
  'landing.dashboard.stockAtRisk': 'Tồn kho có rủi ro',
  'landing.dashboard.forecastedDemand': 'Nhu cầu dự báo',
  'landing.dashboard.inventoryTrend': 'Trendline tồn kho & Dự báo',
  'landing.dashboard.next14Days': '14 ngày tiếp theo',
  'landing.dashboard.topCriticalSkus': 'Top SKU cần nhập bổ sung',
  'landing.dashboard.reorderIn48h': 'Nhập bổ sung trong 48h',
  'landing.dashboard.sku': 'SKU',
  'landing.dashboard.onHand': 'Tồn kho',
  'landing.dashboard.min': 'Tối thiểu',
  'landing.dashboard.lowStockAlerts': 'Cảnh báo sắp hết hàng',
  'landing.dashboard.urgent': 'khẩn cấp',
  'landing.dashboard.warehouse': 'Kho hàng',
  'landing.dashboard.stockByLocation': 'Tồn kho theo địa điểm',
  'landing.dashboard.aiRestockRecommendation': 'Gợi ý nhập bổ sung từ AI',
  'landing.dashboard.reviewSuggestion': 'Xem gợi ý',

  // Problem Section - Comparison
  'landing.problem.spreadsheetLabel': 'Bảng tính',
  'landing.problem.khaoioLabel': 'khoai.io',
  'landing.problem.spreadsheetDesc':
    'Cập nhật thủ công, dễ sai sót, không kiểm soát quyền truy cập.',
  'landing.problem.spreadsheetRow1': 'Cập nhật bị trễ',
  'landing.problem.spreadsheetRow2': 'Kiểm tra thủ công',
  'landing.problem.spreadsheetRow3': 'Phân quyền rời rạc',
  'landing.problem.khaoioDesc':
    'Tồn kho theo thời gian thực, phân quyền rõ ràng, cảnh báo tự động.',
  'landing.problem.khaoioRow1': 'Tồn kho thực tế, mọi địa điểm',
  'landing.problem.khaoioRow2': 'Quy trình phân quyền rõ ràng',
  'landing.problem.khaoioRow3': 'Cảnh báo tự động',
  'landing.problem.khaoioTagline': 'Thời gian thực · Tự động · Mở rộng được',
  'landing.problem.impactLabel': 'tác động doanh thu hàng năm trung bình',
  'landing.problem.controlLabel': 'quyết định nhanh hơn với dữ liệu realtime',
  'landing.problem.comparisonTitle': 'So sánh độ tin cậy hoạt động',
  'landing.problem.stockVisibility': 'Độ hiển thị tồn kho',
  'landing.problem.alertResponseSpeed': 'Tốc độ phản ứng cảnh báo',
  'landing.problem.auditTraceCompleteness': 'Nhật ký kiểm toán đầy đủ',

  // Features Section 1
  'landing.feature.section1.eyebrow': 'Nghiệp vụ quản lý kho cốt lõi',
  'landing.feature.section1.title':
    'Theo dõi từng đơn vị trên tất cả địa điểm trong một quy trình',
  'landing.feature.section1.desc':
    'Quản lý hàng tồn kho trên nhiều kho từ một bảng điều khiển duy nhất. Luôn biết bạn có gì và ở đâu. Di chuyển hàng tồn kho giữa các kho một cách liền mạch với bản ghi chuyển kho được theo dõi.',
  'landing.feature.section1.stockByLocation': 'Tồn kho theo địa điểm',

  // Features Section 2
  'landing.feature.section2.eyebrow': 'Trí tuệ và gợi ý',
  'landing.feature.section2.title':
    'Ưu tiên nhập bổ sung trước khi ảnh hưởng đến doanh thu',
  'landing.feature.section2.desc':
    'Phát hiện hàng ế, dự đoán sắp hết hàng, và ưu tiên nhập bổ sung với báo cáo trí tuệ tích hợp.',
  'landing.feature.section2.lowStockSmartAlerts':
    'Cảnh báo thông minh khi sắp hết hàng',
  'landing.feature.section2.forecast': 'Dự báo tồn kho và gợi ý nhập bổ sung',

  // Features Section 3
  'landing.feature.section3.eyebrow': 'Thực thi và cộng tác',
  'landing.feature.section3.title':
    'Giúp đội ngũ làm việc nhanh hơn với đầy đủ kiểm soát và khả năng truy xuất',
  'landing.feature.section3.desc':
    'Mời đội ngũ của bạn và phân quyền chi tiết với vai trò tùy chỉnh phù hợp với quy trình làm việc. Quét mã vạch, kiểm tra tồn kho, và quản lý kho khi di chuyển từ điện thoại.',
  'landing.feature.section3.topCriticalSkus': 'Top SKU cần nhập bổ sung',
  'landing.feature.section3.status': 'Trạng thái',
  'landing.feature.section3.urgent': 'Khẩn cấp',
  'landing.feature.section3.risk': 'Rủi ro',
  'landing.feature.section3.monitor': 'Giám sát',

  // Dải tín nhiệm / Bằng chứng xã hội
  'landing.stats.item1': 'Đồng bộ thời gian thực trên tất cả địa điểm',
  'landing.stats.item2': 'Mọi điều chỉnh đều có nhật ký kiểm toán đầy đủ',
  'landing.stats.item3': 'Gợi ý nhập bổ sung dựa trên AI',
  'landing.stats.item4': 'Dùng thử Pro 30 ngày · Không cần thẻ tín dụng',

  // FAQ
  'landing.faq.title': 'Câu hỏi thường gặp',
  'landing.faq.subtitle': 'Mọi thứ bạn cần để quyết định tự tin.',
  'landing.faq.q1':
    'khoai.io có phù hợp với quy mô doanh nghiệp của tôi không?',
  'landing.faq.a1':
    'khoai.io phù hợp với doanh nghiệp từ 1 đến 100+ địa điểm — từ một cửa hàng nhỏ đến hệ thống đa kho. Gói miễn phí hỗ trợ một địa điểm; Pro mở rộng tùy nhu cầu.',
  'landing.faq.q2': 'Làm sao nhập dữ liệu tồn kho hiện tại?',
  'landing.faq.a2':
    'Dùng tính năng nhập CSV để tải toàn bộ danh mục sản phẩm trong vài phút. Bạn cũng có thể thêm từng sản phẩm thủ công. Hầu hết doanh nghiệp nhập xong từ bảng tính trong chưa đầy một giờ.',
  'landing.faq.q3': 'Điều gì xảy ra khi hết 30 ngày dùng thử Pro?',
  'landing.faq.a3':
    'Bạn tự động chuyển về gói Miễn phí — không tính phí, không mất dữ liệu. Toàn bộ kho hàng, lịch sử và cài đặt vẫn được giữ nguyên. Nâng cấp lại bất cứ lúc nào.',
  'landing.faq.q4':
    'Tôi có thể theo dõi tồn kho ở nhiều kho và cửa hàng không?',
  'landing.faq.a4':
    'Có. Theo dõi đa địa điểm là tính năng cốt lõi, không phải tiện ích tính thêm. Bạn có thể giám sát tồn kho, xử lý chuyển kho và kiểm toán điều chỉnh trên mọi địa điểm từ một bảng điều khiển.',
  'landing.faq.q5': 'Dữ liệu của tôi có được bảo vệ không?',
  'landing.faq.a5':
    'Dữ liệu của bạn được lưu trên MongoDB Atlas và truyền qua TLS/HTTPS. Hệ thống được thiết kế theo các thực hành bảo mật phổ biến cho ứng dụng doanh nghiệp, với các biện pháp bảo vệ ở cả tầng ứng dụng và hạ tầng.',

  // ============================================
  // Trang bảng giá
  // ============================================
  'pricing.title': 'Bảng giá đơn giản, minh bạch',
  'pricing.subtitle':
    'Bắt đầu miễn phí và mở rộng khi phát triển. Không phí ẩn.',
  'pricing.monthly': 'Hàng tháng',
  'pricing.annual': 'Hàng năm',
  'pricing.save': 'Tiết kiệm ~17%',
  'pricing.perMonth': '/tháng',
  'pricing.billedAnnually': 'Thanh toán hàng năm ({amount}/năm)',
  'pricing.recommended': 'Đề xuất',
  'pricing.vndNote': 'Giá bằng VND dành cho doanh nghiệp tại Việt Nam.',
  'pricing.trust.freePlan': 'Gói miễn phí vĩnh viễn',
  'pricing.trust.noCard': 'Không cần thẻ tín dụng để bắt đầu',
  'pricing.trust.cancel': 'Hủy bất kỳ lúc nào',

  'pricing.free.name': 'Miễn phí',
  'pricing.free.desc': 'Dành cho cá nhân bắt đầu theo dõi hàng tồn kho.',
  'pricing.free.cta': 'Bắt đầu',
  'pricing.free.f1': '1 kho hàng',
  'pricing.free.f2': '1 người dùng',
  'pricing.free.f3': '500 sản phẩm',
  'pricing.free.f4': '2.000 giao dịch/tháng',
  'pricing.free.f5': 'Phân tích cơ bản',

  'pricing.pro.name': 'Pro',
  'pricing.pro.desc':
    'Dành cho đội ngũ đang phát triển cần nhiều dung lượng và cộng tác hơn.',
  'pricing.pro.cta': 'Dùng thử miễn phí',
  'pricing.pro.f1': '3 kho hàng',
  'pricing.pro.f2': '10 người dùng',
  'pricing.pro.f3': '5.000 sản phẩm',
  'pricing.pro.f4': '20.000 giao dịch/tháng',
  'pricing.pro.f5': 'Chuyển kho',
  'pricing.pro.f6': 'Vai trò tùy chỉnh',
  'pricing.pro.f7': 'Báo cáo phân tích',

  'pricing.enterprise.name': 'Doanh nghiệp',
  'pricing.enterprise.desc': 'Dành cho tổ chức lớn với yêu cầu nâng cao.',
  'pricing.enterprise.cta': 'Liên hệ',
  'pricing.enterprise.f1': '10 kho hàng',
  'pricing.enterprise.f2': '30 người dùng',
  'pricing.enterprise.f3': '50.000 sản phẩm',
  'pricing.enterprise.f4': '100.000 giao dịch/tháng',
  'pricing.enterprise.f5': 'Tất cả trong Pro',
  'pricing.enterprise.f6': 'Báo cáo nâng cao',
  'pricing.enterprise.f7': 'Hỗ trợ ưu tiên',

  'pricing.addons.title':
    'Cần thêm? Thêm dung lượng cho bất kỳ gói trả phí nào.',
  'pricing.addons.subtitle':
    'Mua thêm tiện ích bổ sung để mở rộng giới hạn mà không cần thay đổi gói.',
  'pricing.addon.storehouse.name': 'Thêm kho hàng',
  'pricing.addon.storehouse.price': '$4,99/tháng mỗi kho',
  'pricing.addon.users.name': 'Thêm người dùng',
  'pricing.addon.users.price': '$2,99/tháng mỗi người',
  'pricing.addon.items.name': 'Thêm sản phẩm',
  'pricing.addon.items.price': '$3,99/tháng mỗi 1.000 sản phẩm',
  'pricing.addon.transactions.name': 'Thêm giao dịch',
  'pricing.addon.transactions.price': '$2,99/tháng mỗi 5.000 giao dịch',

  'pricing.faq.title': 'Câu hỏi thường gặp',
  'pricing.faq.trial.q': 'Điều gì xảy ra khi bản dùng thử miễn phí kết thúc?',
  'pricing.faq.trial.a':
    'Khi 30 ngày dùng thử Pro kết thúc, bạn có thể đăng ký tiếp tục gói Pro hoặc chuyển xuống gói Miễn phí — không tính phí nếu bạn không làm gì. Dữ liệu của bạn luôn được bảo toàn.',
  'pricing.faq.change.q': 'Tôi có thể thay đổi gói bất kỳ lúc nào không?',
  'pricing.faq.change.a':
    'Có. Nâng cấp có hiệu lực ngay lập tức. Hạ cấp áp dụng vào cuối kỳ thanh toán hiện tại.',
  'pricing.faq.payment.q': 'Bạn chấp nhận các phương thức thanh toán nào?',
  'pricing.faq.payment.a':
    'Chúng tôi chấp nhận chuyển khoản ngân hàng, ví điện tử và thẻ tín dụng thông qua đối tác xử lý thanh toán Xendit. Phương thức khả dụng tùy theo khu vực.',
  'pricing.faq.cancel.q': 'Tôi có thể hủy đăng ký không?',
  'pricing.faq.cancel.a':
    'Hoàn toàn được. Bạn có thể hủy từ trang thanh toán bất kỳ lúc nào. Bạn vẫn giữ quyền truy cập đến hết kỳ thanh toán hiện tại.',
  'pricing.faq.refund.q': 'Bạn có hoàn tiền không?',
  'pricing.faq.refund.a':
    'Gói hàng tháng không được hoàn tiền. Gói hàng năm có thể được hoàn tiền theo tỷ lệ tùy từng trường hợp. Liên hệ human.khoai@gmail.com.',
  'pricing.faq.vnd.q': 'Giá có sẵn bằng VND không?',
  'pricing.faq.vnd.a':
    'Có. Giá bằng VND dành cho doanh nghiệp đăng ký tại Việt Nam. Đơn vị tiền tệ được cố định tại thời điểm đăng ký.',
  'pricing.faq.data.q': 'Dữ liệu của tôi sẽ ra sao nếu tôi hủy?',
  'pricing.faq.data.a':
    'Dữ liệu của bạn được lưu trữ trong 30 ngày sau khi hủy để bạn có thể xuất trước khi bị xóa vĩnh viễn. Bạn có thể xuất dữ liệu bất kỳ lúc nào từ trang Cài đặt.',

  // ============================================
  // Điều khoản dịch vụ (19 mục)
  // ============================================
  'terms.title': 'Điều khoản dịch vụ',
  'terms.effective': 'Có hiệu lực: Tháng 5, 2026',

  // 1. Chấp nhận điều khoản
  'terms.s1.title': '1. Chấp nhận điều khoản',
  'terms.s1.p1':
    'Bằng việc truy cập hoặc sử dụng dịch vụ khoai.io ("Dịch vụ"), bạn đồng ý bị ràng buộc bởi các Điều khoản dịch vụ này ("Điều khoản"). Nếu bạn không đồng ý với các Điều khoản này, vui lòng không sử dụng Dịch vụ. Các Điều khoản này tạo thành thỏa thuận ràng buộc pháp lý giữa bạn và khoai.io.',

  // 2. Mô tả dịch vụ
  'terms.s2.title': '2. Mô tả dịch vụ',
  'terms.s2.p1':
    'khoai.io là nền tảng quản lý kho hàng và cửa hàng trên nền tảng đám mây, cho phép doanh nghiệp theo dõi hàng tồn kho trên nhiều kho, quản lý đơn hàng và giao dịch, cộng tác với các thành viên trong nhóm và tạo báo cáo. Dịch vụ có thể truy cập qua trình duyệt web và ứng dụng di động tại',

  // 3. Đăng ký tài khoản
  'terms.s3.title': '3. Đăng ký tài khoản',
  'terms.s3.li1':
    'Bạn phải cung cấp thông tin chính xác và đầy đủ khi tạo tài khoản.',
  'terms.s3.li2':
    'Bạn chịu trách nhiệm bảo mật thông tin đăng nhập và mọi hoạt động diễn ra dưới tài khoản của bạn.',
  'terms.s3.li3':
    'Mỗi tài khoản được liên kết với một doanh nghiệp. Bạn không được tạo nhiều tài khoản cho cùng một doanh nghiệp.',
  'terms.s3.li4':
    'Bạn phải thông báo cho chúng tôi ngay lập tức nếu nghi ngờ có truy cập trái phép vào tài khoản của bạn.',

  // 4. Sử dụng hợp lệ
  'terms.s4.title': '4. Sử dụng hợp lệ',
  'terms.s4.p1':
    'Bạn đồng ý chỉ sử dụng Dịch vụ cho các mục đích hợp pháp. Bạn không được:',
  'terms.s4.li1':
    'Sử dụng Dịch vụ cho bất kỳ hoạt động bất hợp pháp, gian lận hoặc có hại nào',
  'terms.s4.li2':
    'Lạm dụng, quấy rối hoặc can thiệp vào người dùng khác của Dịch vụ',
  'terms.s4.li3':
    'Cố gắng can thiệp, phá vỡ hoặc làm quá tải hạ tầng của Dịch vụ',
  'terms.s4.li4':
    'Kỹ thuật đảo ngược, dịch ngược hoặc tháo rời bất kỳ phần nào của Dịch vụ',
  'terms.s4.li5':
    'Truy cập hoặc cố gắng truy cập tài khoản của người dùng khác mà không được phép',
  'terms.s4.li6':
    'Sử dụng công cụ tự động để thu thập, quét hoặc trích xuất dữ liệu từ Dịch vụ ngoài những gì có sẵn qua các tính năng xuất dữ liệu',

  // 5. Sở hữu trí tuệ
  'terms.s5.title': '5. Sở hữu trí tuệ',
  'terms.s5.p1':
    'Nền tảng khoai.io — bao gồm thiết kế, mã nguồn, tính năng và tài liệu — là tài sản trí tuệ của khoai.io. Bạn không được sao chép, sửa đổi, phân phối hoặc tạo các tác phẩm phái sinh từ Dịch vụ mà không có sự đồng ý bằng văn bản của chúng tôi.',
  'terms.s5.p2':
    'Bạn giữ toàn quyền sở hữu tất cả dữ liệu bạn nhập vào Dịch vụ, bao gồm dữ liệu tồn kho, bản ghi giao dịch và thông tin doanh nghiệp.',

  // 6. Dữ liệu người dùng
  'terms.s6.title': '6. Dữ liệu người dùng',
  'terms.s6.p1':
    'Bạn giữ quyền sở hữu tất cả dữ liệu bạn gửi lên khoai.io. Bằng việc sử dụng Dịch vụ, bạn cấp cho khoai.io giấy phép hạn chế, không độc quyền để xử lý, lưu trữ và hiển thị dữ liệu của bạn chỉ nhằm mục đích cung cấp và cải thiện Dịch vụ.',
  'terms.s6.p2':
    'Chúng tôi sẽ không truy cập dữ liệu của bạn trừ khi cần thiết để vận hành Dịch vụ, cung cấp hỗ trợ theo yêu cầu của bạn hoặc tuân thủ nghĩa vụ pháp lý. Để biết thêm chi tiết, xem',

  // 7. Đăng ký và thanh toán
  'terms.s7.title': '7. Đăng ký và thanh toán',
  'terms.s7.li1':
    'khoai.io cung cấp các gói Miễn phí, Pro và Doanh nghiệp. Tính năng và giới hạn khác nhau theo gói. Xem',
  'terms.s7.li1.after': 'để biết giá hiện tại.',
  'terms.s7.li2':
    'Các gói trả phí được thanh toán trước theo chu kỳ hàng tháng hoặc hàng năm. Gói hàng năm được giảm giá.',
  'terms.s7.li3':
    'Tiện ích bổ sung cho dung lượng thêm (kho hàng, người dùng, sản phẩm, giao dịch) có sẵn cho các gói trả phí và được thanh toán hàng tháng.',
  'terms.s7.li4': 'Đăng ký được gia hạn tự động vào cuối mỗi kỳ thanh toán.',
  'terms.s7.li5':
    'Thanh toán được xử lý an toàn thông qua Xendit. Các phương thức thanh toán bao gồm chuyển khoản ngân hàng, ví điện tử và thẻ tín dụng (khả dụng tùy theo khu vực).',
  'terms.s7.li6':
    'Gói Pro bao gồm 30 ngày dùng thử miễn phí — không cần thẻ tín dụng. Nếu bạn không đăng ký trước khi kết thúc dùng thử, tài khoản sẽ tự động chuyển về gói Miễn phí. Mỗi doanh nghiệp được dùng thử một lần.',
  'terms.s7.p1': 'Để biết chi tiết thanh toán đầy đủ, xem',

  // 8. Hủy và hoàn tiền
  'terms.s8.title': '8. Hủy và hoàn tiền',
  'terms.s8.li1':
    'Bạn có thể hủy đăng ký bất kỳ lúc nào từ trang thanh toán trong cài đặt tài khoản.',
  'terms.s8.li2':
    'Sau khi hủy, bạn vẫn giữ quyền truy cập các tính năng trả phí đến hết kỳ thanh toán hiện tại.',
  'terms.s8.li3': 'Gói hàng tháng không được hoàn tiền.',
  'terms.s8.li4':
    'Gói hàng năm có thể được hoàn tiền theo tỷ lệ tùy từng trường hợp theo quyết định của chúng tôi. Liên hệ',
  'terms.s8.li4.after': 'để yêu cầu hoàn tiền.',

  // 9. Tính khả dụng của dịch vụ
  'terms.s9.title': '9. Tính khả dụng của dịch vụ',
  'terms.s9.p1':
    'Chúng tôi nỗ lực duy trì thời gian hoạt động cao nhất có thể cho Dịch vụ. Đối với gói trả phí, chúng tôi đặt mục tiêu khả dụng 99,9%. Tuy nhiên, gói Miễn phí không bao gồm đảm bảo SLA.',
  'terms.s9.p2':
    'Chúng tôi có thể tạm ngừng Dịch vụ để bảo trì, cập nhật hoặc sửa chữa khẩn cấp. Chúng tôi sẽ nỗ lực hợp lý để thông báo trước về thời gian ngừng hoạt động theo kế hoạch.',

  // 10. Giới hạn trách nhiệm
  'terms.s10.title': '10. Giới hạn trách nhiệm',
  'terms.s10.p1':
    'Dịch vụ được cung cấp trên cơ sở "nguyên trạng" và "khi có sẵn". khoai.io không đưa ra bất kỳ bảo đảm nào, dù rõ ràng hay ngụ ý, về độ tin cậy, độ chính xác hoặc sự phù hợp của Dịch vụ cho một mục đích cụ thể.',
  'terms.s10.p2':
    'Trong phạm vi tối đa được pháp luật cho phép, tổng trách nhiệm của khoai.io đối với bạn cho bất kỳ khiếu nại nào phát sinh từ hoặc liên quan đến Dịch vụ không vượt quá tổng số tiền bạn đã thanh toán cho khoai.io trong 12 tháng trước khiếu nại.',
  'terms.s10.p3':
    'khoai.io không chịu trách nhiệm cho bất kỳ thiệt hại gián tiếp, ngẫu nhiên, đặc biệt, hậu quả hoặc mang tính trừng phạt nào, bao gồm mất dữ liệu, doanh thu hoặc cơ hội kinh doanh.',

  // 11. Bồi thường (MỚI)
  'terms.s11.title': '11. Bồi thường',
  'terms.s11.p1':
    'Bạn đồng ý bồi thường, bảo vệ và giữ cho khoai.io, các viên chức, giám đốc, nhân viên và đại lý không bị thiệt hại từ bất kỳ khiếu nại, trách nhiệm, tổn hại, mất mát và chi phí nào (bao gồm phí pháp lý hợp lý) phát sinh từ hoặc liên quan đến việc bạn truy cập hoặc sử dụng Dịch vụ, vi phạm các Điều khoản này hoặc xâm phạm quyền của bên thứ ba.',

  // 12. Bất khả kháng (MỚI)
  'terms.s12.title': '12. Bất khả kháng',
  'terms.s12.p1':
    'khoai.io không chịu trách nhiệm cho bất kỳ sự thất bại hoặc chậm trễ nào trong việc thực hiện nghĩa vụ theo các Điều khoản này nếu sự thất bại hoặc chậm trễ đó do các hoàn cảnh ngoài tầm kiểm soát hợp lý, bao gồm nhưng không giới hạn ở thiên tai, hành vi của chính phủ, gián đoạn internet hoặc hạ tầng, tấn công mạng, đại dịch hoặc các sự kiện bất khả kháng khác.',

  // 13. Chấm dứt tài khoản
  'terms.s13.title': '13. Chấm dứt tài khoản',
  'terms.s13.li1':
    'Chúng tôi có thể đình chỉ hoặc chấm dứt tài khoản của bạn nếu bạn vi phạm các Điều khoản này, lạm dụng Dịch vụ hoặc tham gia hoạt động gây hại cho khoai.io hoặc người dùng khác.',
  'terms.s13.li2':
    'Bạn có thể xóa tài khoản bất kỳ lúc nào. Khi xóa, tất cả dữ liệu liên quan (doanh nghiệp, tồn kho, giao dịch và hồ sơ đội ngũ) sẽ bị xóa vĩnh viễn sau thời gian ân hạn 30 ngày.',
  'terms.s13.li3':
    'Việc chấm dứt không miễn trừ bạn khỏi bất kỳ nghĩa vụ thanh toán còn tồn đọng nào.',

  // 14. Luật áp dụng và giải quyết tranh chấp (mở rộng)
  'terms.s14.title': '14. Luật áp dụng và giải quyết tranh chấp',
  'terms.s14.p1':
    'Các Điều khoản này được điều chỉnh và giải thích theo luật pháp của nước Cộng hòa Xã hội Chủ nghĩa Việt Nam, không xét đến các nguyên tắc xung đột pháp luật.',
  'terms.s14.p2':
    'Mọi tranh chấp phát sinh từ hoặc liên quan đến các Điều khoản này hoặc Dịch vụ trước tiên sẽ được giải quyết thông qua đàm phán thiện chí giữa các bên. Nếu tranh chấp không thể giải quyết thông qua đàm phán trong vòng 30 ngày, bất kỳ bên nào có thể đưa tranh chấp ra tòa án có thẩm quyền tại Việt Nam để giải quyết.',

  // 15. Chuyển nhượng (MỚI)
  'terms.s15.title': '15. Chuyển nhượng',
  'terms.s15.p1':
    'Bạn không được chuyển nhượng hoặc chuyển giao quyền hoặc nghĩa vụ của mình theo các Điều khoản này mà không có sự đồng ý bằng văn bản trước của khoai.io. khoai.io có thể chuyển nhượng quyền và nghĩa vụ theo các Điều khoản này cho một thực thể kế thừa trong trường hợp sáp nhập, mua lại hoặc bán toàn bộ hoặc phần lớn tài sản, với điều kiện bên kế thừa đồng ý bị ràng buộc bởi các Điều khoản này.',

  // 16. Thay đổi điều khoản
  'terms.s16.title': '16. Thay đổi điều khoản',
  'terms.s16.p1':
    'Chúng tôi có thể cập nhật các Điều khoản này theo thời gian. Khi thực hiện các thay đổi quan trọng, chúng tôi sẽ thông báo trước ít nhất 30 ngày qua email. Việc bạn tiếp tục sử dụng Dịch vụ sau khi các thay đổi có hiệu lực đồng nghĩa với việc bạn chấp nhận các Điều khoản đã sửa đổi.',

  // 17. Quy định riêng lẻ (MỚI)
  'terms.s17.title': '17. Quy định riêng lẻ',
  'terms.s17.p1':
    'Nếu bất kỳ điều khoản nào của các Điều khoản này bị tòa án có thẩm quyền xác định là không hợp lệ, bất hợp pháp hoặc không thể thi hành, các điều khoản còn lại vẫn tiếp tục có hiệu lực đầy đủ. Điều khoản không hợp lệ sẽ được sửa đổi đến mức tối thiểu cần thiết để có hiệu lực và có thể thi hành trong khi bảo toàn ý định ban đầu.',

  // 18. Toàn bộ thỏa thuận (MỚI — bao gồm từ bỏ quyền)
  'terms.s18.title': '18. Toàn bộ thỏa thuận',
  'terms.s18.p1':
    'Các Điều khoản này, cùng với Chính sách bảo mật và Điều khoản thanh toán, tạo thành toàn bộ thỏa thuận giữa bạn và khoai.io liên quan đến việc sử dụng Dịch vụ. Chúng thay thế tất cả các thỏa thuận, thông tin liên lạc và đề xuất trước đó hoặc đồng thời, dù bằng miệng hay bằng văn bản.',
  'terms.s18.p2':
    'Việc khoai.io không thực hiện hoặc thi hành bất kỳ quyền hoặc điều khoản nào của các Điều khoản này không cấu thành việc từ bỏ quyền hoặc điều khoản đó. Bất kỳ sự từ bỏ nào đối với bất kỳ điều khoản nào chỉ có hiệu lực khi được thể hiện bằng văn bản và có chữ ký của khoai.io.',

  // 19. Liên hệ
  'terms.s19.title': '19. Liên hệ',
  'terms.s19.p1':
    'Nếu bạn có bất kỳ câu hỏi nào về các Điều khoản này, vui lòng liên hệ với chúng tôi tại:',

  // ============================================
  // Chính sách bảo mật (14 mục)
  // ============================================
  'privacy.title': 'Chính sách bảo mật',
  'privacy.lastUpdated': 'Cập nhật lần cuối: Tháng 5, 2026',

  // 1. Giới thiệu
  'privacy.s1.title': '1. Giới thiệu',
  'privacy.s1.p1':
    'khoai.io là nền tảng quản lý kho hàng và cửa hàng trên nền tảng đám mây, giúp doanh nghiệp theo dõi hàng tồn kho, quản lý đơn hàng và cộng tác với đội ngũ. Chính sách bảo mật này giải thích cách khoai.io ("chúng tôi") thu thập, sử dụng, lưu trữ và bảo vệ thông tin cá nhân và doanh nghiệp của bạn khi bạn sử dụng trang web và dịch vụ tại',
  'privacy.s1.p2':
    'Bằng việc tạo tài khoản hoặc sử dụng dịch vụ, bạn đồng ý với các thông lệ được mô tả trong chính sách này. Nếu bạn không đồng ý, vui lòng không sử dụng khoai.io.',

  // 2. Thông tin chúng tôi thu thập
  'privacy.s2.title': '2. Thông tin chúng tôi thu thập',
  'privacy.s2.accountData': 'Dữ liệu tài khoản',
  'privacy.s2.account.li1': 'Họ và tên',
  'privacy.s2.account.li2': 'Địa chỉ email',
  'privacy.s2.account.li3': 'Số điện thoại (tùy chọn)',
  'privacy.s2.account.li4':
    'Mật khẩu (được lưu dưới dạng mã hóa — chúng tôi không bao giờ lưu mật khẩu dạng văn bản)',
  'privacy.s2.businessData': 'Dữ liệu doanh nghiệp',
  'privacy.s2.business.li1': 'Tên doanh nghiệp',
  'privacy.s2.business.li2': 'Địa chỉ doanh nghiệp',
  'privacy.s2.business.li3': 'Số điện thoại doanh nghiệp',
  'privacy.s2.inventoryData': 'Dữ liệu tồn kho và giao dịch',
  'privacy.s2.inventory.li1': 'Sản phẩm (tên, SKU, mô tả, giá, số lượng)',
  'privacy.s2.inventory.li2': 'Giao dịch (mua, bán, điều chỉnh)',
  'privacy.s2.inventory.li3': 'Chuyển kho giữa các kho hàng',
  'privacy.s2.inventory.li4': 'Hồ sơ nhà cung cấp và khách hàng',
  'privacy.s2.usageData': 'Dữ liệu sử dụng',
  'privacy.s2.usage.li1': 'Địa chỉ IP',
  'privacy.s2.usage.li2': 'Loại và phiên bản trình duyệt',
  'privacy.s2.usage.li3': 'Trang đã truy cập và tính năng đã sử dụng',
  'privacy.s2.usage.li4': 'Thời gian truy cập',
  'privacy.s2.cookiesData': 'Cookie',
  'privacy.s2.cookies.li1': 'Cookie phiên (để xác thực)',
  'privacy.s2.cookies.li2': 'Cookie CSRF (để bảo mật)',

  // 3. Cách chúng tôi sử dụng thông tin
  'privacy.s3.title': '3. Cách chúng tôi sử dụng thông tin của bạn',
  'privacy.s3.li1': 'Cung cấp, vận hành và duy trì dịch vụ khoai.io',
  'privacy.s3.li2': 'Xác thực danh tính và quản lý phiên đăng nhập',
  'privacy.s3.li3':
    'Gửi email giao dịch (xác minh tài khoản, đặt lại mật khẩu, lời mời đội ngũ)',
  'privacy.s3.li4': 'Xử lý thanh toán và quản lý đăng ký',
  'privacy.s3.li5':
    'Cải thiện dịch vụ thông qua phân tích sử dụng tổng hợp, ẩn danh',
  'privacy.s3.li6': 'Phản hồi yêu cầu hỗ trợ',

  // 4. Cơ sở pháp lý xử lý dữ liệu (MỚI)
  'privacy.s4.title': '4. Cơ sở pháp lý xử lý dữ liệu',
  'privacy.s4.p1':
    'Chúng tôi xử lý dữ liệu cá nhân của bạn dựa trên các cơ sở pháp lý sau:',
  'privacy.s4.li1':
    'Thực hiện hợp đồng — để cung cấp dịch vụ khoai.io mà bạn đã đăng ký, bao gồm quản lý tài khoản, thanh toán và hỗ trợ',
  'privacy.s4.li2':
    'Lợi ích hợp pháp — để cải thiện và bảo mật dịch vụ, ngăn chặn gian lận và phân tích mô hình sử dụng tổng hợp',
  'privacy.s4.li3':
    'Nghĩa vụ pháp lý — để tuân thủ luật pháp, quy định và quy trình pháp lý hiện hành',
  'privacy.s4.li4':
    'Sự đồng ý — khi bạn đã đưa ra sự đồng ý rõ ràng cho các hoạt động xử lý cụ thể, chẳng hạn như nhận thông tin tiếp thị',

  // 5. Chia sẻ dữ liệu
  'privacy.s5.title': '5. Chia sẻ dữ liệu',
  'privacy.s5.p1':
    'Chúng tôi không bán, cho thuê hoặc trao đổi dữ liệu cá nhân hoặc doanh nghiệp của bạn cho bên thứ ba.',
  'privacy.s5.p2':
    'Chúng tôi chỉ chia sẻ dữ liệu với các nhà cung cấp dịch vụ sau, chỉ để vận hành dịch vụ khoai.io:',
  'privacy.s5.li1': 'Xendit — xử lý thanh toán cho đăng ký và tiện ích bổ sung',
  'privacy.s5.li2': 'Resend / SendGrid — gửi email giao dịch',
  'privacy.s5.li3': 'Nhà cung cấp hạ tầng đám mây — lưu trữ hạ tầng giao diện',
  'privacy.s5.li4': 'Nhà cung cấp hạ tầng đám mây — lưu trữ hạ tầng máy chủ',
  'privacy.s5.li5': 'MongoDB Atlas — lưu trữ cơ sở dữ liệu',
  'privacy.s5.p3':
    'Mỗi nhà cung cấp có nghĩa vụ hợp đồng bảo vệ dữ liệu của bạn và chỉ sử dụng cho các mục đích chúng tôi chỉ định.',

  // 6. Chuyển dữ liệu quốc tế (MỚI)
  'privacy.s6.title': '6. Chuyển dữ liệu quốc tế',
  'privacy.s6.p1':
    'Dữ liệu của bạn có thể được lưu trữ và xử lý tại các khu vực pháp lý bên ngoài quốc gia cư trú của bạn, bao gồm nhưng không giới hạn ở Hoa Kỳ và Singapore, thông qua các nhà cung cấp hạ tầng và lưu trữ của chúng tôi.',
  'privacy.s6.p2':
    'Chúng tôi đảm bảo rằng các biện pháp bảo vệ thích hợp được áp dụng để bảo vệ dữ liệu của bạn theo luật bảo vệ dữ liệu hiện hành. Khi cần thiết, chúng tôi dựa vào các điều khoản hợp đồng tiêu chuẩn hoặc các cơ chế chuyển dữ liệu được phê duyệt khác.',

  // 7. Bảo mật dữ liệu
  'privacy.s7.title': '7. Bảo mật dữ liệu',
  'privacy.s7.p1':
    'Chúng tôi coi trọng bảo mật dữ liệu của bạn và áp dụng các biện pháp sau:',
  'privacy.s7.li1':
    'Tất cả dữ liệu được mã hóa khi truyền tải bằng TLS (HTTPS)',
  'privacy.s7.li2': 'Dữ liệu được mã hóa khi lưu trữ thông qua MongoDB Atlas',
  'privacy.s7.li3':
    'Cookie phiên là httpOnly và secure, ngăn chặn truy cập từ phía máy khách',
  'privacy.s7.li4':
    'Bảo vệ CSRF được kích hoạt trên tất cả các yêu cầu thay đổi trạng thái',
  'privacy.s7.li5': 'Giới hạn tốc độ được áp dụng để ngăn chặn lạm dụng',
  'privacy.s7.li6': 'Mật khẩu được mã hóa bằng thuật toán tiêu chuẩn ngành',

  // 8. Lưu giữ dữ liệu
  'privacy.s8.title': '8. Lưu giữ dữ liệu',
  'privacy.s8.li1':
    'Dữ liệu tài khoản được lưu giữ khi tài khoản còn hoạt động và trong 30 ngày sau khi xóa tài khoản để cho phép khôi phục.',
  'privacy.s8.li2':
    'Dữ liệu tồn kho, giao dịch và doanh nghiệp bị xóa vĩnh viễn khi doanh nghiệp liên quan bị xóa.',
  'privacy.s8.li3':
    'Nhật ký sử dụng được lưu giữ tối đa 90 ngày cho mục đích bảo mật và gỡ lỗi, sau đó tự động xóa.',

  // 9. Quyền của bạn (mở rộng)
  'privacy.s9.title': '9. Quyền của bạn',
  'privacy.s9.p1': 'Bạn có quyền:',
  'privacy.s9.li1':
    'Truy cập — xem tất cả dữ liệu cá nhân và doanh nghiệp được lưu trong tài khoản',
  'privacy.s9.li2':
    'Chỉnh sửa — cập nhật thông tin không chính xác từ cài đặt tài khoản',
  'privacy.s9.li3': 'Xóa — xóa tài khoản và tất cả dữ liệu liên quan',
  'privacy.s9.li4': 'Xuất dữ liệu — xuất dữ liệu tồn kho và giao dịch qua CSV',
  'privacy.s9.li5':
    'Hạn chế xử lý — yêu cầu giới hạn cách chúng tôi sử dụng dữ liệu của bạn trong một số trường hợp',
  'privacy.s9.li6':
    'Phản đối — phản đối việc xử lý dữ liệu dựa trên lợi ích hợp pháp',
  'privacy.s9.li7':
    'Khiếu nại — nộp đơn khiếu nại với cơ quan bảo vệ dữ liệu tại khu vực pháp lý của bạn',

  // 10. Cookie
  'privacy.s10.title': '10. Cookie',
  'privacy.s10.p1': 'khoai.io sử dụng các cookie sau:',
  'privacy.s10.li1':
    'Cookie phiên — cookie httpOnly được sử dụng để duy trì phiên xác thực của bạn',
  'privacy.s10.li2':
    'Cookie CSRF — được sử dụng để bảo vệ chống lại các cuộc tấn công giả mạo yêu cầu chéo trang',
  'privacy.s10.p2':
    'Chúng tôi không sử dụng cookie theo dõi của bên thứ ba, cookie quảng cáo hoặc cookie phân tích từ các nhà cung cấp bên ngoài.',

  // 11. Trẻ em
  'privacy.s11.title': '11. Trẻ em',
  'privacy.s11.p1':
    'khoai.io không dành cho người dưới 16 tuổi. Chúng tôi không cố ý thu thập thông tin cá nhân từ trẻ em. Nếu bạn cho rằng trẻ em dưới 16 tuổi đã cung cấp dữ liệu cá nhân cho chúng tôi, vui lòng liên hệ tại',
  'privacy.s11.p1.after': 'và chúng tôi sẽ xóa thông tin ngay lập tức.',

  // 12. Thông báo vi phạm dữ liệu (MỚI)
  'privacy.s12.title': '12. Thông báo vi phạm dữ liệu',
  'privacy.s12.p1':
    'Trong trường hợp vi phạm dữ liệu có khả năng gây rủi ro cho quyền và quyền tự do của bạn, chúng tôi sẽ thông báo cho người dùng bị ảnh hưởng qua email trong vòng 72 giờ kể từ khi phát hiện vi phạm. Thông báo sẽ bao gồm bản chất vi phạm, dữ liệu có thể bị ảnh hưởng và các biện pháp được thực hiện để khắc phục và giảm thiểu tác động.',

  // 13. Thay đổi chính sách
  'privacy.s13.title': '13. Thay đổi chính sách này',
  'privacy.s13.p1':
    'Chúng tôi có thể cập nhật Chính sách bảo mật này theo thời gian. Khi thực hiện các thay đổi quan trọng, chúng tôi sẽ thông báo cho bạn qua email sử dụng địa chỉ liên kết với tài khoản của bạn. Chính sách cập nhật cũng sẽ được đăng trên trang này với ngày "Cập nhật lần cuối" đã sửa đổi.',

  // 14. Liên hệ
  'privacy.s14.title': '14. Liên hệ',
  'privacy.s14.p1':
    'Nếu bạn có bất kỳ câu hỏi hoặc lo ngại nào về Chính sách bảo mật này hoặc cách chúng tôi xử lý dữ liệu của bạn, vui lòng liên hệ tại:',

  // ============================================
  // Điều khoản thanh toán (16 mục)
  // ============================================
  'billingTerms.title': 'Điều khoản thanh toán',
  'billingTerms.effective': 'Có hiệu lực: Tháng 5, 2026',

  // 1. Gói và bảng giá
  'billingTerms.s1.title': '1. Gói và bảng giá',
  'billingTerms.s1.p1':
    'khoai.io cung cấp ba gói đăng ký: Miễn phí, Pro và Doanh nghiệp. Mỗi gói bao gồm các giới hạn tính năng khác nhau cho kho hàng, người dùng, sản phẩm và giao dịch hàng tháng. Chi tiết giá và tính năng hiện tại được công bố trên',

  // 2. Chu kỳ thanh toán
  'billingTerms.s2.title': '2. Chu kỳ thanh toán',
  'billingTerms.s2.p1':
    'Đăng ký trả phí được thanh toán trước theo chu kỳ hàng tháng hoặc hàng năm, tùy thuộc vào tùy chọn bạn chọn khi đăng ký. Thanh toán hàng năm được giảm giá so với giá hàng tháng. Chu kỳ thanh toán bắt đầu từ ngày bạn đăng ký và gia hạn vào cùng ngày mỗi kỳ.',

  // 3. Đơn vị tiền tệ
  'billingTerms.s3.title': '3. Đơn vị tiền tệ',
  'billingTerms.s3.p1':
    'Đăng ký được thanh toán bằng USD hoặc VND, được xác định bởi khu vực doanh nghiệp bạn chọn khi đăng ký. Đơn vị tiền tệ thanh toán được cố định tại thời điểm đăng ký và không thể thay đổi nếu không hủy và đăng ký lại.',

  // 4. Phương thức thanh toán
  'billingTerms.s4.title': '4. Phương thức thanh toán',
  'billingTerms.s4.p1':
    'Tất cả thanh toán được xử lý an toàn thông qua Xendit, đối tác xử lý thanh toán bên thứ ba của chúng tôi. Các phương thức thanh toán có sẵn bao gồm:',
  'billingTerms.s4.li1': 'Chuyển khoản ngân hàng',
  'billingTerms.s4.li2': 'Ví điện tử (nơi có sẵn)',
  'billingTerms.s4.li3': 'Thẻ tín dụng và thẻ ghi nợ (nơi có sẵn)',
  'billingTerms.s4.p2':
    'Phương thức thanh toán có sẵn có thể khác nhau tùy theo khu vực. khoai.io không lưu trữ chi tiết thẻ thanh toán — tất cả thông tin thanh toán nhạy cảm được xử lý bởi Xendit.',

  // 5. Tiện ích bổ sung
  'billingTerms.s5.title': '5. Tiện ích bổ sung',
  'billingTerms.s5.p1':
    'Người đăng ký gói trả phí có thể mua tiện ích bổ sung để tăng dung lượng vượt quá giới hạn của gói. Tiện ích bổ sung có sẵn cho kho hàng, người dùng, sản phẩm và giao dịch hàng tháng bổ sung.',
  'billingTerms.s5.p2':
    'Tiện ích bổ sung được thanh toán hàng tháng, bất kể chu kỳ thanh toán đăng ký. Tiện ích bổ sung có thể được thêm hoặc xóa bất kỳ lúc nào và có hiệu lực ngay lập tức.',

  // 6. Nâng cấp và hạ cấp
  'billingTerms.s6.title': '6. Nâng cấp và hạ cấp',
  'billingTerms.s6.li1':
    'Nâng cấp có hiệu lực ngay lập tức. Bạn sẽ bị tính phí theo tỷ lệ cho phần còn lại của kỳ thanh toán hiện tại theo giá gói mới.',
  'billingTerms.s6.li2':
    'Hạ cấp có hiệu lực vào cuối kỳ thanh toán hiện tại. Bạn sẽ giữ quyền truy cập các tính năng cấp cao hơn cho đến hết kỳ đó.',

  // 7. Gói miễn phí và Dùng thử
  'billingTerms.s7.title': '7. Gói miễn phí và Dùng thử',
  'billingTerms.s7.p1':
    'Gói Miễn phí không yêu cầu phương thức thanh toán và có sẵn vô thời hạn. Gói Miễn phí có tính năng và dung lượng giới hạn. Không có thỏa thuận mức dịch vụ (SLA) cho gói Miễn phí.',
  'billingTerms.s7.p2':
    'Đăng ký Pro mới bao gồm 30 ngày dùng thử miễn phí. Không cần thẻ tín dụng hoặc phương thức thanh toán để bắt đầu dùng thử.',
  'billingTerms.s7.p3':
    'Khi hết thời gian dùng thử, bạn có thể đăng ký để tiếp tục sử dụng các tính năng Pro, hoặc tài khoản sẽ tự động chuyển về gói Miễn phí — dữ liệu của bạn luôn được bảo toàn. Mỗi doanh nghiệp được dùng thử một lần.',

  // 8. Gia hạn tự động
  'billingTerms.s8.title': '8. Gia hạn tự động',
  'billingTerms.s8.p1':
    'Tất cả đăng ký trả phí được gia hạn tự động vào cuối mỗi kỳ thanh toán theo giá hiện hành. Để ngăn gia hạn, bạn phải hủy đăng ký trước ngày gia hạn. Bạn sẽ nhận được email nhắc nhở ít nhất 7 ngày trước khi đăng ký được gia hạn.',

  // 9. Thanh toán thất bại
  'billingTerms.s9.title': '9. Thanh toán thất bại',
  'billingTerms.s9.p1':
    'Nếu thanh toán thất bại, chúng tôi sẽ thông báo cho bạn và cung cấp thời gian ân hạn để cập nhật phương thức thanh toán. Nếu không nhận được thanh toán sau nhiều lần thử, tài khoản của bạn có thể bị đình chỉ. Trong thời gian đình chỉ, bạn không thể truy cập các tính năng trả phí, nhưng dữ liệu được bảo toàn trong 30 ngày. Sau 30 ngày đình chỉ mà không thanh toán, tài khoản có thể bị hạ cấp xuống gói Miễn phí và dữ liệu vượt quá giới hạn gói Miễn phí có thể không truy cập được.',

  // 10. Hoàn tiền
  'billingTerms.s10.title': '10. Hoàn tiền',
  'billingTerms.s10.li1':
    'Gói hàng tháng: Không hoàn tiền cho đăng ký hàng tháng. Bạn sẽ giữ quyền truy cập đến hết kỳ thanh toán hiện tại.',
  'billingTerms.s10.li2':
    'Gói hàng năm: Hoàn tiền theo tỷ lệ cho các tháng chưa sử dụng có thể được xem xét tùy từng trường hợp theo quyết định của chúng tôi. Liên hệ',
  'billingTerms.s10.li2.after': 'để yêu cầu hoàn tiền.',

  // 11. Hủy
  'billingTerms.s11.title': '11. Hủy',
  'billingTerms.s11.p1':
    'Bạn có thể hủy đăng ký bất kỳ lúc nào từ trang thanh toán trong cài đặt tài khoản. Sau khi hủy:',
  'billingTerms.s11.li1':
    'Bạn sẽ giữ quyền truy cập các tính năng trả phí đến hết kỳ thanh toán hiện tại.',
  'billingTerms.s11.li2':
    'Sau khi hết kỳ thanh toán, tài khoản sẽ chuyển về gói Miễn phí.',
  'billingTerms.s11.li3':
    'Dữ liệu được bảo toàn, nhưng các tính năng và giới hạn vượt quá gói Miễn phí sẽ không còn truy cập được.',

  // 12. Thuế
  'billingTerms.s12.title': '12. Thuế',
  'billingTerms.s12.p1':
    'Tất cả giá niêm yết trên khoai.io chưa bao gồm thuế. Bạn chịu trách nhiệm thanh toán bất kỳ loại thuế, phí hoặc lệ phí nào do khu vực pháp lý của bạn áp đặt liên quan đến việc sử dụng Dịch vụ.',

  // 13. Hóa đơn và biên lai (MỚI)
  'billingTerms.s13.title': '13. Hóa đơn và biên lai',
  'billingTerms.s13.p1':
    'Hóa đơn cho các khoản thanh toán đăng ký được tạo tự động và có sẵn trong cài đặt thanh toán. Hóa đơn bao gồm kỳ thanh toán, chi tiết gói, số tiền tính phí và thuế áp dụng. Bạn có thể tải hóa đơn dạng PDF để lưu trữ.',

  // 14. Tranh chấp thanh toán (MỚI)
  'billingTerms.s14.title': '14. Tranh chấp thanh toán',
  'billingTerms.s14.p1':
    'Nếu bạn cho rằng một khoản phí không chính xác, vui lòng liên hệ chúng tôi tại',
  'billingTerms.s14.p1.after':
    'trong vòng 30 ngày kể từ ngày tính phí. Chúng tôi sẽ xem xét tranh chấp và phản hồi trong vòng 10 ngày làm việc. Trong thời gian tranh chấp đang được xem xét, quyền truy cập Dịch vụ của bạn sẽ không bị gián đoạn.',

  // 15. Thay đổi giá
  'billingTerms.s15.title': '15. Thay đổi giá',
  'billingTerms.s15.p1':
    'Chúng tôi có thể thay đổi giá theo thời gian. Khi thay đổi, chúng tôi sẽ thông báo trước ít nhất 30 ngày qua email. Thay đổi giá sẽ áp dụng vào ngày gia hạn tiếp theo. Đăng ký hiện tại sẽ được giữ nguyên giá hiện hành cho đến hết kỳ thanh toán hiện tại.',

  // 16. Liên hệ
  'billingTerms.s16.title': '16. Liên hệ',
  'billingTerms.s16.p1':
    'Đối với các câu hỏi về thanh toán, yêu cầu hoàn tiền hoặc vấn đề thanh toán, vui lòng liên hệ tại:',

  // ============================================
  // Feature Pages - Chung/Công cộng
  // ============================================
  'feature.problemSolution': 'Vấn đề và giải pháp',
  'feature.featureBreakdown': 'Phân tích tính năng',
  'feature.cta': 'Bắt đầu miễn phí',
  'feature.ctaSubtitle':
    'Bắt đầu với một quy trình mà đội ngũ bạn có thể tin tưởng và mở rộng từ một kho hoặc cửa hàng đến nhiều điểm vận hành.',

  // Trang quản lý kho hàng
  'feature.inventory.tag': 'Quản lý kho hàng',
  'feature.inventory.title':
    'Một hệ thống cho mọi mặt hàng, mọi kho và cửa hàng, mọi chuyển động',
  'feature.inventory.subtitle':
    'Từ một kho hoặc cửa hàng đến nhiều điểm, khoai.io cung cấp số lượng tồn kho trực tiếp, điều chỉnh có thể truy vết và chuyển kho kiểm soát — trong một hồ sơ hoạt động duy nhất.',
  'feature.inventory.problemDesc1':
    'Hầu hết các vấn đề tồn kho đều xuất phát từ một điều: không ai biết con số chính xác. Bảng tính lỗi thời, các địa điểm mâu thuẫn nhau, và khi phát hiện sai lệch thì thiệt hại đã xảy ra.',
  'feature.inventory.problemDesc2':
    'khoai.io biến mỗi thay đổi số lượng thành một sự kiện có thể kiểm toán — được ghi loại, đóng dấu thời gian và gắn với đúng địa điểm. Mọi điều chỉnh và cập nhật tồn kho đều được xử lý đồng bộ để số lượng luôn nhất quán, và chuyển kho theo dõi từng bước từ xuất kho đến nhận hàng được xác nhận.',
  'feature.inventory.bullet1':
    'Số lượng trực tiếp theo từng điểm kho/cửa hàng với tín hiệu sắp hết và hết hàng',
  'feature.inventory.bullet2':
    'Điều chỉnh với lý do được ghi rõ — bán, trả hàng, hư hỏng, mất trộm, kiểm kê thủ công, hàng nhà cung cấp',
  'feature.inventory.bullet3':
    'Chuyển kho theo dõi từ chờ xử lý → đang vận chuyển → đã nhận, kèm xác nhận nhận hàng từng phần và ghi nhận sai lệch',
  'feature.inventory.f1': 'Hiển thị tồn kho theo địa điểm',
  'feature.inventory.f1Desc':
    'Xem tổng mặt hàng, số lượng, số hàng sắp hết, và doanh thu hôm nay trong một bảng tóm tắt. Lọc theo kho/cửa hàng, trạng thái, nhãn hoặc khoảng giá. Quyền truy cập của mỗi địa điểm được kiểm soát độc lập.',
  'feature.inventory.f1Bullet1':
    'Một dashboard hiển thị tổng mặt hàng, số lượng tồn và mức rủi ro theo từng địa điểm',
  'feature.inventory.f1Bullet2':
    'Bộ lọc nhanh theo điểm kho/cửa hàng, trạng thái tồn kho, nhãn và khoảng giá',
  'feature.inventory.f1Bullet3':
    'Tín hiệu sắp hết hàng và hết hàng tích hợp sẵn để nhập bổ sung kịp thời',
  'feature.inventory.f2': 'Điều chỉnh tồn kho có kiểm toán',
  'feature.inventory.f2Desc':
    'Mỗi thay đổi số lượng đều ghi lý do cụ thể và được cập nhật tức thì và nhất quán trên toàn hệ thống. Nhờ đó đội ngũ của bạn có lịch sử rõ ràng, dễ truy vết cho kiểm toán, đối soát và kiểm kê lại.',
  'feature.inventory.f2Bullet1':
    'Mỗi lần điều chỉnh đều ghi số lượng trước, số lượng sau, lý do, ghi chú và người thực hiện',
  'feature.inventory.f2Bullet2':
    'Lý do được chuẩn hóa giúp lịch sử kiểm toán dễ đọc hơn cho bán hàng, hư hỏng, trả hàng, mất trộm, kiểm kê thủ công và hàng từ nhà cung cấp',
  'feature.inventory.f2Bullet3':
    'Lịch sử điều chỉnh có thể lọc theo mặt hàng, lý do, người thao tác, khoảng ngày hoặc từ khóa để điều tra nhanh hơn',
  'feature.inventory.f3': 'Vòng đời chuyển kho',
  'feature.inventory.f3Desc':
    'Chuyển hàng giữa các kho và cửa hàng với theo dõi trạng thái đầy đủ từ lúc tạo yêu cầu đến khi nhận hàng. Đội ngũ có thể theo dõi từng bước, ghi nhận sai lệch khi hàng đến và giữ lịch sử di chuyển gắn với đúng địa điểm.',
  'feature.inventory.f3Bullet1':
    'Theo dõi từng lệnh chuyển qua các trạng thái chờ xử lý, đã xuất, đang vận chuyển, nhận từng phần, hoàn tất hoặc đã hủy',
  'feature.inventory.f3Bullet2':
    'Cho phép nhận hàng từng phần và ghi lý do sai lệch như hư hỏng, thiếu hàng, sai mặt hàng hoặc lệch số lượng',
  'feature.inventory.f3Bullet3':
    'Xem các chuyển kho quá hạn và các phiếu nhận từng phần bị treo từ hộp thư ngoại lệ chuyên dụng',
  'feature.inventory.proTooltip': 'Có sẵn từ gói Pro trở lên',
  'feature.inventory.f4': 'Nhập dữ liệu chỉ trong vài phút',
  'feature.inventory.f4Desc':
    'Chỉ cần tải lên bảng tính hiện có của bạn. khoai.io tự động ánh xạ cột, kiểm tra từng dòng và đưa kho hàng vào hệ thống — chính xác, không cần định dạng lại.',
  'feature.inventory.f4SpreadsheetAlt': 'Dữ liệu bảng tính thô',
  'feature.inventory.f4TableAlt': 'Kho hàng đã nhập vào khoai.io',
  'feature.inventory.cta': 'Ngừng đoán. Bắt đầu kiểm soát.',

  // Trang AI & Phân tích
  'feature.analytics.tag': 'AI & Phân tích',
  'feature.analytics.title': 'Ngừng đoán. Bắt đầu dự báo.',
  'feature.analytics.subtitle':
    'Đi trước tình trạng hết hàng với dự báo được hỗ trợ bởi AI, cảnh báo thông minh, và các gợi ý dựa trên dữ liệu cho đội vận hành kho, cửa hàng, nhà bán lẻ và nhà phân phối.',
  'feature.analytics.f0': 'Xu hướng doanh thu & chi phí',
  'feature.analytics.f0Desc':
    'Xem chính xác doanh thu đang tăng trưởng ở đâu và chi phí đang leo thang ở đâu — phân tích theo điểm kho/cửa hàng, danh mục hoặc khoảng thời gian.',
  'feature.analytics.f0Bullet1':
    'Doanh thu so với giá vốn hàng bán trên cùng một trục thời gian để so sánh nhanh',
  'feature.analytics.f0Bullet2':
    'Phân tích chi tiết theo điểm kho/cửa hàng, đối tác hoặc danh mục để xác định áp lực lợi nhuận',
  'feature.analytics.f0Bullet3':
    'So sánh theo kỳ để phát hiện biến động mùa vụ và xu hướng tăng trưởng',
  'feature.analytics.problemDesc1':
    'Các cách làm truyền thống dựa vào dữ liệu lịch sử và ngưỡng thủ công thường không còn hiệu quả khi nhu cầu thay đổi, có tính mùa vụ, hoặc tăng trưởng nhanh.',
  'feature.analytics.problemDesc2':
    'khoai.io liên tục học từ các mẫu tồn kho của bạn và cảnh báo bạn hàng tuần trước để bạn có thể hành động, không phải phản ứng.',
  'feature.analytics.bullet1': 'Dự báo nhu cầu tự động theo SKU và địa điểm',
  'feature.analytics.bullet2': 'Cảnh báo sắp hết hàng và hàng ế thời gian thực',
  'feature.analytics.bullet3':
    'Gợi ý nhập hàng tự động — phê duyệt chỉ một thao tác',
  'feature.analytics.f1': 'Dự báo theo bối cảnh',
  'feature.analytics.f1Desc':
    'Dự báo nhu cầu dựa trên lịch sử bán hàng, xu hướng mùa, và các mẫu riêng của từng địa điểm — không chỉ là mức trung bình đơn giản.',
  'feature.analytics.f1Bullet1':
    'Dự báo nhu cầu theo từng SKU, bám sát nhịp bán hàng và tốc độ tăng trưởng của bạn',
  'feature.analytics.f1Bullet2':
    'Phát hiện xu hướng theo mùa vụ và sự kiện trên toàn bộ danh mục hàng hóa',
  'feature.analytics.f1Bullet3':
    'Dự báo theo từng địa điểm để mỗi kho và cửa hàng có ngưỡng cảnh báo phù hợp riêng',
  'feature.analytics.f2': 'Cảnh báo thông minh khi sắp hết hàng',
  'feature.analytics.f2Desc':
    'Nhận thông báo hàng tuần trước về khả năng hết hàng để các đơn nhập bổ sung đến kịp thời.',
  'feature.analytics.f2Bullet1':
    'Danh sách cảnh báo được xếp hạng theo mức độ nghiêm trọng — khẩn cấp, cảnh báo và theo dõi',
  'feature.analytics.f2Bullet2':
    'Ngưỡng cảnh báo tự điều chỉnh khi nhu cầu biến động',
  'feature.analytics.f2Bullet3':
    'Gợi ý nhập hàng kèm số lượng, nhà cung cấp và thời gian giao dự kiến — phê duyệt chỉ một thao tác',
  'feature.analytics.f3': 'Khám phá hàng ế',
  'feature.analytics.f3Desc':
    'Phát hiện hàng hóa luân chuyển chậm và tìm cơ hội giải phóng vốn hoặc phân bổ lại.',
  'feature.analytics.f4': 'Tự động nhập bổ sung',
  'feature.analytics.f4Desc':
    'Xem lại đề xuất nhập bổ sung từ AI và phê duyệt chỉ với một cú nhấp. Tích hợp liền mạch với quy trình PO của bạn.',
  'feature.analytics.cta': 'Giảm tình trạng hết hàng và hàng ế bằng trí tuệ',

  // Trang mã vạch & di động
  'feature.barcode.tag': 'Mã vạch & Di động',
  'feature.barcode.title': 'Tự tin về tồn kho, mọi lúc mọi nơi',
  'feature.barcode.subtitle':
    'Tạo mã vạch trong vài giây. Quét từ bất kỳ điện thoại nào. Di chuyển tồn kho nhanh trên cả kho và cửa hàng với tầm nhìn đầy đủ và ít sai sót hơn.',
  'feature.barcode.problemDesc1':
    'Đếm thủ công và ghi chép bằng bút giấy chậm, dễ sai sót, và không cho đội ngũ thấy số liệu tồn kho kịp thời.',
  'feature.barcode.problemDesc2':
    'Ứng dụng di động khoai.io cho phép đội ngũ quét, kiểm tra và xác nhận mỗi thao tác ngay tại kho hay quầy bán hàng — không cần bảng tính, không cần quay về máy tính.',
  'feature.barcode.bullet1': 'Tạo mã vạch và mã QR',
  'feature.barcode.bullet2':
    'Ứng dụng di động để nhận hàng, soát kho và ghi điều chỉnh',
  'feature.barcode.bullet3':
    'Đồng bộ theo thời gian thực — vẫn hoạt động khi mất mạng',
  'feature.barcode.f1': 'In mã vạch nhanh',
  'feature.barcode.f1Desc':
    'Tạo và in nhãn mã vạch hoặc mã QR cho bất kỳ mặt hàng nào ngay trong khoai.io — in hàng loạt theo lô hoặc in riêng từng cái.',
  'feature.barcode.f1Bullet1':
    'In nhãn đơn lẻ hoặc theo lô chỉ với một thao tác',
  'feature.barcode.f1Bullet2':
    'Tùy chỉnh mẫu nhãn với tên mặt hàng, SKU, đơn vị và giá',
  'feature.barcode.f1Bullet3':
    'Xuất sang bất kỳ máy in nhãn tương thích hoặc định dạng trang tiêu chuẩn',
  'feature.barcode.f2': 'Quy trình bán hàng trên di động',
  'feature.barcode.f2Desc':
    'Bán hàng trực tiếp trên di động: quét để thêm sản phẩm, tổng tiền cập nhật tức thì và hoàn tất thanh toán ngay — phù hợp cho quầy bán nhanh.',
  'feature.barcode.f2Bullet1':
    'Quét mã vạch để thêm vào giỏ — giá và tồn kho được kiểm tra tức thì',
  'feature.barcode.f2Bullet2':
    'Điều chỉnh số lượng, áp dụng giảm giá theo dòng và hoàn tất đơn bán mà không cần chuyển màn hình',
  'feature.barcode.f2Bullet3':
    'Mỗi giao dịch trên di động được đồng bộ ngay để tồn kho và doanh thu luôn chính xác cho toàn đội',
  'feature.barcode.f3': 'Lịch sử thao tác di động được kiểm toán',
  'feature.barcode.f3Desc':
    'Mọi thao tác bán hàng trên di động đều được ghi lại theo dòng thời gian đầy đủ để quản lý kiểm tra rõ ai làm gì, vào lúc nào.',
  'feature.barcode.f3Bullet1':
    'Theo dõi vòng đời đơn hàng từ lúc tạo giỏ trên di động đến khi hoàn tất thanh toán',
  'feature.barcode.f3Bullet2':
    'Xem người thực hiện từng thao tác cùng mốc thời gian để rà soát nhanh',
  'feature.barcode.f3Bullet3':
    'Mở lịch sử đơn hàng ngay trong Activity để xử lý ngoại lệ hoặc khiếu nại khách hàng',
  'feature.barcode.f4': 'Chế độ ngoại tuyến',
  'feature.barcode.f4Desc':
    'Tiếp tục quét và làm việc ngay cả khi mất mạng. Toàn bộ dữ liệu tự đồng bộ ngay khi kết nối trở lại.',
  'feature.barcode.f4Bullet1':
    'Mọi lần quét được lưu cục bộ và tự động đồng bộ khi có mạng trở lại',
  'feature.barcode.f4Bullet2':
    'Hoạt động trên mọi thiết bị Android hoặc iOS — không cần phần cứng đặc biệt',
  'feature.barcode.f4Bullet3':
    'Không mất dữ liệu — toàn bộ thao tác trong phiên được giữ nguyên và khớp đúng sau khi kết nối lại',
  'feature.barcode.cta':
    'Tăng tốc vận hành kho và cửa hàng với giải pháp quản lý tồn kho trên di động',

  // Trang đội ngũ & quyền hạn
  'feature.team.tag': 'Đội ngũ & Quyền hạn',
  'feature.team.title': 'Làm việc như một đội ngũ, với kiểm soát tuyệt đối',
  'feature.team.subtitle':
    'Mời đội ngũ của bạn. Gán vai trò. Theo dõi mọi hành động. Tất cả giao dịch được ghi để tuân thủ và trách nhiệm vận hành trên cả kho và cửa hàng.',
  'feature.team.problemDesc1':
    'Tài khoản dùng chung và quyền truy cập quá rộng khiến bạn khó biết ai đã làm gì, làm tăng rủi ro dữ liệu và gây khó khăn cho kiểm toán.',
  'feature.team.problemDesc2':
    'khoai.io cung cấp phân quyền theo vai trò, quyền chi tiết và nhật ký kiểm toán đầy đủ để mọi thao tác đều có thể truy vết và an toàn.',
  'feature.team.bullet1': 'Kiểm soát truy cập chi tiết theo vai trò',
  'feature.team.bullet2':
    'Vai trò tùy chỉnh phù hợp với quy trình làm việc của bạn',
  'feature.team.bullet3': 'Nhật ký kiểm tra đầy đủ của tất cả giao dịch',
  'feature.team.f1': 'Vai trò được xây dựng trước',
  'feature.team.f1Desc':
    'Vai trò Quản trị, Giám sát, Người vận hành, và Người xem đã sẵn sàng sử dụng. Gán thành viên đội ngũ linh hoạt bất cứ lúc nào.',
  'feature.team.f1Bullet1':
    'Bắt đầu với bộ vai trò mẫu đã kiểm chứng cho các vị trí vận hành kho và cửa hàng phổ biến',
  'feature.team.f1Bullet2':
    'Gán quyền trong vài giây khi thêm nhân sự mới hoặc nhân viên thời vụ',
  'feature.team.f1Bullet3':
    'Giữ hành vi quyền hạn nhất quán giữa các kho và các ca làm việc',
  'feature.team.f2': 'Vai trò tùy chỉnh',
  'feature.team.f2Desc':
    'Tạo vai trò với quyền hạn cụ thể cho người nhận, người lấy, người đếm, hoặc bất kỳ quy trình tùy chỉnh nào bạn cần.',
  'feature.team.f2Bullet1':
    'Phân quyền theo module và hành động thay vì cấp quyền rộng gây rủi ro',
  'feature.team.f2Bullet2':
    'Thiết kế mẫu vai trò cho quy trình nhiều bước với trách nhiệm rõ ràng',
  'feature.team.f2Bullet3':
    'Cập nhật quyền an toàn khi quy trình thay đổi mà không gián đoạn vận hành',
  'feature.team.f3': 'Nhật ký kiểm tra',
  'feature.team.f3Desc':
    'Xem ai đã tạo, chỉnh sửa, hoặc xóa mỗi giao dịch. Xuất nhật ký để tuân thủ và đào tạo.',
  'feature.team.f3Bullet1':
    'Ghi nhận đầy đủ chuỗi thao tác cho chuyển kho, điều chỉnh và đơn hàng',
  'feature.team.f3Bullet2':
    'Lọc theo người dùng, loại thao tác và khoảng thời gian để điều tra nhanh',
  'feature.team.f3Bullet3':
    'Dùng lịch sử rõ ràng để hỗ trợ kiểm toán tuân thủ và huấn luyện đội ngũ',
  'feature.team.f4': 'Mời bằng email',
  'feature.team.f4Desc':
    'Gửi lời mời cho thành viên đội ngũ ngay lập tức. Họ có thể chấp nhận và bắt đầu làm việc ngay.',
  'feature.team.f4Bullet1':
    'Mời nhân sự bằng email công việc để quản lý tài khoản theo miền doanh nghiệp',
  'feature.team.f4Bullet2':
    'Áp vai trò đúng trước lần đăng nhập đầu tiên để đảm bảo an toàn ngay từ đầu',
  'feature.team.f4Bullet3':
    'Theo dõi trạng thái lời mời để nhắc thành viên đang chờ mà không cần bảng tính thủ công',
  'feature.team.cta':
    'Xây dựng quy trình tồn kho an toàn và tuân thủ trên cả kho và cửa hàng cùng đội ngũ của bạn',

  // Câu hỏi thường gặp — trang tính năng
  'feature.inventory.faq.title': 'Câu hỏi thường gặp',
  'feature.inventory.faq.q1':
    'khoai.io có quản lý tồn kho trên nhiều kho và cửa hàng không?',
  'feature.inventory.faq.a1':
    'Có. khoai.io được xây dựng cho vận hành đa địa điểm. Bạn có bảng tồn kho riêng theo địa điểm, chuyển kho theo từng chi nhánh, và kiểm soát quyền truy cập để mỗi đội chỉ thấy thông tin cần thiết.',
  'feature.inventory.faq.q2':
    'khoai.io theo dõi điều chỉnh tồn kho như thế nào?',
  'feature.inventory.faq.a2':
    'Mỗi thay đổi số lượng chạy như một giao dịch nguyên tử và ghi lại lý do cụ thể — bán hàng, trả hàng, hư hỏng, mất mát, kiểm kê thủ công hoặc nhận từ nhà cung cấp — cùng người thực hiện, thời gian và số lượng trước/sau.',
  'feature.inventory.faq.q3':
    'Tôi có thể nhập tồn kho hiện tại từ bảng tính không?',
  'feature.inventory.faq.a3':
    'Có. Chỉ cần tải lên bảng tính hiện tại của bạn, khoai.io sẽ ánh xạ cột, kiểm tra từng dòng và tải tồn kho mà không cần định dạng lại.',
  'feature.inventory.faq.q4':
    'Chuyển kho giữa các địa điểm hoạt động như thế nào?',
  'feature.inventory.faq.a4':
    'Chuyển kho theo dõi toàn bộ vòng đời từ chờ xử lý đến đang vận chuyển đến đã nhận. Đội ngũ có thể theo dõi từng bước, ghi nhận sai lệch khi nhận hàng và xem các chuyển kho quá hạn từ hộp thư ngoại lệ.',
  'feature.inventory.faq.q5': 'khoai.io có hỗ trợ nhận hàng từng phần không?',
  'feature.inventory.faq.a5':
    'Có. Bạn có thể nhận một phần lô hàng chuyển kho và ghi lý do sai lệch. Số lượng còn lại sẽ ở trạng thái đang vận chuyển cho đến khi nhận đủ hoặc hủy.',

  'feature.analytics.faq.title': 'Câu hỏi thường gặp',
  'feature.analytics.faq.q1': 'khoai.io dự báo nhu cầu tồn kho như thế nào?',
  'feature.analytics.faq.a1':
    'khoai.io phân tích dữ liệu bán hàng lịch sử, xu hướng theo mùa và tốc độ nhu cầu theo từng địa điểm để tạo đường cong nhu cầu theo SKU. Dự báo được cập nhật liên tục khi có dữ liệu bán hàng mới.',
  'feature.analytics.faq.q2':
    'Tính năng phân tích cung cấp những loại cảnh báo nào?',
  'feature.analytics.faq.a2':
    'Bạn nhận cảnh báo sắp hết hàng được xếp hạng theo mức độ nghiêm trọng — nguy hiểm, cảnh báo và theo dõi — cùng tín hiệu hàng ế cho các mặt hàng không luân chuyển. Mỗi cảnh báo đi kèm gợi ý nhập hàng một chạm.',
  'feature.analytics.faq.q3': 'khoai.io có giúp giảm hàng ế không?',
  'feature.analytics.faq.a3':
    'Có. Mô-đun khám phá hàng ế hiển thị các mặt hàng luân chuyển chậm trên tất cả địa điểm để bạn có thể hành động — giảm giá, phân bổ lại hoặc trả hàng nhà cung cấp — trước khi vốn tiếp tục bị đọng.',
  'feature.analytics.faq.q4':
    'Dự báo AI có hoạt động tốt cho doanh nghiệp theo mùa không?',
  'feature.analytics.faq.a4':
    'Có. Hệ thống dự báo nhận diện các mẫu theo mùa và sự kiện trên toàn bộ danh mục sản phẩm và điều chỉnh ngưỡng phù hợp, giúp cảnh báo luôn phù hợp cả mùa cao điểm lẫn thấp điểm.',
  'feature.analytics.faq.q5': 'Dự báo nhu cầu được cập nhật bao lâu một lần?',
  'feature.analytics.faq.a5':
    'Dự báo được tính lại liên tục khi giao dịch mới được ghi nhận, vì vậy các đề xuất bạn thấy luôn phản ánh hoạt động bán hàng gần nhất.',

  'feature.barcode.faq.title': 'Câu hỏi thường gặp',
  'feature.barcode.faq.q1':
    'Ứng dụng di động khoai.io có hoạt động ngoại tuyến không?',
  'feature.barcode.faq.a1':
    'Có. Mọi lần quét và thao tác được lưu cục bộ trên thiết bị và tự động đồng bộ ngay khi có kết nối internet trở lại. Không mất dữ liệu giữa các phiên.',
  'feature.barcode.faq.q2': 'Ứng dụng hỗ trợ những thiết bị di động nào?',
  'feature.barcode.faq.a2':
    'Ứng dụng di động khoai.io chạy trên mọi thiết bị Android hoặc iOS. Không cần phần cứng quét mã vạch đặc biệt — camera tích hợp xử lý tất cả việc quét.',
  'feature.barcode.faq.q3': 'Làm thế nào để in nhãn mã vạch với khoai.io?',
  'feature.barcode.faq.a3':
    'Chọn một mặt hàng hoặc một lô từ danh sách tồn kho, chọn mẫu nhãn và in trực tiếp tới bất kỳ máy in nhãn tương thích hoặc định dạng trang tiêu chuẩn. Mẫu nhãn hỗ trợ tên mặt hàng, SKU, đơn vị và giá.',
  'feature.barcode.faq.q4':
    'Nhân viên có thể dùng điện thoại cá nhân để quét tồn kho không?',
  'feature.barcode.faq.a4':
    'Có. Mỗi thành viên cài ứng dụng khoai.io trên thiết bị Android hoặc iOS của họ và đăng nhập bằng tài khoản phân quyền. Mọi lần quét đồng bộ tới tồn kho chung theo thời gian thực.',
  'feature.barcode.faq.q5':
    'Bán hàng trên di động có đồng bộ với tồn kho chính không?',
  'feature.barcode.faq.a5':
    'Có. Mỗi đơn bán hoàn tất trên di động cập nhật số lượng tồn kho và doanh thu tức thì trên tất cả thiết bị kết nối và bảng điều khiển web.',

  'feature.team.faq.title': 'Câu hỏi thường gặp',
  'feature.team.faq.q1':
    'Tôi có thể tạo vai trò tùy chỉnh trong khoai.io không?',
  'feature.team.faq.a1':
    'Có. Ngoài các vai trò có sẵn Quản trị, Giám sát, Người vận hành và Người xem, bạn có thể tạo vai trò tùy chỉnh với quyền đặt theo từng mô-đun và thao tác — phù hợp cho người nhận, người lấy, người đếm hoặc bất kỳ vị trí nào theo quy trình.',
  'feature.team.faq.q2': 'Làm thế nào để mời thành viên đội ngũ?',
  'feature.team.faq.a2':
    'Gửi lời mời qua email công việc từ trang cài đặt Đội ngũ. Người được mời nhận liên kết, chấp nhận và tài khoản được kích hoạt với vai trò bạn chỉ định — sẵn sàng làm việc ngay từ lần đăng nhập đầu tiên.',
  'feature.team.faq.q3':
    'khoai.io có lưu nhật ký kiểm tra tất cả thao tác không?',
  'feature.team.faq.a3':
    'Có. Mọi giao dịch — chuyển kho, điều chỉnh, đơn hàng, thay đổi vai trò — đều được ghi lại với người dùng, loại thao tác, dấu thời gian và trạng thái trước/sau. Nhật ký có thể được lọc và xuất để kiểm tra tuân thủ hoặc đào tạo nội bộ.',
  'feature.team.faq.q4':
    'Tôi có thể giới hạn quyền truy cập theo địa điểm kho không?',
  'feature.team.faq.a4':
    'Có. Quyền theo địa điểm được tích hợp trong mô hình phân quyền, cho phép bạn cấp cho đội kho một địa điểm toàn quyền truy cập địa điểm của họ trong khi giữ các địa điểm khác riêng tư.',
  'feature.team.faq.q5': 'Tôi có thể thêm bao nhiêu thành viên?',
  'feature.team.faq.a5':
    'Số lượng chỗ phụ thuộc vào gói của bạn. Xem trang Bảng giá để biết giới hạn theo từng gói. Bạn có thể điều chỉnh gói bất kỳ lúc nào khi đội ngũ phát triển.',

  // Nhãn giao diện dùng chung
  'public.nav.overview': 'Tổng quan',
  'public.placeholder.visual': 'Khu vực ảnh sản phẩm mẫu',
  'public.placeholder.swapLater': 'Thay bằng ảnh chụp màn hình sau',

  // Nhãn Landing bổ sung
  'landing.founder.comingSoon': 'Sắp ra mắt',
  'landing.dashboard.storefrontHub': 'Điểm bán lẻ',
  'landing.dashboard.left': 'còn lại',
  'landing.dashboard.restockSuggestion':
    'Chuyển 24 đơn vị B-440 Sensor từ Kho B sang Kho A và tạo PO cho 120 đơn vị để tránh dự báo hết hàng vào thứ Sáu.',
  'landing.feature.section1.retailFront': 'Mặt tiền bán lẻ',
  'landing.feature.section2.reorderForecast':
    'Dự báo tồn kho cho thấy cần nhập bổ sung sau 4 ngày cho Kho A.',

  // Ảnh minh họa trang tính năng và mô tả CTA theo từng trang
  'feature.inventory.visual1Title': 'Danh sách tồn kho và tóm tắt số lượng',
  'feature.inventory.visual1Caption':
    'Danh sách mặt hàng trực tiếp với bộ lọc kho, phân tích trạng thái (còn hàng / sắp hết / hết hàng), và tóm tắt doanh thu hôm nay.',
  'feature.inventory.visual2Title': 'Trạng thái chuyển kho và hộp thư ngoại lệ',
  'feature.inventory.visual2Caption':
    'Giao diện vòng đời chuyển kho hiển thị các lô hàng đang chờ, đang vận chuyển, và nhận từng phần — kèm hộp thư ngoại lệ cho các chuyển kho quá hạn.',
  'feature.inventory.visual3Title': 'Lịch sử điều chỉnh theo SKU',
  'feature.inventory.visual3Caption':
    'Nhật ký điều chỉnh theo mặt hàng với lý do cụ thể, dấu thời gian và thay đổi số lượng — mỗi thay đổi là một sự kiện có thể kiểm toán.',
  'feature.inventory.ctaSubtitle':
    'Nhận số lượng tồn kho trực tiếp, lịch sử điều chỉnh đầy đủ và luồng chuyển hàng đa điểm trong một hệ thống hoạt động. Gói miễn phí gồm một địa điểm và 500 mặt hàng. Nâng cấp khi bạn sẵn sàng.',

  'feature.analytics.visual1Title': 'Ảnh chụp tổng quan phân tích',
  'feature.analytics.visual1Caption':
    'Khu vực ảnh sản phẩm trong tương lai: biểu đồ xu hướng, bảng rủi ro sắp hết hàng, và widget dự báo.',
  'feature.analytics.visual2Title': 'Ảnh chụp cảnh báo thông minh sắp hết hàng',
  'feature.analytics.visual2Caption':
    'Khu vực ảnh sản phẩm trong tương lai: danh sách cảnh báo theo mức độ, địa điểm, và hành động đề xuất.',
  'feature.analytics.visual3Title': 'Ảnh chụp dự báo và khuyến nghị',
  'feature.analytics.visual3Caption':
    'Khu vực ảnh sản phẩm trong tương lai: biểu đồ dự báo với gợi ý nhập hàng bằng AI.',
  'feature.analytics.ctaSubtitle':
    'Trao cho đội ngũ vận hành đầy đủ thông tin để bảo vệ biên lợi nhuận và đảm bảo hàng luôn sẵn có trên nhiều kho, cửa hàng và nhóm hàng cần kiểm soát chặt.',

  'feature.barcode.visual1Title': 'Quy trình tạo mã vạch',
  'feature.barcode.visual1Caption':
    'Tạo nhãn trực tiếp từ danh sách sắp hết hàng, kiểm tra mẫu nhãn, và đẩy lô in không cần bảng tính thủ công.',
  'feature.barcode.visual2Title':
    'Giỏ bán hàng và luồng thanh toán trên di động',
  'feature.barcode.visual2Caption':
    'Tạo đơn bán bằng cách quét sản phẩm, điều chỉnh số lượng, và hoàn tất thanh toán với tồn kho và doanh thu được đồng bộ.',
  'feature.barcode.visual3Title':
    'Lịch sử đơn hàng trên di động và dòng thời gian kiểm toán',
  'feature.barcode.visual3Caption':
    'Giao diện lịch sử đơn hàng trong Activity hiển thị thao tác đã ghi nhận, thời điểm, và người thực hiện cho từng đơn di động.',
  'feature.barcode.ctaSubtitle':
    'Trang bị công cụ di động và độ chính xác mã vạch để giảm lỗi và tăng tốc thực thi.',

  'feature.team.visual1Title': 'Ảnh chụp ma trận vai trò và quyền hạn',
  'feature.team.visual1Caption':
    'Khu vực ảnh sản phẩm trong tương lai: ma trận quyền theo vai trò và luồng gán người dùng.',
  'feature.team.visual2Title': 'Ảnh chụp dòng thời gian hoạt động người dùng',
  'feature.team.visual2Caption':
    'Khu vực ảnh sản phẩm trong tương lai: nhật ký hoạt động theo người dùng với metadata và thời gian.',
  'feature.team.visual3Title': 'Ảnh chụp trình chỉnh sửa chính sách quyền hạn',
  'feature.team.visual3Caption':
    'Khu vực ảnh sản phẩm trong tương lai: trình chỉnh sửa vai trò với quyền truy cập theo module và bản xem trước.',
  'feature.team.visual4Title':
    'Ảnh chụp lời mời đội ngũ và trạng thái thành viên',
  'feature.team.visual4Caption':
    'Khu vực ảnh sản phẩm trong tương lai: luồng gửi lời mời, thành viên chờ xác nhận, và gán vai trò trước khi kích hoạt.',
  'feature.team.ctaSubtitle':
    'Giữ đội ngũ làm việc hiệu quả với mô hình phân quyền đúng, đồng thời bảo vệ quy trình tồn kho quan trọng.',

  // SEO page descriptions
  'seo.landing.desc':
    'Ngăn hết hàng và mất doanh thu với khoai.io — quản lý tồn kho theo thời gian thực trên nhiều kho và cửa hàng. Theo dõi từng SKU, đặt cảnh báo nhập hàng, quét mã vạch, kiểm kho và quản lý chuyển hàng từ mọi thiết bị. Miễn phí cho 1 địa điểm.',
  'seo.pricing.desc':
    'Giá cả đơn giản, minh bạch cho mọi quy mô. Bắt đầu miễn phí và mở rộng theo nhu cầu kinh doanh.',
  'seo.privacy.desc':
    'Tìm hiểu cách khoai.io thu thập, sử dụng và bảo vệ dữ liệu cá nhân của bạn.',
  'seo.terms.desc':
    'Điều khoản điều chỉnh việc bạn sử dụng khoai.io — nền tảng quản lý kho và cửa hàng.',
  'seo.billingTerms.desc':
    'Điều khoản thanh toán, chi tiết gói dịch vụ, chính sách hoàn tiền và phương thức thanh toán cho khoai.io.',
};
